/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import CachedProfileImage from '../components/CachedProfileImage';
import StoreModal from '../components/StoreModal';
import { classCodeService, StudentAccessResult } from '../services/classCodeService';
import { userDataService } from '../services/userDataService';
import { StudentAssignmentHistory } from '../types/assignmentPointsMemo';
import { PointRecord } from '../types/pointManagement';
import { doc, getDoc } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { db, auth } from '../firebase/config';
import GemIcon from '../components/icons/GemIcon';
import { ItemCard, StudentItemCard, ItemCardCategory } from '../types/itemCard';
import { chatQuizService } from '../services/chatQuizService';
import { ChatQuiz, StudentQuizResponse, ConnectedStudentInfo } from '../types/chatQuiz';
import ProfileImageSelectModal from '../components/ProfileImageSelectModal';
import StudentProfileEditModal from '../components/StudentProfileEditModal';
import ManagerAssignmentView from '../components/ManagerAssignmentView';
import { withImageVersion } from '../utils/imageVersion';
import AssignmentSubmissionCropModal from '../components/AssignmentSubmissionCropModal';
import { checkAssignmentImageSubmissionAccess } from '../utils/licenseLimits';
import { getLevelDisplayInfo, normalizeLevelUpPoints } from '../utils/levelUpRewardHelper';
import { assignmentSubmissionImageCache } from '../utils/assignmentSubmissionImageCache';
import {
  buildMemberPageScope,
  studentMemberPageData
} from '../services/studentMemberPageDataService';
import {
  invalidateMemberPageAfterAssignmentChange,
  invalidateMemberPageAfterProfileChange,
  invalidateMemberPageAfterRoutineChange,
  invalidateMemberPageAfterStoreChange,
  studentMemberPageCache
} from '../services/studentMemberPageCache';
import type { CachedRoutineToday, CachedStudentProfile } from '../services/studentMemberPageDataService';

const MAX_MULTI_RESPONSES = 5;

// 세트 플레이 전용 간단 카드 스타일
const SetQuizListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 60px;
  margin-top: 12px;
`;

const SetQuizCard = styled.div`
  border: 1px solid var(--color-border);
  background: var(--color-panel);
  border-radius: 12px;
  padding: 14px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
  position: relative;

  /* 라이트 모드: 연한 회색 */
  :root[data-theme='light'] & {
    background: #f3f3f3;
  }

  /* 다크 모드: 기존 패널 색 유지 */
  :root:not([data-theme='light']) & {
    background: var(--color-surface);
  }
`;

const SetQuizTitle = styled.div`
  font-weight: 700;
  font-size: 16px;
  margin-bottom: 8px;
  color: var(--color-text-primary);
`;

const SetChoiceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SetChoiceButton = styled.button<{ $selected: boolean }>`
  width: 100%;
  display: grid;
  grid-template-columns: 44px 1fr;
  align-items: center;
  column-gap: 10px;
  padding: 10px 12px;
  border-radius: 12px;
  border: 2px solid ${p => (p.$selected ? 'var(--color-accent-strong)' : '#B0B0B0')};
  background: ${p => (p.$selected ? 'rgba(var(--color-accent-rgb),0.08)' : 'var(--color-surface)')};
  color: var(--color-text-primary);
  cursor: pointer;
  font-size: 14px;
  transition: all 0.15s ease;

  :root[data-theme='light'] & {
    background: ${p => (p.$selected ? 'rgba(var(--color-accent-rgb),0.08)' : 'rgba(255,255,255,0.6)')};
  }

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    border-color: var(--color-accent-strong);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.08);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    border-color: var(--color-border);
    background: #f2f2f2;
    pointer-events: none;
  }

  :root:not([data-theme='light']) & {
    border-color: ${p => (p.$selected ? '#ffffff' : '#B0B0B0')};
    background: ${p => (p.$selected ? 'rgba(255,255,255,0.08)' : '#3a3a3a')};
    color: #ffffff;

    &:hover:not(:disabled) {
      box-shadow: 0 6px 12px rgba(0, 0, 0, 0.25);
    }

    &:disabled {
      background: var(--color-surface);
      border-color: var(--color-border);
      color: #cfcfcf;
      box-shadow: none;
    }
  }
`;

const SetChoiceNumber = styled.div<{ $levelIndex?: number }>`
  width: 40px;
  height: 32px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 15px;
  color: #111;
  border: 1px solid var(--color-border);
  position: relative;
  background: ${({ $levelIndex }) => {
    // Pastel palette matching new quiz modal choice badges
    const colors = [
      '#fcd8d8', // 0 fallback
      '#fcd8d8', // 1 핑크
      '#fce6d4', // 2 피치
      '#dee7ce', // 3 연두
      '#dcede9', // 4 민트
      '#d9e6f6', // 5 연파랑
      '#e6d8f6', // 6 연보라
      '#fcd8d8', // 7 fallback
      '#fce6d4', // 8 fallback
      '#dee7ce', // 9 fallback
      '#dcede9', // 10 fallback
    ];
    if (typeof $levelIndex === 'number' && $levelIndex >= 1 && $levelIndex < colors.length) {
      return colors[$levelIndex];
    }
    return colors[0];
  }};
`;

const SetChoiceText = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary);
  word-break: break-word;
  line-height: 1.4;

  :root:not([data-theme='light']) & {
    color: #ffffff;
  }
`;

const SetChoiceSelectedBadge = styled.div`
  position: absolute;
  top: -8px;
  right: -8px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: var(--color-accent-strong);
  border: 2px solid #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.18);
  svg {
    width: 10px;
    height: 10px;
    stroke: #ffffff;
    stroke-width: 3;
    fill: none;
  }
`;

const SetStatusBadge = styled.div<{ $status: 'correct' | 'wrong' | 'neutral' }>`
  position: absolute;
  top: 10px;
  right: 10px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $status }) =>
    $status === 'correct'
      ? '#10504A'
      : $status === 'wrong'
      ? '#FF6B6B'
      : '#777777'};
  border: 2px solid #ffffff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  color: #ffffff;
  svg {
    width: 18px;
    height: 18px;
    stroke: currentColor;
  }
`;

const SetTitleBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 10px 18px;
  border-radius: 14px;
  background: #ffffff;
  border: 2px solid var(--color-block-border);
  color: var(--color-text-primary);
  font-size: 22px;
  font-weight: 800;
  max-width: 100%;
  word-break: break-word;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.12);

  :root:not([data-theme='light']) & {
    background: #3a3a3a;
    border-color: #5a5a5a;
    color: #ffffff;
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.32);
  }
`;

const SetTextInput = styled.textarea`
  width: 100%;
  min-height: 80px;
  border-radius: 10px;
  border: 2px solid #b0b0b0;
  padding: 10px 12px;
  font-size: 14px;
  background: var(--color-surface);
  color: var(--color-text-primary);
  box-sizing: border-box;
  resize: vertical;

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  &:read-only,
  &:disabled {
    opacity: 0.75;
    cursor: not-allowed;
    background: #f2f2f2;
    pointer-events: none;

    :root:not([data-theme='light']) & {
      background: var(--color-surface); /* 문항 블럭과 동일한 배경 */
      color: #e5e5e5;
      border-color: var(--color-border);
    }
  }

  :root:not([data-theme='light']) & {
    /* 활성 입력은 기존보다 살짝 밝은 톤 */
    background: #3a3a3a;
    color: #ffffff;
    border-color: #5a5a5a;
  }
`;

const SetSubmitButton = styled.button`
  width: 100%;
  margin-top: 60px;
  padding: 14px 16px;
  border: none;
  border-radius: 12px;
  background: var(--color-accent-strong);
  color: #fff;
  font-weight: 700;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(var(--color-accent-rgb), 0.9);
    transform: translateY(-1px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  /* 라이트 모드: 흰색 배경 + 메인 초록 테두리/텍스트 */
  :root[data-theme='light'] & {
    background: #ffffff;
    color: var(--color-accent-strong);
    border: 2px solid var(--color-accent-strong);

    &:hover {
      background: #ffffff;
      border-color: var(--color-accent-strong);
      transform: translateY(-1px);
      box-shadow: 0 8px 18px rgba(0, 0, 0, 0.12);
    }

    &:active {
      transform: translateY(0);
      box-shadow: none;
    }
  }
`;

const NAV_THEME_COLOR = 'var(--color-accent-strong)';

const Container = styled.div`
  position: relative;
  min-height: 100vh;
  width: 100%;
  overflow-x: hidden;
`;

// 고정 배경 레이어
const BackgroundLayer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--color-accent-strong);
  z-index: 0;
  pointer-events: none;

  /* 다크 모드: 배경을 #141414로 변경 */
  :root:not([data-theme='light']) & {
    background: #141414;
  }
`;

const MemberPageBootstrapOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: var(--color-accent-strong);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  color: #ffffff;
  text-align: center;
  padding: 24px;
  :root:not([data-theme='light']) & {
    background: #141414;
    color: #f5f5f5;
  }
`;

const MemberPageBootstrapSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid var(--color-block-border);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: memberBootstrapSpin 0.85s linear infinite;
  @keyframes memberBootstrapSpin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const MemberPageBootstrapText = styled.p`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
`;

const MemberPageBootstrapSub = styled.span`
  font-size: 14px;
  opacity: 0.85;
`;

// 네비바 스타일
const NavBar = styled.div`
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 20px;
  color: ${NAV_THEME_COLOR};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  height: 36px;
  box-sizing: border-box;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border-bottom: 1px solid var(--color-border);

  /* 다크 모드: 강조 색상 배경, 흰색 텍스트 */
  :root:not([data-theme='light']) & {
    background: var(--color-accent-strong);
    color: #ffffff;
  }
`;

// 뒤로가기 버튼 숨김용
const BackButtonPlaceholder = styled.div`
  width: 40px;
  height: 40px;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s;
  color: ${NAV_THEME_COLOR};
  
  &:hover {
    transform: translateX(-4px);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
  
  svg {
    stroke: ${NAV_THEME_COLOR};
  }

  /* 다크 모드: 흰색 */
  :root:not([data-theme='light']) & {
    color: #ffffff;
    
    svg {
      stroke: #ffffff;
    }
  }
`;

const NavTitle = styled.h1`
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  color: ${NAV_THEME_COLOR};

  /* 다크 모드: 흰색 */
  :root:not([data-theme='light']) & {
    color: #ffffff;
  }
`;

const StudentNameTag = styled.span`
  font-size: 1.2rem;
  font-weight: 500;
  color: ${NAV_THEME_COLOR};
  
  @media (max-width: 600px) {
    display: none;
  }

  /* 다크 모드: 흰색 */
  :root:not([data-theme='light']) & {
    color: #ffffff;
  }
`;

const RightButtonContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
`;

const HamburgerButton = styled.button`
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s ease;
  padding: 0;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
  
  &:focus {
    outline: none;
  }
  
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.3);
  }
  
  /* 다크 모드: 흰색 */
  :root:not([data-theme='light']) & {
    color: #ffffff;
  }
  
  /* 라이트 모드: 네비바 테마 색상 */
  :root[data-theme='light'] & {
    color: ${NAV_THEME_COLOR};
  }
`;

const MenuDropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  box-shadow: var(--modal-shadow);
  z-index: 9999;
  width: 220px;
  overflow: hidden;
  margin-top: 4px;
  transform: translateX(0);
`;

const MenuIdentityHeader = styled.div`
  padding: 14px 16px 13px;
  text-align: left;
  border-bottom: 1px solid var(--color-border);
  background: #232323;

  :root[data-theme='light'] & {
    background: #ffffff;
  }
`;

const MenuIdentityTopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
`;

const MenuProfileThumb = styled.div`
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 10px;
  overflow: hidden;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  box-sizing: border-box;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 0;
  }
`;

const MenuLogoutButton = styled.button`
  margin-left: auto;
  flex-shrink: 0;
  padding: 6px 10px;
  border-radius: 6px;
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  color: var(--color-text-primary);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: var(--color-panel);
    border-color: var(--color-border-strong);
  }

  &:focus-visible {
    outline: 2px solid var(--color-accent-strong);
    outline-offset: 2px;
  }
`;

const MenuIdentityName = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: var(--color-text-primary);
  line-height: 1.3;
  overflow-wrap: anywhere;
`;

const MenuIdentityMeta = styled.div`
  margin-top: 5px;
  font-size: 0.75rem;
  font-weight: 400;
  color: var(--color-text-muted);
  line-height: 1.35;
  overflow-wrap: anywhere;
`;

const MenuItem = styled.button`
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: var(--color-surface);
  color: var(--color-text-primary);
  font-size: 14px;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.2s ease;
  position: relative;
  z-index: 10000;
  pointer-events: auto;
  min-height: 44px;

  &:hover {
    background: var(--color-panel);
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: none;
    box-shadow: inset 0 0 0 2px rgba(var(--color-accent-rgb), 0.3);
  }

  &:active {
    background: var(--color-border);
  }
`;

const MenuDivider = styled.div`
  width: 100%;
  height: 1px;
  background: var(--color-border);
  opacity: 0.6;
`;

const StudentCreateStartButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 12px 22px;
  border-radius: 999px;
  border: 1px solid var(--color-accent-strong);
  background: #10504a;
  color: #fff;
  font-weight: 800;
  font-size: 15px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 46px;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.12);

  &:hover:not(:disabled) {
    background: #0c3f39;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const StudentCreatePanel = styled.div`
  width: 100%;
  max-width: 600px;
  background: var(--color-panel);
  border: 1px solid var(--color-border);
  border-radius: 12px;
  padding: 16px;
  margin: 12px auto 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.08);

  :root[data-theme='light'] & {
    background: var(--color-bg);
    border-color: var(--color-border);
  }

  :root:not([data-theme='light']) & {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.08);
  }
`;

const StudentCreateRow = styled.div`
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 10px;
  align-items: center;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
    align-items: stretch;
  }
`;

const StudentCreateLabel = styled.label`
  font-size: 13px;
  font-weight: 700;
  color: var(--color-text-primary);
`;

const StudentCreateSelect = styled.select`
  padding: 10px 12px;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  background: var(--color-surface);
  color: var(--color-text-primary);
  font-size: 14px;

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const StudentCreateInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  background: var(--color-surface);
  color: var(--color-text-primary);
  font-size: 14px;

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const StudentCreateTextArea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  background: var(--color-surface);
  color: var(--color-text-primary);
  font-size: 14px;
  min-height: 80px;
  resize: vertical;

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const StudentTypeTabs = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const StudentTypeTab = styled.button<{ $active?: boolean }>`
  min-width: 120px;
  padding: 12px 16px;
  border-radius: 12px;
  border: 1px solid ${({ $active }) => ($active ? '#adb5bd' : 'var(--color-border)')};
  background: #ffffff;
  color: ${({ $active }) => ($active ? '#000000' : '#9aa0a6')};
  font-weight: 800;
  font-size: 15px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: ${({ $active }) => ($active ? '0 0 0 rgba(0,0,0,0)' : '0 10px 22px rgba(0, 0, 0, 0.14)')};

  &:hover:not(:disabled) {
    border-color: #7c858d;
    box-shadow: ${({ $active }) => ($active ? '0 0 0 rgba(0,0,0,0)' : '0 12px 24px rgba(0,0,0,0.18)')};
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  :root:not([data-theme='light']) & {
    background: #1f1f1f;
    border-color: ${({ $active }) => ($active ? '#5c5c5c' : '#3a3a3a')};
    color: ${({ $active }) => ($active ? '#ffffff' : '#b8b8b8')};
    box-shadow: ${({ $active }) => ($active ? '0 0 0 rgba(0,0,0,0)' : '0 8px 18px rgba(0,0,0,0.5)')};

    &:hover:not(:disabled) {
      border-color: #6a6a6a;
      box-shadow: ${({ $active }) => ($active ? '0 0 0 rgba(0,0,0,0)' : '0 10px 20px rgba(0,0,0,0.6)')};
      background: #2a2a2a;
      color: #ffffff;
    }

    &:active {
      background: #333333;
      color: #ffffff;
    }
  }
`;

const RefreshIcon = styled.svg`
  width: 14px;
  height: 14px;
  stroke: currentColor;
  fill: none;
`;

const TrashIcon = styled.svg`
  width: 14px;
  height: 14px;
  stroke: currentColor;
  fill: none;
`;

const StudentQuestionInput = styled.textarea`
  width: 100%;
  border: 2px solid var(--color-border);
  border-radius: 14px;
  padding: 14px 16px;
  font-size: 18px;
  font-weight: 700;
  color: var(--color-text-primary);
  background: var(--color-surface);
  min-height: 40px;
  resize: none;
  overflow: hidden;

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  :root:not([data-theme='light']) & {
    background: #1f1f1f;
    border-color: #3a3a3a;
    color: #ffffff;

    &::placeholder {
      color: #b8b8b8;
    }
  }
`;

const StudentChoiceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 12px;
`;

const StudentChoiceRow = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 10px;
`;

const StudentChoiceNumberWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const StudentChoiceNumber = styled.button<{ $selected?: boolean; $levelIndex?: number }>`
  padding: 8px 12px;
  min-width: 44px;
  height: 40px;
  border-radius: 10px;
  border: 2px solid var(--color-border);
  background: ${({ $levelIndex }) => {
    const idx = $levelIndex ?? 1;
    if (idx === 10) return 'color-mix(in srgb, var(--color-level-0) 18%, transparent)';
    return hexToRgba(choiceButtonColorByIndex(idx), 0.18);
  }};
  color: var(--color-text-primary);
  font-weight: 700;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const StudentChoiceSelectedBadge = styled.div`
  position: absolute;
  top: -6px;
  right: -6px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: var(--color-accent-strong);
  border: 2px solid #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 12px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
`;

const StudentChoiceInputWrapper = styled.div`
  width: 100%;
  height: 40px;
  border: 2px solid var(--color-border);
  border-radius: 10px;
  background: var(--color-surface);
  display: grid;
  grid-template-columns: 1fr auto auto;
  align-items: center;
  padding: 0 6px 0 12px;

  :root:not([data-theme='light']) & {
    background: #1f1f1f;
    border-color: #3a3a3a;
  }
`;

const StudentChoiceInput = styled.input`
  border: none;
  width: 100%;
  height: 100%;
  background: transparent;
  color: var(--color-text-primary);
  font-size: 14px;

  &:focus {
    outline: none;
  }

  :root:not([data-theme='light']) & {
    color: #ffffff;
    &::placeholder {
      color: #b8b8b8;
    }
  }
`;

const StudentIconButton = styled.button`
  width: 30px;
  height: 30px;
  border: none;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.15s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  :root:not([data-theme='light']) & {
    color: #ffffff;
  }
`;

const StudentAddAnswerBox = styled.button`
  margin-top: 12px;
  width: 100%;
  padding: 14px;
  border: 2px dashed var(--color-border);
  border-radius: 12px;
  background: transparent;
  color: var(--color-text-primary);
  font-weight: 700;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: var(--color-accent-strong);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const StudentChoiceRowWrapper = styled.div<{ $isDragging?: boolean }>`
  transition: transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;
  transform: ${({ $isDragging }) => ($isDragging ? 'scale(1.01)' : 'none')};
  box-shadow: ${({ $isDragging }) => ($isDragging ? '0 10px 26px rgba(0,0,0,0.12)' : 'none')};
  opacity: ${({ $isDragging }) => ($isDragging ? 0.85 : 1)};
`;

const StudentSortableChoiceRow: React.FC<{
  id: string;
  index: number;
  selected: boolean;
  choiceText: string;
  onSelect: () => void;
  onChange: (text: string) => void;
  onReset: () => void;
  onDelete: () => void;
}> = ({ id, index, selected, choiceText, onSelect, onChange, onReset, onDelete }) => {
  const { setNodeRef, attributes, listeners, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <StudentChoiceRowWrapper ref={setNodeRef} style={style} $isDragging={isDragging}>
      <StudentChoiceRow>
        <StudentChoiceNumberWrapper>
          <StudentChoiceNumber
            type="button"
            $selected={selected}
            $levelIndex={(index % 10) + 1}
            onClick={onSelect}
          >
            {index + 1}
          </StudentChoiceNumber>
          {selected && (
            <StudentChoiceSelectedBadge>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </StudentChoiceSelectedBadge>
          )}
        </StudentChoiceNumberWrapper>
        <StudentChoiceInputWrapper>
          <StudentChoiceInput
            value={choiceText}
            onChange={(e) => onChange(e.target.value)}
            placeholder={`${index + 1}번 선택지 내용을 입력하세요...`}
          />
          <div style={{ display: 'flex', gap: 0, paddingLeft: 0 }}>
            <StudentIconButton type="button" onClick={onReset} title="보기 초기화">
              <RefreshIcon viewBox="0 0 24 24" strokeWidth="2">
                <path d="M1 4v6h6" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M23 20v-6h-6" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" strokeLinecap="round" strokeLinejoin="round" />
              </RefreshIcon>
            </StudentIconButton>
            <StudentIconButton type="button" onClick={onDelete} title="보기 삭제">
              <TrashIcon viewBox="0 0 24 24" strokeWidth="2">
                <path d="M3 6h18" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M10 11v6" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M14 11v6" strokeLinecap="round" strokeLinejoin="round" />
              </TrashIcon>
            </StudentIconButton>
          </div>
        </StudentChoiceInputWrapper>
        <StudentIconButton
          type="button"
          title="드래그로 순서 변경"
          style={{ cursor: 'grab' }}
          {...attributes}
          {...listeners}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M4 7h16" />
            <path d="M4 12h16" />
            <path d="M4 17h16" />
          </svg>
        </StudentIconButton>
      </StudentChoiceRow>
    </StudentChoiceRowWrapper>
  );
};

const StudentCreateActions = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
`;

const StudentCreateSubmit = styled.button`
  width: 100%;
  padding: 12px 16px;
  border-radius: 12px;
  border: 1px solid var(--color-accent-strong);
  background: #10504a;
  color: #fff;
  font-weight: 800;
  font-size: 15px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #0c3f39;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  :root:not([data-theme='light']) & {
    background: #4a4a4a;
    border-color: #666666;

    &:hover:not(:disabled) {
      background: #5a5a5a;
      border-color: #7a7a7a;
    }

    &:disabled {
      background: #3b3b3b;
      border-color: #5a5a5a;
      color: #cccccc;
    }
  }
`;

const StudentCreateCancel = styled.button`
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  color: var(--color-text-primary);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: var(--color-panel);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const MenuFooter = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: var(--color-panel);
`;

const NavThemeToggleButton = styled.button<{ $isDark: boolean }>`
  display: flex;
  flex: 1;
  width: 100%;
  background: var(--color-accent-strong);
  border-radius: 13px;
  padding: 2px;
  position: relative;
  cursor: pointer;
  transition: all 0.3s ease;
  height: 26px;
  min-width: 80px;
  min-height: 26px;
  box-sizing: border-box;
  border: none;
  appearance: none;
  -webkit-appearance: none;

  :root[data-theme='light'] & {
    background: #ffffff;
  border: 1px solid var(--color-border);
  }

  &::before {
    content: '';
    position: absolute;
    top: 2px;
    left: ${(props) => (props.$isDark ? '50%' : '2px')};
    width: calc(50% - 2px);
    height: calc(100% - 4px);
  background: var(--color-surface);
    border-radius: 11px;
    transition: left 0.3s ease;
    z-index: 1;
  }

  :root[data-theme='light'] &::before {
    background: var(--color-accent-strong);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const NavThemeToggleOption = styled.div<{ $isActive: boolean }>`
  flex: 1;
  padding: 2px 10px;
  font-size: 11px !important;
  font-weight: 600;
  color: ${(props) => (props.$isActive ? 'var(--color-toggle-active)' : 'var(--color-toggle-inactive)')};
  text-align: center;
  position: relative;
  z-index: 2;
  transition: color 0.3s ease;
  user-select: none;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  cursor: pointer;

  :root[data-theme='light'] & {
    color: ${(props) => (props.$isActive ? '#ffffff' : 'rgba(var(--color-accent-rgb), 0.6)')};
  }
`;

const ReconnectButton = styled.button`
  width: 100%;
  max-width: 600px;
  margin: 0 auto 16px auto;
  padding: 14px 20px;
  background: var(--color-accent-strong);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  
  &:hover {
    background: rgba(var(--color-accent-rgb), 0.9);
    transform: translateY(-2px);
    box-shadow: var(--modal-shadow);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
  
  svg {
    width: 20px;
    height: 20px;
    stroke: white;
    fill: none;
    transition: transform 0.2s;
  }
  
  &:hover svg {
    transform: rotate(180deg);
  }
`;

// 컨텐츠 영역
const Content = styled.div`
  position: relative;
  z-index: 1;
  max-width: 600px;
  margin: 0 auto;
  padding: 56px 20px 20px 20px;
  min-height: calc(100vh - 36px);
`;

// 매니저 과제 관리 전용: 전체 너비(코디 과제 관리 카드와 동일 레이아웃)
const ManagerViewFullWidth = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  min-width: 100%;
  padding: 0;
  margin: 0;
`;

// 메인 카드 컨테이너
const MainCardContainer = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  width: 100%;
  margin-bottom: 20px;
  
  @media (max-width: 600px) {
    gap: 12px;
  }
`;

// 메인 카드 (퀴즈, 프로필, 매니저) - 투명 배경, 아이콘만. 매니저는 프로필과 같은 높이·선 두께, 크기만 약간 축소
const MainCard = styled.div<{ $disabled?: boolean; $isManagerCard?: boolean }>`
  width: ${props => props.$isManagerCard ? '60px' : '64px'};
  height: 64px;
  min-width: ${props => props.$isManagerCard ? '60px' : '64px'};
  margin-left: ${props => props.$isManagerCard ? '-4px' : '0'};
  background: transparent;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  padding: 0;
  opacity: ${props => props.$disabled ? 0.5 : 1};
  
  &:hover {
    transform: ${props => props.$disabled ? 'none' : 'translateY(-2px)'};
    opacity: ${props => props.$disabled ? 0.5 : 0.8};
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
  
  @media (max-width: 600px) {
    width: ${props => props.$isManagerCard ? '44px' : '48px'};
    height: 48px;
    min-width: ${props => props.$isManagerCard ? '44px' : '48px'};
  }
`;

// 메인 카드 아이콘 영역
const MainCardIcon = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    width: 100%;
    height: 100%;
    filter: brightness(0) invert(1); /* 흰색으로 변경 */
    object-fit: contain;
  }
`;

// 메인 카드 제목 (사용하지 않음)
const MainCardTitle = styled.div`
  display: none;
`;

// 섹션 박스 컨테이너 (프로필 내 포인트/내 보유 아이템: $whiteBorder 시 타이틀 상단 중앙 배치용 position·padding 적용)
const SectionBox = styled.div<{ $whiteBorder?: boolean }>`
  background: ${props => props.$whiteBorder ? 'rgba(255, 255, 255, 0.1)' : '#ffffff'};
  border: ${props => props.$whiteBorder ? '1.5px dashed #ffffff' : '1px solid var(--color-border)'};
  border-radius: 12px;
  padding: ${props => props.$whiteBorder ? '40px 20px 20px' : '12px'};
  margin-bottom: 20px;
  ${props => props.$whiteBorder && 'margin-top: 28px;'}
  box-shadow: ${props => props.$whiteBorder ? 'none' : '0 2px 8px rgba(0, 0, 0, 0.08)'};
  ${props => props.$whiteBorder && 'position: relative;'}
`;

// 내 생각을 남겨요 버튼
const LeaveThoughtButton = styled.button`
  width: 100%;
  max-width: 600px;
  margin: 0 auto 20px auto;
  padding: 16px 24px;
  background: var(--color-accent-strong);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: block;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  
  &:hover {
    background: var(--color-accent);
    transform: translateY(-2px);
    box-shadow: var(--modal-shadow);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

// 안내 메시지 / 퀴즈 문제
const ThoughtGuideMessage = styled.div`
  padding: 12px 16px;
  margin-bottom: 16px;
  text-align: center;
  color: white;
  font-size: 16px;
  font-weight: 600;
  background: transparent;
  border: none;
  word-break: break-word;
  line-height: 1.6;
  white-space: pre-line;
`;

const StudentQuizImageBox = styled.div`
  width: 100%;
  aspect-ratio: 16 / 9;
  border: 2px dashed rgba(255, 255, 255, 0.3);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.05);
  overflow: hidden;
  position: relative;
  margin: 12px 0 16px 0;
`;

const StudentQuizImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const SetQuizImageBox = styled.div`
  width: 100%;
  aspect-ratio: 16 / 9;
  border: 2px dashed var(--color-border);
  border-radius: 12px;
  background: var(--color-panel);
  overflow: hidden;
  position: relative;
  margin: 8px 0 12px 0;
`;

const SetQuizImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

// 클래스챗 대기 상태 표시에 사용하는 흰색 점 3개 애니메이션
const TypingIndicatorWaiting = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  margin: 4px 0 16px;
  
  span {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #ffffff;
    box-shadow: 0 0 6px rgba(255, 255, 255, 0.65);
    animation: typingWaiting 1.4s infinite;
    
    &:nth-child(2) {
      animation-delay: 0.2s;
    }
    
    &:nth-child(3) {
      animation-delay: 0.4s;
    }
  }
  
  @keyframes typingWaiting {
    0%, 60%, 100% {
      transform: translateY(0);
    }
    30% {
      transform: translateY(-8px);
    }
  }
`;

// 퀴즈 가이드 메시지 (보드 페이지 개별 공지 학생 블럭 텍스트 영역과 동일: 흰색 반투명 배경)
const QuizGuideMessage = styled.div`
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 12px;
  font-size: 14px;
  line-height: 1.6;
  text-align: left;
  /* 라이트/다크 공통: 흰색 반투명 배경 + 흰색 계열 테두리 */
  background: rgba(255, 255, 255, 0.2);
  border: 2px solid var(--color-block-border);
  color: white;

  :root[data-theme='light'] & {
    background: rgba(255, 255, 255, 0.2);
    border-color: var(--color-block-border);
    color: white;
  }
  :root:not([data-theme='light']) & {
    background: rgba(255, 255, 255, 0.2);
    border-color: var(--color-block-border);
    color: white;
  }
`;

// 단일 응답 모드 제출 완료 안내 (문제 아래)
const QuizSubmitCompleteNotice = styled.div`
  padding: 10px 14px;
  border-radius: 8px;
  margin: 0 0 14px;
  font-size: 14px;
  line-height: 1.5;
  text-align: center;
  font-weight: 500;
  background: rgba(255, 255, 255, 0.2);
  border: 2px solid var(--color-block-border);
  color: white;
`;

// 제출 가능 시 안내 (선택형/단답형/서술형 공통 스타일)
const QuizSubmitPromptNotice = styled(QuizSubmitCompleteNotice)``;

// 퀴즈 탭 섹션 타이틀 (과제 모달의 ModalSectionTitle과 동일 계열)
const QuizPanelSectionTitleWrap = styled.div<{ $marginTop?: boolean }>`
  display: flex;
  justify-content: center;
  width: 100%;
  margin-bottom: 14px;
  margin-top: ${({ $marginTop }) => ($marginTop ? '16px' : '0')};
`;

const QuizPanelSectionTitle = styled.h2`
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--color-text-primary);
  background: var(--color-surface);
  padding: 4px 16px;
  border: 2px solid var(--color-border);
  border-radius: 20px;
  display: inline-block;
  line-height: 1.35;

  :root[data-theme='light'] & {
    background: #ffffff;
    border-color: var(--color-border);
    color: #212529;
  }

  :root:not([data-theme='light']) & {
    background: #2e2f33;
    border-color: rgba(255, 255, 255, 0.12);
    color: #f1f3f5;
  }
`;

// 단일 퀴즈: 문제·입력 영역 / 퀴즈 응답 영역 각각 흰색 점선(배경 없음)
const QuizFlowDashedSection = styled.div`
  width: 100%;
  box-sizing: border-box;
  border-radius: 12px;
  border: 3px dashed #ffffff;
  padding: 14px 12px 16px;
  margin-bottom: 16px;
  background: transparent;
`;

const QuizSubmittedDashedSection = styled.div`
  width: 100%;
  box-sizing: border-box;
  border-radius: 12px;
  border: 3px dashed #ffffff;
  padding: 12px 12px 14px;
  margin-bottom: 0;
  background: transparent;
`;

// 선택형 선택지
const ChoiceOption = styled.label<{ $isSelected: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  margin-bottom: 6px;
  border: 2px solid ${({ $isSelected }) => ($isSelected ? 'var(--color-accent-strong)' : 'var(--color-border)')};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${({ $isSelected }) => ($isSelected ? 'var(--color-accent-soft)' : 'var(--color-surface)')};
  font-size: 13px;
  
  &:hover {
    border-color: var(--color-accent-text);
    background: rgba(var(--color-accent-rgb), 0.02);
  }
  
  input[type="checkbox"] {
    width: 16px;
    height: 16px;
    cursor: pointer;
    flex-shrink: 0;
    accent-color: var(--color-accent-text);
    
    &:checked {
      accent-color: var(--color-accent-text);
      background-color: var(--color-accent-text);
      border-color: var(--color-accent-text);
    }
    
    &:focus {
      outline: 2px solid rgba(var(--color-accent-rgb), 0.5);
      outline-offset: 2px;
    }
  }
  
  span {
    flex: 1;
  }
`;

// 제출된 답변 말풍선
const SubmittedAnswerBubble = styled.div<{ $isOtherStudent?: boolean }>`
  display: flex;
  gap: 12px;
  align-items: flex-end;
  margin-bottom: 12px;
  flex-direction: ${props => props.$isOtherStudent ? 'row' : 'row'};
`;

const SubmittedAnswerContent = styled.div<{ $isOtherStudent?: boolean }>`
  flex: 1;
  position: relative;
  background: var(--color-surface);
  border-radius: 6px;
  display: flex;
  align-items: center;
  padding: 8px 16px; /* 텍스트 크기는 유지하면서 높이를 프로필(44px)에 맞춤 */
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  min-height: 44px; /* 프로필 높이와 동일 */
  height: auto;
  
  /* 다크 모드: 배경을 #4a4a4a로 조금 더 밝게 */
  :root:not([data-theme='light']) & {
    background: #4a4a4a;
  }
  
  /* 말풍선 꼬리 - 자신의 답변은 오른쪽, 다른 멤버의 답변은 왼쪽 */
  &::after {
    content: '';
    position: absolute;
    top: 12px;
    ${props => props.$isOtherStudent ? 'left: -10px;' : 'right: -10px;'}
    width: 0;
    height: 0;
    border-style: solid;
    ${props => props.$isOtherStudent 
      ? 'border-width: 4px 12px 4px 0; border-color: transparent white transparent transparent;'
      : 'border-width: 4px 0 4px 12px; border-color: transparent transparent transparent white;'
    }
    filter: drop-shadow(${props => props.$isOtherStudent ? '-2px 0 2px' : '2px 0 2px'} rgba(0, 0, 0, 0.1));
  }
  
  /* 라이트 모드: 흰색 꼬리 */
  :root[data-theme='light'] &::after {
    ${props => props.$isOtherStudent 
      ? 'border-color: transparent white transparent transparent;'
      : 'border-color: transparent transparent transparent white;'
    }
  }
  
  /* 다크 모드: 배경과 동일한 색상 (#4a4a4a) */
  :root:not([data-theme='light']) &::after {
    ${props => props.$isOtherStudent 
      ? 'border-color: transparent #4a4a4a transparent transparent;'
      : 'border-color: transparent transparent transparent #4a4a4a;'
    }
  }
`;

const SubmittedAnswerText = styled.div`
  font-size: 14px;
  color: var(--color-text-primary);
  word-break: break-word;
  line-height: 1.5;
`;

const SubmittedAnswerProfileContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
`;

const SubmittedAnswerProfile = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 2px;
  overflow: hidden;
  flex-shrink: 0;
`;

const SubmittedAnswerProfileName = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: white;
  white-space: nowrap;
`;

// 선택형 답변 배지 컨테이너
const SubmittedChoiceBadgesContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 4px;
  width: 100%;
`;

// 선택형 답변 배지
const SubmittedChoiceBadge = styled.div<{ $levelIndex?: number }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: ${props => {
    // levelIndex는 1부터 시작 (1번 버튼 = levelColors[1], 2번 버튼 = levelColors[2])
    // 1~6번 버튼에 색상 적용
    if (props.$levelIndex !== undefined && props.$levelIndex >= 1 && props.$levelIndex <= 6) {
      const colors = [
        'var(--color-accent-strong)', // 레벨0
        '#FC5555', // 레벨1 - 빨강
        '#FF9933', // 레벨2 - 노랑
        '#669900', // 레벨3 - 초록
        '#119473', // 레벨4 - 파랑
        '#0066CC', // 레벨5
        '#9900CC', // 레벨6
      ];
      return hexToRgba(colors[props.$levelIndex], 0.2);
    }
    return 'rgba(var(--color-accent-rgb), 0.2)';
  }};
  color: var(--color-text-secondary); /* 검정 텍스트 */
  border: 2px solid var(--color-border); /* 회색 테두리 */
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
`;

// 퀴즈 탭 본문 래퍼 (점선 테두리는 퀴즈/제출응답 각 섹션 박스에만 둠)
const ThoughtSection = styled.div`
  background: transparent;
  border: none;
  padding: 0;
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  position: relative;
  min-height: 0;
`;

const ThoughtContent = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  margin-bottom: 12px;
  margin-top: 12px;
`;

const ThoughtBubble = styled.div`
  flex: 1;
  position: relative;
  background: var(--color-surface);
  border-radius: 12px;
  padding: 12px 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  min-height: auto;
  height: auto;
  
  /* 다크 모드: 배경을 #4a4a4a로 조금 더 밝게 */
  :root:not([data-theme='light']) & {
    background: #4a4a4a;
  }
  
  /* 말풍선 꼬리 - 오른쪽 하단 오른쪽 면으로 */
  &::after {
    content: '';
    position: absolute;
    bottom: 12px;
    right: -8px;
    width: 0;
    height: 0;
    border-top: 8px solid transparent;
    border-bottom: 8px solid transparent;
    border-left: 8px solid white;
  }
  
  /* 라이트 모드: 흰색 꼬리 */
  :root[data-theme='light'] &::after {
    border-left-color: white;
  }
  
  /* 다크 모드: 배경과 동일한 색상 (#4a4a4a) */
  :root:not([data-theme='light']) &::after {
    border-left-color: #4a4a4a;
  }
`;

const ThoughtTextarea = styled.textarea`
  width: 100%;
  border: none !important;
  outline: none !important;
  resize: none;
  font-size: 14px;
  color: var(--color-text-primary);
  
  /* 다크 모드: 텍스트를 흰색으로 */
  :root:not([data-theme='light']) & {
    color: #ffffff;
  }
  font-family: inherit;
  line-height: 1.5;
  min-height: 24px;
  max-height: 200px;
  background: transparent;
  box-shadow: none !important;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  
  &::placeholder {
    color: #999;
  }
  
  &:focus {
    outline: none !important;
    border: none !important;
    box-shadow: none !important;
  }
  
  &:active {
    outline: none !important;
    border: none !important;
  }
`;

const ThoughtProfileImage = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 4px;
  overflow: hidden;
  flex-shrink: 0;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ThoughtActions = styled.div`
  display: flex;
  justify-content: center;
  margin-top: auto;
`;

// 선택형 보기 버튼
const ChoiceButton = styled.button<{ $levelIndex?: number; $isSelected: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  padding: 4px 6px;
  background: var(--color-surface);
  border: 2px solid ${props => props.$isSelected ? 'var(--color-accent-strong)' : 'var(--color-border)'};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: auto;
  height: auto;
  
  &:hover {
    border-color: var(--color-accent-text);
    background: rgba(var(--color-accent-rgb), 0.1);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

// hex 또는 rgba 색상을 rgba로 변환하는 헬퍼 함수
const hexToRgba = (color: string, alpha: number = 1): string => {
  if (color.startsWith('rgba')) {
    return color.replace(/[\d.]+\)$/g, `${alpha})`);
  }
  if (color.startsWith('#')) {
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  return `rgba(var(--color-accent-rgb), ${alpha})`;
};

// 선택형 버튼 번호
const ChoiceButtonNumber = styled.div<{ $levelIndex?: number }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0;
  background: ${props => {
    // levelIndex는 1부터 시작 (1번 버튼 = levelColors[1], 2번 버튼 = levelColors[2])
    // 1~6번 버튼에 색상 적용
    if (props.$levelIndex !== undefined && props.$levelIndex >= 1 && props.$levelIndex <= 6) {
      const colors = [
        'var(--color-accent-strong)', // 레벨0
        '#FC5555', // 레벨1 - 빨강
        '#FF9933', // 레벨2 - 노랑
        '#669900', // 레벨3 - 초록
        '#119473', // 레벨4 - 파랑
        '#0066CC', // 레벨5
        '#9900CC', // 레벨6
      ];
      return hexToRgba(colors[props.$levelIndex], 0.2);
    }
    return 'rgba(var(--color-accent-rgb), 0.2)';
  }};
  color: var(--color-text-secondary); /* 검정 텍스트 */
  border: 2px solid var(--color-border); /* 회색 테두리 */
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  flex-shrink: 0;
`;

// 선택형 버튼 텍스트
const ChoiceButtonText = styled.div`
  flex: 1;
  text-align: left;
  font-size: 13px;
  color: var(--color-text-primary);
  word-break: break-word;
  line-height: 1.4;
`;

// 선택 표시 배지 (동그라미 V) - 번호 도형 우측 상단에 표시
const ChoiceSelectedBadge = styled.div`
  position: absolute;
  top: -6px;
  right: -6px;
  width: 20px;
  height: 20px;
  background: var(--color-accent-strong);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 12px;
  font-weight: bold;
  border: 2px solid white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  z-index: 1;
  
  svg {
    width: 12px;
    height: 12px;
    stroke: white;
    stroke-width: 3;
    fill: none;
  }
`;

// 선택형 보기 컨테이너 (메시지 창 안에 들어감)
const ChoiceOptionsContainer = styled.div<{ $hasSendButton?: boolean }>`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 6px;
  overflow-y: auto;
  max-height: 200px;
  padding-right: 40px; /* 보내기 버튼 영역 확보 */
  min-height: 0;
`;

// 채팅 입력 필드 컨테이너
const ChatInputContainer = styled.div<{ $isChoiceMode?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: ${props => props.$isChoiceMode ? 'flex-start' : 'center'};
  gap: 8px;
  width: 100%;
  background: var(--color-surface);
  border-radius: 8px;
  padding: ${props => props.$isChoiceMode ? '8px 12px 8px 12px' : '8px 12px'};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  min-height: ${props => props.$isChoiceMode ? 'auto' : '40px'};
  height: ${props => props.$isChoiceMode ? 'auto' : '44px'};
  margin-top: 12px;
  margin-bottom: 12px;
  position: ${props => props.$isChoiceMode ? 'relative' : 'static'};
  
  /* 다크 모드: 배경을 #4a4a4a로 조금 더 밝게 */
  :root:not([data-theme='light']) & {
    background: #4a4a4a;
  }
`;

// 채팅 입력 필드
const ChatInputField = styled.input<{ $hasSendButton?: boolean }>`
  flex: 1;
  border: none !important;
  outline: none !important;
  font-size: 14px;
  font-family: inherit;
  background: transparent;
  padding: 4px 8px;
  height: 100%;
  padding-right: ${props => props.$hasSendButton ? '40px' : '8px'};
  color: var(--color-text-primary);
  
  /* 다크 모드: 텍스트를 흰색으로 */
  :root:not([data-theme='light']) & {
    color: #ffffff;
  }
  
  &::placeholder {
    color: #999;
  }
  
  &:focus {
    outline: none !important;
    border: none !important;
    box-shadow: none !important;
  }
  
  &:active {
    outline: none !important;
    border: none !important;
  }
  
  &:focus-visible {
    outline: none !important;
    border: none !important;
    box-shadow: none !important;
  }
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
    background: transparent;
  }
`;

// 보내기 버튼 (비행기 아이콘)
const SendIconButton = styled.button<{ $show: boolean; $isChoiceMode?: boolean }>`
  display: flex; /* 항상 flex로 유지하여 레이아웃 안정성 확보 */
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  min-width: 24px;
  min-height: 24px;
  border-radius: 50%;
  background: var(--color-accent-strong);
  border: none;
  cursor: pointer;
  transition: opacity 0.2s ease, visibility 0.2s ease;
  padding: 0;
  flex-shrink: 0;
  position: ${props => props.$isChoiceMode ? 'absolute' : 'static'};
  bottom: ${props => props.$isChoiceMode ? '10px' : 'auto'}; /* 선택 버튼의 마지막 행과 같은 높이 (선택 버튼 높이 28px, 보내기 버튼 높이 24px 기준 중앙 정렬) */
  right: ${props => props.$isChoiceMode ? '12px' : 'auto'};
  align-self: ${props => !props.$isChoiceMode ? 'center' : 'auto'};
  margin-top: ${props => !props.$isChoiceMode ? '0' : '0'};
  z-index: 10;
  opacity: ${props => props.$show ? 1 : 0};
  visibility: ${props => props.$show ? 'visible' : 'hidden'};
  pointer-events: ${props => props.$show ? 'auto' : 'none'};

  svg {
    width: 14px;
    height: 14px;
    fill: white;
    transition: opacity 0.2s ease;
  }

  &:hover {
    background: rgba(var(--color-accent-rgb), 0.85);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  &:disabled {
    cursor: not-allowed;
    background: rgba(var(--color-accent-rgb), 0.6);
    opacity: 0.65;
    pointer-events: none;
  }
`;

const SubmitButton = styled.button`
  padding: 10px 20px;
  background: var(--color-accent-strong);
  color: #ffffff;
  border: 2px solid var(--color-accent-strong);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(var(--color-accent-rgb), 0.9);
    border-color: rgba(var(--color-accent-rgb), 0.9);
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
  
  &:disabled {
    background: var(--color-text-muted);
    border-color: var(--color-text-muted);
    cursor: not-allowed;
    transform: none;
    opacity: 0.6;
  }

  :root:not([data-theme='light']) & {
    background: #4a4a4a;
    color: #ffffff;
    border-color: #666666;

    &:hover {
      background: #5a5a5a;
      border-color: #7a7a7a;
    }

    &:disabled {
      background: #3b3b3b;
      border-color: #5a5a5a;
      color: #cccccc;
    }
  }
`;

// 포인트 요약 카드
const PointsSummaryCard = styled.div`
  background: transparent;
  border-radius: 0;
  padding: 0;
  margin-bottom: 0;
  box-shadow: none;
`;

const PointsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-top: 16px;
`;

const PointItem = styled.div`
  text-align: center;
  padding: 12px 16px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  
  /* 다크 모드: 프로필 블럭과 동일한 배경색 */
  :root:not([data-theme='light']) & {
    background: var(--color-surface);
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const HistoryButton = styled.button`
  padding: 6px 12px;
  background: rgba(var(--color-accent-rgb), 0.7);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 4px;
  
  &:hover {
    background: rgba(var(--color-accent-rgb), 0.85);
    transform: translateY(-1px);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const PointLabel = styled.div`
  color: 'var(--color-text-gray)';
  font-size: 14px;
  margin-bottom: 0;
`;

const PointValue = styled.div`
  color: var(--color-accent-text);
  font-size: 28px;
  font-weight: 700;
`;

// 멤버 프로필 카드 (포인트 페이지와 동일)
const StudentProfileCard = styled.div`
  background: var(--color-surface);
  border: 2px solid var(--color-border);
  border-radius: 8px;
  padding: 8px;
  height: 120px;
  margin-bottom: 20px;
  overflow: hidden;
`;

const StudentCardLayout = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  grid-template-rows: 1fr;
  gap: 6px;
  height: 100%;
`;

const ProfileImageArea = styled.div`
  width: 100%;
  height: 100%;
  background: transparent;
  border: 2px solid #000000;
  border-radius: 8px;
  flex-shrink: 0;
  cursor: pointer;
  transition: opacity 0.2s ease;
  
  &:hover {
    opacity: 0.8;
  }
  display: flex;
  align-items: center;
  justify-content: center;
  grid-row: 1 / 2;
  grid-column: 1 / 2;
  aspect-ratio: 1;
  max-height: 100%;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  z-index: 10;
  pointer-events: auto;

  &:hover {
    transform: scale(1.05);
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const InfoArea = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  grid-row: 1 / 2;
  grid-column: 2 / 3;
  margin-bottom: 0;
`;

const InfoTopRow = styled.div`
  display: flex;
  gap: 4px;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 4px;
`;

const LevelDisplayBox = styled.div<{ $levelColor: string; $level: number }>`
  background: ${props => props.$levelColor};
  color: ${props => getLevelTextColor(props.$level)};
  font-size: 14px;
  font-weight: 700;
  padding: 4px 8px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  min-width: 36px;
  height: 26px;
  text-align: center;
  line-height: 14px;
  letter-spacing: 0.5px;
  position: relative;
  z-index: 2;
  flex-shrink: 0;
`;

const StudentInfoGroup = styled.div`
  display: flex;
  gap: 4px;
  align-items: baseline;
  flex: 1;
`;

const StudentNumber = styled.span`
  font-size: 16px;
  color: 'var(--color-text-gray)';
  font-weight: 500;
  flex-shrink: 0;
  position: relative;
  z-index: 2;
`;

const StudentName = styled.span`
  color: var(--color-text-primary);
  font-size: 16px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  position: relative;
  z-index: 2;
`;

const CoinInfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1px;
  margin-bottom: 0px;
`;

const CoinIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  flex-shrink: 0;
`;

const MultiplySign = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #f39c12;
  margin: 0;
`;

const CoinAmount = styled.span`
  font-size: 18px;
  font-weight: 600;
  color: #f39c12;
`;

const TotalPointsDisplay = styled.div<{ $digitCount: number }>`
  font-size: ${props => {
    if (props.$digitCount <= 3) return '48px';
    if (props.$digitCount === 4) return '42px';
    if (props.$digitCount === 5) return '36px';
    return '30px';
  }};
  font-weight: bold;
  color: var(--color-accent-text);
  line-height: 1;
  text-align: left;
  transition: font-size 0.2s ease;
  padding: 0;
  margin: 0;
  display: inline-block;
  vertical-align: top;
  overflow: hidden;
  height: 1em;
  box-sizing: border-box;
  position: relative;
  z-index: 2;
`;

const RightArea = styled.div`
  width: 20px;
  height: 100px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  grid-row: 1 / 2;
  grid-column: 3 / 4;
  background: transparent;
  padding: 0;
  margin: 0;
  box-sizing: border-box;
`;

const LevelProgressBar = styled.div<{ $progress: number; $levelColor: string; $level: number }>`
  width: 12px;
  height: 100%;
  max-height: 100%;
  margin: 0;
  background: var(--color-border);
  border-radius: 6px;
  position: relative;
  overflow: hidden;
  z-index: 1;
  
  /* 진행도에 따른 채우기 */
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: ${props => props.$progress}%;
    background: ${props => props.$levelColor};
    border-radius: 6px;
    transition: height 0.3s ease;
  }
`;

const CoinBlock = styled.div`
  margin-top: 12px;
  padding: 12px 16px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  
  /* 다크 모드: 프로필 블럭과 동일한 배경색 */
  :root:not([data-theme='light']) & {
    background: var(--color-surface);
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const CoinBlockLabel = styled.div`
  color: 'var(--color-text-gray)';
  font-size: 14px;
  margin-bottom: 0;
`;

const CoinBlockValue = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1px;
  margin-bottom: 0;
  line-height: 1;
`;

const CoinBlockIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CoinBlockText = styled.span`
  font-size: 28px;
  font-weight: 700;
  color: #f39c12;
`;

const CoinHistoryButton = styled.button`
  padding: 6px 12px;
  background: rgba(var(--color-accent-rgb), 0.7);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 4px;
  
  &:hover {
    background: rgba(var(--color-accent-rgb), 0.85);
    transform: translateY(-1px);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

// 과제 섹션
const AssignmentSection = styled.div`
  margin-top: 0;
`;

const SectionTitle = styled.h2<{ $centerAlign?: boolean; $whiteColor?: boolean }>`
  font-size: 18px;
  font-weight: 700;
  color: var(--color-text-primary);
  margin-top: 32px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: ${props => props.$centerAlign ? 'center' : 'flex-start'};
  gap: 8px;
  text-align: ${props => props.$centerAlign ? 'center' : 'left'};
  
  /* 다크모드에서 $whiteColor가 true일 때 흰색 텍스트 */
  :root:not([data-theme='light']) & {
    color: ${props => props.$whiteColor ? '#ffffff' : 'var(--color-text-primary)'};
  }
  
  :root[data-theme='light'] & {
    color: ${props => props.$whiteColor ? '#ffffff' : '#212529'};
  }
  
  :root:not([data-theme='light']) & {
    color: ${props => props.$whiteColor ? '#ffffff' : '#f1f3f5'};
  }
`;

const AssignmentCount = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: var(--color-accent-text);
  background: rgba(var(--color-accent-rgb), 0.1);
  padding: 4px 12px;
  border-radius: 12px;
`;

// 미완료 과제 섹션 상단 점선 안내 박스
const PendingAssignmentHintBox = styled.div`
  width: 100%;
  border-radius: 12px;
  padding: 12px 14px;
  margin: 0 auto 14px;
  margin-bottom: 32px;
  border: 1.5px dashed #ffffff;
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
  text-align: center;
  box-sizing: border-box;
`;

const PendingAssignmentHintRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const PendingAssignmentHintLabel = styled.span`
  font-size: 13px;
  font-weight: 800;
  opacity: 0.92;
`;

// 점선 섹션 내부에 표시되는 전체공지노트(핀 카드)
const GeneralNoticePreviewCard = styled.div`
  width: 100%;
  margin-top: 12px;
  background: var(--color-surface);
  border-radius: 14px;
  padding: 22px 16px 16px;
  position: relative;
  cursor: pointer;
  user-select: none;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.12);

  :root[data-theme='light'] & {
    border: 2px solid var(--color-accent-strong);
  }

  :root:not([data-theme='light']) & {
    border: 2px solid rgba(255, 255, 255, 0.85);
  }
`;

const GeneralNoticeHeader = styled.div`
  position: absolute;
  top: -14px;
  left: 50%;
  transform: translateX(-50%);
  min-width: 120px;
  height: 34px;
  border-radius: 17px;
  background: var(--color-surface);
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  padding: 0 12px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.18);

  :root[data-theme='light'] & {
    border: 2px solid var(--color-accent-strong);
    color: var(--color-accent-strong);
  }

  :root:not([data-theme='light']) & {
    border: 2px solid rgba(255, 255, 255, 0.85);
    color: rgba(255, 255, 255, 0.9);
  }
`;

const GeneralNoticeHeaderIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
`;

const GeneralNoticeHeaderTitle = styled.div`
  font-size: 14px;
  font-weight: 900;
  color: #10504a;
  white-space: nowrap;
  
  :root[data-theme='light'] & {
    color: var(--color-accent-strong);
  }

  :root:not([data-theme='light']) & {
    color: rgba(255, 255, 255, 0.9);
  }
`;

const GeneralNoticePreviewTitle = styled.div`
  display: none;
`;

const GeneralNoticePreviewDate = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: var(--color-accent-strong);
  text-align: center;
  margin-top: 12px;
  margin-bottom: 8px;
  
  :root:not([data-theme='light']) & {
    color: rgba(255, 255, 255, 0.9);
  }
`;

const GeneralNoticePreviewContent = styled.div<{ $fontSize?: number }>`
  color: #111;
  font-size: ${props => (props.$fontSize ? `${props.$fontSize}px` : '16px')};
  line-height: 1.45;
  white-space: pre-wrap;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 6;
  -webkit-box-orient: vertical;
  padding-top: 8px;
  
  :root:not([data-theme='light']) & {
    color: rgba(255, 255, 255, 0.9);
  }
`;

// 점선 섹션 내부에 표시되는 개별공지노트(핀 카드)
const IndividualNoticePreviewCard = styled.div`
  width: 100%;
  margin-top: 24px;
  background: var(--color-surface);
  border-radius: 14px;
  padding: 22px 16px 16px;
  position: relative;
  cursor: pointer;
  user-select: none;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.12);

  :root[data-theme='light'] & {
    border: 2px solid var(--color-accent-strong);
  }

  :root:not([data-theme='light']) & {
    border: 2px solid rgba(255, 255, 255, 0.85);
  }
`;

const IndividualNoticeHeader = styled.div`
  position: absolute;
  top: -14px;
  left: 50%;
  transform: translateX(-50%);
  min-width: 120px;
  height: 34px;
  border-radius: 17px;
  background: var(--color-surface);
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  padding: 0 12px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.18);

  :root[data-theme='light'] & {
    border: 2px solid var(--color-accent-strong);
    color: var(--color-accent-strong);
  }

  :root:not([data-theme='light']) & {
    border: 2px solid rgba(255, 255, 255, 0.85);
    color: rgba(255, 255, 255, 0.9);
  }
`;

const IndividualNoticeHeaderIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
`;

const IndividualNoticeHeaderTitle = styled.div`
  font-size: 14px;
  font-weight: 900;
  color: #10504a;
  white-space: nowrap;
  
  :root[data-theme='light'] & {
    color: var(--color-accent-strong);
  }

  :root:not([data-theme='light']) & {
    color: rgba(255, 255, 255, 0.9);
  }
`;

const IndividualNoticePreviewTitle = styled.div`
  display: none;
`;

const IndividualNoticePreviewDate = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: var(--color-accent-strong);
  text-align: center;
  margin-top: 12px;
  margin-bottom: 8px;
  
  :root:not([data-theme='light']) & {
    color: rgba(255, 255, 255, 0.9);
  }
`;

const IndividualNoticePreviewContent = styled.div<{ $fontSize?: number }>`
  color: #111;
  font-size: ${props => (props.$fontSize ? `${props.$fontSize}px` : '16px')};
  line-height: 1.45;
  white-space: pre-wrap;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 6;
  -webkit-box-orient: vertical;
  padding-top: 8px;
  
  :root:not([data-theme='light']) & {
    color: rgba(255, 255, 255, 0.9);
  }
`;

// 전체공지노트 열람 모달(학생용, 읽기 전용)
const NoticeList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 8px;
`;

const NoticePaginationRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  margin: 10px 0 6px;
`;

const NoticePaginationButton = styled.button`
  width: 36px;
  height: 36px;
  min-width: 36px;
  min-height: 36px;
  border-radius: 50%;
  background: var(--color-surface);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  :root[data-theme='light'] & {
    border: 2px solid var(--color-accent-strong);
    color: var(--color-accent-strong);
  }

  :root:not([data-theme='light']) & {
    border: 2px solid rgba(255, 255, 255, 0.85);
    color: rgba(255, 255, 255, 0.92);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    transform: scale(1.05);
    :root[data-theme='light'] & {
      background: rgba(var(--color-accent-rgb), 0.08);
    }
    :root:not([data-theme='light']) & {
      background: rgba(255, 255, 255, 0.08);
    }
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }
`;

const NoticePaginationInfo = styled.div`
  font-size: 14px;
  font-weight: 800;
  color: var(--color-text-primary);
`;

// 과제 필터 토글 버튼 스타일
const AssignmentFilterToggleContainer = styled.div`
  display: flex;
  background: var(--color-border);
  border-radius: 12px;
  padding: 4px;
  position: relative;
  width: 100%;
  min-width: 300px;
  margin-bottom: 16px;
  
  @media (max-width: 480px) {
    min-width: 250px;
  }
`;

const AssignmentFilterToggleSlider = styled.div<{ $position: number }>`
  position: absolute;
  top: 4px;
  left: ${props => {
    const tabCount = 4;
    const basePosition = props.$position * (100/tabCount);
    const sliderWidth = 100/tabCount;
    const margin = (100/tabCount - sliderWidth) / 2;
    const adjustment = props.$position < tabCount - 1 ? 0.5 : 0;
    return `${basePosition + margin + adjustment}%`;
  }};
  width: calc(25% - 4px);
  height: calc(100% - 8px);
  background: var(--color-surface);
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: left 0.3s ease;
  z-index: 1;
`;

const AssignmentFilterToggleOption = styled.button<{ $active: boolean }>`
  position: relative;
  z-index: 2;
  background: transparent;
  border: none;
  padding: 8px 12px;
  font-size: 0.9rem;
  font-weight: 500;
  color: ${props => props.$active ? 'var(--color-text-primary)' : 'var(--color-text-muted)'};
  cursor: pointer;
  border-radius: 8px;
  transition: color 0.2s ease;
  white-space: nowrap;
  flex: 1;
  min-width: 0;

  &:hover {
    color: var(--color-text-primary);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
  
  @media (max-width: 480px) {
    padding: 6px 8px;
    font-size: 0.8rem;
  }
`;

const NoticeListItem = styled.div<{ $expanded?: boolean }>`
  border: 2px solid var(--color-border);
  border-radius: 12px;
  padding: 14px;
  background: var(--color-surface);
  cursor: pointer;
  transition: all 0.2s ease;
  
  :root[data-theme='light'] & {
    &:hover {
      border-color: var(--color-accent-strong);
      box-shadow: 0 8px 18px rgba(0, 0, 0, 0.08);
    }
  }
  
  :root:not([data-theme='light']) & {
    &:hover {
      border-color: rgba(255, 255, 255, 0.85);
      box-shadow: 0 8px 18px rgba(255, 255, 255, 0.06);
    }
  }
`;

const NoticeListItemDate = styled.div`
  font-size: 13px;
  font-weight: 900;
  color: var(--color-text-primary);
  margin-bottom: 6px;
`;

const NoticeListItemPreview = styled.div`
  font-size: 14px;
  color: var(--color-text-primary);
  line-height: 1.4;
  opacity: 0.92;
`;

const NoticeListItemFull = styled.div`
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid var(--color-border);
  white-space: pre-wrap;
  font-size: 14px;
  line-height: 1.5;
  color: var(--color-text-primary);
  min-height: 24px; /* 첫줄만 있는 경우에도 확장 시 여백 유지 */
`;


const AssignmentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const AssignmentBlockWrapper = styled.div<{ $isSubmitted?: boolean }>`
  background: var(--color-surface);
  border: 2px solid var(--color-border);
  border-radius: 12px;
  padding: 10px 10px;
  margin: 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;
  user-select: none;
  opacity: ${props => props.$isSubmitted ? 0.7 : 1};
  text-align: center;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.$isSubmitted 
      ? '0 4px 12px rgba(0, 0, 0, 0.1)' 
      : '0 8px 24px rgba(0, 0, 0, 0.15)'
    };
  }
  
  &:focus {
    outline: none;
    border-color: #000;
    box-shadow: 0 0 0 2px rgba(0,0,0,0.3);
  }
  
  &:focus-visible {
    outline: none;
    border-color: #000;
    box-shadow: 0 0 0 2px rgba(0,0,0,0.3);
  }
`;

const AssignmentHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 8px;
  position: relative;
`;

const AssignmentBadge = styled.span<{ $type: string; $projectColor?: string }>`
  position: absolute;
  left: 0;
  top: 0;
  font-size: 11px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 8px;
  background: ${props => {
    if (props.$type === 'general') return '#6c757d';
    if (props.$type === 'project') return (props.$projectColor && String(props.$projectColor).trim()) ? props.$projectColor : '#FC5555';
    return 'var(--color-border)';
  }};
  color: #ffffff;
`;

const AssignmentTitle = styled.div<{ $isSubmitted?: boolean }>`
  font-size: 16px;
  font-weight: 700;
  color: ${props => props.$isSubmitted ? '#999' : 'var(--color-text-primary)'};
  text-align: center;
  margin-top: 4px;
  
  :root[data-theme='light'] & {
    color: ${props => props.$isSubmitted ? '#999' : '#212529'};
  }
  
  :root:not([data-theme='light']) & {
    color: ${props => props.$isSubmitted ? '#8f979f' : '#f1f3f5'};
  }
`;

const AssignmentInfo = styled.div<{ $isSubmitted?: boolean }>`
  font-size: 14px;
  color: ${props => props.$isSubmitted ? '#999' : 'var(--color-text-gray)'};
  margin-top: 4px;
  text-align: center;
`;

const AssignmentInfoText = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const SubmittedTag = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
  color: var(--color-accent-text);
  background: rgba(var(--color-accent-rgb), 0.1);
  padding: 4px 8px;
  border-radius: 6px;
  margin-top: 8px;
  text-align: center;
`;

const TodayRoutineTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: var(--color-text-primary);
  margin-top: 32px;
  margin-bottom: 12px;
  text-align: center;
  :root:not([data-theme='light']) & { color: #ffffff; }
  :root[data-theme='light'] & { color: #ffffff; }
`;

/* 오늘의 루틴과제 섹션: 점선 테두리 + 반투명 흰색 배경 (전체공지노트 섹션과 동일 스타일) */
const TodayRoutineSectionBox = styled.div`
  width: 100%;
  border-radius: 12px;
  padding: 40px 14px 16px;
  margin: 0 auto 32px;
  border: 1.5px dashed #ffffff;
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
  box-sizing: border-box;
  position: relative;
`;

/* 오늘의 루틴과제 타이틀: 전체공지노트 타이틀처럼 모서리 둥근 직사각형 */
const TodayRoutineSectionTitleBox = styled.div`
  position: absolute;
  top: -14px;
  left: 50%;
  transform: translateX(-50%);
  min-width: 120px;
  height: 34px;
  border-radius: 17px;
  background: var(--color-surface);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 12px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.18);
  :root[data-theme='light'] & {
    border: 2px solid var(--color-accent-strong);
    color: var(--color-accent-strong);
  }
  :root:not([data-theme='light']) & {
    border: 2px solid rgba(255, 255, 255, 0.85);
    color: rgba(255, 255, 255, 0.9);
  }
`;

const TodayRoutineSectionTitleText = styled.span`
  font-size: 14px;
  font-weight: 900;
  white-space: nowrap;
`;

/* 미완료 일반, 프로젝트 과제 섹션: 점선 테두리 + 반투명 흰색 배경 */
const IncompleteAssignmentSectionBox = styled.div`
  width: 100%;
  border-radius: 12px;
  padding: 40px 14px 16px;
  margin: 0 auto 32px;
  border: 1.5px dashed #ffffff;
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
  box-sizing: border-box;
  position: relative;
`;

/* 미완료 일반,프로젝트 과제 타이틀: 전체공지노트 타이틀처럼 모서리 둥근 직사각형 */
const IncompleteAssignmentSectionTitleBox = styled.div`
  position: absolute;
  top: -14px;
  left: 50%;
  transform: translateX(-50%);
  min-width: 220px;
  height: 34px;
  border-radius: 17px;
  background: var(--color-surface);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 12px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.18);
  :root[data-theme='light'] & {
    border: 2px solid var(--color-accent-strong);
    color: var(--color-accent-strong);
  }
  :root:not([data-theme='light']) & {
    border: 2px solid rgba(255, 255, 255, 0.85);
    color: rgba(255, 255, 255, 0.9);
  }
`;

const IncompleteAssignmentSectionTitleText = styled.span`
  font-size: 14px;
  font-weight: 900;
  white-space: nowrap;
`;

/* 프로필 페이지 내 포인트/내 보유 아이템 섹션 타이틀: 미완료 과제 섹션 타이틀과 동일 스타일 (경계 위쪽 중앙) */
const ProfileSectionTitleBox = styled.div`
  position: absolute;
  top: -14px;
  left: 50%;
  transform: translateX(-50%);
  min-width: 120px;
  height: 34px;
  border-radius: 17px;
  background: var(--color-surface);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 12px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.18);
  :root[data-theme='light'] & {
    border: 2px solid var(--color-accent-strong);
    color: var(--color-accent-strong);
  }
  :root:not([data-theme='light']) & {
    border: 2px solid rgba(255, 255, 255, 0.85);
    color: rgba(255, 255, 255, 0.9);
  }
`;

const ProfileSectionTitleText = styled.span`
  font-size: 14px;
  font-weight: 900;
  white-space: nowrap;
`;

const SaveRoutineButton = styled.button`
  width: 100%;
  padding: 14px 20px;
  margin-bottom: 16px;
  background: var(--color-accent-strong);
  color: #ffffff;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  
  &:hover:not(:disabled) {
    background: var(--color-accent);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: var(--color-border);
    color: var(--color-text-muted);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const RoutineBlockRow = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 20px;
`;

const RoutineBlock = styled.div<{ $status: 'pending' | 'confirmed' }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 200px;
  padding: 10px 14px;
  border-radius: 12px;
  border: 2px solid var(--color-border);
  background: var(--color-surface);
  cursor: pointer;
  user-select: none;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.12);
  }
  &:focus-visible {
    outline: none;
    border-color: #000;
    box-shadow: 0 0 0 2px rgba(0,0,0,0.2);
  }
`;

const RoutineBlockTopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
`;

const RoutineBlockName = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: var(--color-text-primary);
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RoutineBlockStatusLabel = styled.span<{ $color: string }>`
  font-size: 11px;
  font-weight: 700;
  color: #fff;
  background: ${(p) => p.$color};
  padding: 4px 10px;
  border-radius: 8px;
  flex-shrink: 0;
`;

const RoutineProgressContainer = styled.div`
  width: 100%;
  margin-top: 4px;
`;

const RoutineProgressPercent = styled.div`
  font-size: 0.8rem;
  color: var(--color-accent-strong);
  margin-bottom: 4px;
  :root:not([data-theme='light']) & {
    color: #ffffff;
  }
`;

const RoutineProgressBar = styled.div`
  width: 100%;
  height: 10px;
  border-radius: 6px;
  background: var(--color-border);
  overflow: hidden;
  position: relative;
`;

const RoutineProgressBarToday = styled.div`
  height: 100%;
  background: #bfc3c9;
  transition: width 0.2s ease;
  position: absolute;
  left: 0;
  top: 0;
  :root:not([data-theme='light']) & {
    background: rgba(255, 255, 255, 0.25);
  }
`;

const RoutineProgressBarFill = styled.div`
  height: 100%;
  background: var(--color-accent-strong);
  transition: width 0.2s ease;
  position: absolute;
  left: 0;
  top: 0;
  :root:not([data-theme='light']) & {
    background: #ffffff;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  color: #ffffff;
  padding: 40px 20px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  border: 1.5px dashed #ffffff;
`;

/* 빈 상태 안내용: 도형 없이 텍스트만 중앙 표시 (오늘의 루틴/미완료 과제 섹션) */
const EmptyStateTextOnly = styled.div`
  text-align: center;
  color: #ffffff;
  padding: 24px 16px;
`;

const ItemCardEmptyState = styled.div`
  text-align: center;
  color: 'var(--color-text-gray)';
  padding: 20px;
  background: #f1f3f5;
  border-radius: 8px;
  border: 1.5px dashed var(--color-text-muted);
  min-width: 120px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: 'var(--color-text-gray)';
`;

const SectionHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const StoreButton = styled.button<{ $disabled?: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--color-surface);
  border: 1px solid var(--store-icon-border-color);
  color: var(--color-accent-text);
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  opacity: ${props => props.$disabled ? 0.5 : 1};

  &:hover {
    ${props => !props.$disabled && `
    transform: scale(1.1);
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
    border-color: rgba(var(--color-accent-rgb), 0.5);
    `}
  }

  &:active {
    transform: scale(0.95);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

/* 사용 완료 아이템 카드 보이기 토글 (보드 개별공지 번호/자리 모드 토글과 동일 스타일, 크기 70x28 유지) */
const ShowExhaustedToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 16px;
  width: 100%;
`;

/* 내 보유 아이템: 사용 완료 토글(왼쪽) + 스토어 버튼(오른쪽) 한 행 */
const ItemSectionToolbarRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
  width: 100%;
`;

const ShowExhaustedLeftGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: flex-start;
`;

const ShowExhaustedLabel = styled.span`
  font-size: 14px;
  color: var(--color-text-primary);
  text-align: center;
  :root[data-theme='light'] & { color: #ffffff; }
`;

/* 내 보유 아이템 섹션: 토글 트랙 완전 투명, 슬라이딩 pill만 반투명 */
const ShowExhaustedToggle = styled.button<{ $isEnabled: boolean }>`
  display: flex;
  background: transparent;
  border-radius: 10px;
  padding: 1px;
  position: relative;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 70px;
  height: 22px;
  min-width: 70px;
  min-height: 22px;
  border: 1px solid rgba(255, 255, 255, 0.45);
  outline: none !important;
  -webkit-tap-highlight-color: transparent;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;

  &::before {
    content: '';
    position: absolute;
    top: 1px;
    left: ${props => props.$isEnabled ? '50%' : '1px'};
    width: calc(50% - 1px);
    height: calc(100% - 2px);
    background: rgba(255, 255, 255, 0.22);
    border-radius: 8px;
    transition: left 0.3s ease;
    z-index: 1;
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const ShowExhaustedToggleOption = styled.span<{ $isActive: boolean }>`
  flex: 1;
  padding: 0 4px;
  font-size: 9px;
  font-weight: 600;
  color: #ffffff;
  text-align: center;
  position: relative;
  z-index: 2;
  transition: color 0.3s ease;
  user-select: none;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
`;

/* 내 보유 아이템 섹션: 좌우 8px로 4열 그리드(504px)가 들어가도록 (Content 600px - SectionBox 40 - 16 = 504) */
const ItemCardCategorySection = styled.div`
  margin-bottom: 24px;
  text-align: center;
  padding-left: 8px;
  padding-right: 8px;
  box-sizing: border-box;

  @media (max-width: 600px) {
    padding-left: 16px;
    padding-right: 16px;
  }

  @media (max-width: 400px) {
    padding-left: 20px;
    padding-right: 20px;
  }
`;

const CategoryTitle = styled.h4`
  margin: 0 auto 16px auto;
  font-size: 1.1rem;
  color: #ffffff;
  font-weight: 600;
  background: var(--color-surface);
  padding: 3px 14px;
  border: 2px solid var(--color-border);
  border-radius: 20px;
  display: block;
  width: fit-content;
  text-align: center;
  
  /* 라이트 모드에서 텍스트 색상을 메인테마 초록색으로 */
  :root[data-theme='light'] & {
    color: var(--color-accent-strong);
  }
  
  .count {
    color: #ffffff;
    font-weight: 600;
    
    /* 라이트 모드에서 카운트 텍스트도 메인테마 초록색으로 */
    :root[data-theme='light'] & {
      color: var(--color-accent-strong);
    }
  }
`;

const ItemCardScrollWrapper = styled.div`
  position: relative;
  width: 100%;
`;

/* 아이템 카드 그리드: 넓은 화면에서 4열 고정(504px 중앙), 좁을 때 minmax로 3/2열·넘침 방지 */
const ItemCardScrollContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 8px;
  padding-bottom: 8px;
  justify-items: center;
  align-items: start;
  width: 100%;
  max-width: 504px;
  min-width: 0;
  margin-left: auto;
  margin-right: auto;
  box-sizing: border-box;

  /* 넓은 화면에서 4열 확실히 표시 (섹션 가용 너비가 504px 이상일 때) */
  @media (min-width: 600px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const ScrollGradientOverlay = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: 60px;
  height: 100%;
  background: linear-gradient(to right, transparent, rgba(255, 255, 255, 1));
  pointer-events: none;
  z-index: 1;
`;

const ScrollGradientOverlayLeft = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 60px;
  height: 100%;
  background: linear-gradient(to left, transparent, rgba(255, 255, 255, 1));
  pointer-events: none;
  z-index: 1;
`;

const ItemCardBlock = styled.div<{ $isSelected?: boolean; $isLocked?: boolean; $isExhausted?: boolean }>`
  width: 120px;
  max-width: 120px;
  height: 200px;
  border: 2px solid ${props => {
    if (props.$isLocked) return '#999';
    if (props.$isExhausted) return 'var(--color-accent-strong)';
    return props.$isSelected ? 'var(--color-accent-strong)' : 'var(--color-border)';
  }};
  border-radius: 8px;
  background: ${props => {
    if (props.$isLocked) return 'var(--color-surface-highlight)';
    return props.$isExhausted ? 'var(--color-surface-highlight)' : 'var(--color-surface)';
  }};
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px;
  cursor: ${props => props.$isLocked ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  position: relative;
  box-sizing: border-box;
  flex-shrink: 0;
  min-width: 0;
  overflow: visible;
  opacity: ${props => {
    if (props.$isLocked) return 0.5;
    return props.$isExhausted ? 0.6 : 1;
  }};
  filter: ${props => {
    if (props.$isLocked) return 'grayscale(100%)';
    return props.$isExhausted ? 'grayscale(100%)' : 'none';
  }};

  /* 라이트 모드에서 테두리를 메인테마 초록색으로 */
  :root[data-theme='light'] & {
    border-color: ${props => {
      if (props.$isLocked) return '#999';
      if (props.$isExhausted) return 'var(--color-accent-strong)';
      return 'var(--color-accent-strong)';
    }};
  }

  /* 다크모드에서 테두리 흰색으로 표시 */
  :root:not([data-theme='light']) & {
    border-color: ${props => {
      if (props.$isLocked) return 'rgba(255, 255, 255, 0.3)';
      if (props.$isExhausted) return 'rgba(255, 255, 255, 0.8)';
      return props.$isSelected ? 'rgba(255, 255, 255, 0.8)' : 'rgba(255, 255, 255, 0.8)';
    }};
  }

  ${props => props.$isSelected && !props.$isLocked && `
    box-shadow: 0 2px 4px rgba(var(--color-accent-rgb), 0.3);
  `}

  &:hover {
    transform: ${props => {
      if (props.$isLocked) return 'none';
      return (props.$isExhausted || props.$isSelected) ? 'translateY(-2px)' : 'translateY(-2px)';
    }};
    box-shadow: ${props => {
      if (props.$isLocked) return 'none';
      if (props.$isExhausted) return '0 4px 12px rgba(0, 0, 0, 0.1)';
      return '0 4px 12px rgba(0, 0, 0, 0.15)';
    }};
    border-color: ${props => {
      if (props.$isLocked) return '#999';
      if (props.$isExhausted) return '#ccc';
      return props.$isSelected ? 'var(--color-accent-strong)' : 'var(--color-border)';
    }};
  }

  /* 라이트 모드에서 호버 시에도 메인테마 초록색 테두리 유지 */
  :root[data-theme='light'] &:hover {
    border-color: ${props => {
      if (props.$isLocked) return '#999';
      if (props.$isExhausted) return '#ccc';
      return 'var(--color-accent-strong)';
    }};
  }

  /* 다크모드에서 호버 시에도 흰색 테두리 유지 */
  :root:not([data-theme='light']) &:hover {
    border-color: ${props => {
      if (props.$isLocked) return 'rgba(255, 255, 255, 0.3)';
      if (props.$isExhausted) return '#ccc';
      return 'rgba(255, 255, 255, 0.8)';
    }};
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const ItemCardIconArea = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  overflow: hidden;
  background: var(--color-panel);
  flex-shrink: 0;
  border: 2px solid transparent;

  /* 라이트 모드에서 이미지 영역 테두리를 메인테마 초록색으로 */
  :root[data-theme='light'] & {
    border-color: var(--color-accent-strong);
  }

  /* 다크모드에서 이미지 영역 테두리 흰색으로 표시 */
  :root:not([data-theme='light']) & {
    border-color: rgba(255, 255, 255, 0.8);
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 6px;
    border: none; /* 이미지 테두리 제거 */
  }
`;

const ItemCardPlaceholderIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ItemCardName = styled.div`
  width: 100%;
  margin-top: 8px;
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary);
  text-align: center;
  line-height: 1.3;
  flex-shrink: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

const ItemCardMemo = styled.div`
  width: 100%;
  margin-top: 4px;
  margin-bottom: auto;
  font-size: 8px;
  color: #999;
  text-align: center;
  word-break: break-word;
  line-height: 1.3;
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  min-height: 0;
  min-width: 0;
  padding: 0 2px;
`;

const ItemCardMetaGroup = styled.div`
  width: 100%;
  margin-top: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  flex-shrink: 0;
  min-width: 0;
`;

const ItemCardCost = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  svg {
    width: 18px;
    height: 18px;
  }

  span {
    font-size: 16px;
    font-weight: 700;
    color: #f39c12;
    line-height: 1;
  }
`;

const ItemCardLockedBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: rgba(153, 153, 153, 0.2);
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  color: #999;
  
  svg {
    width: 12px;
    height: 12px;
    stroke: currentColor;
    fill: none;
  }
  
  span {
    font-size: 11px;
  }
  
  :root:not([data-theme='light']) & {
    background: rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.5);
  }
`;

const ItemCardUseCount = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  svg {
    width: 16px;
    height: 16px;
  }

  span {
    font-size: 14px;
    font-weight: 700;
    color: #ff4757;
    line-height: 1;
  }
`;

// 과제 제출 모달
const ModalOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${props => props.$isOpen ? 'block' : 'none'};
  z-index: 1000;
  overflow-y: auto;
  padding: 80px 20px;
  
  @media (max-width: 768px) {
    padding: 60px 10px;
  }
`;

const ModalContent = styled.div`
  background: var(--color-surface);
  border-radius: 16px;
  padding: 16px 24px 24px 24px;
  max-width: 680px;
  width: 100%;
  margin: 0 auto;
  box-shadow: var(--modal-shadow-large);
  border: var(--modal-border);
  
  @media (max-width: 768px) {
    margin: 0 auto;
    padding: 20px 16px 24px 16px;
  }
`;

const ModalHeader = styled.div`
  position: relative;
  margin-bottom: 8px;
`;

const ModalCloseButton = styled.button`
  position: absolute;
  top: 0;
  right: 0;
  background: none;
  border: none;
  font-size: 24px;
  color: var(--color-text-gray);
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  
  /* 다크 모드: 흰색 */
  :root:not([data-theme='light']) & {
    color: #ffffff;
  }
  align-items: center;
  justify-content: center;
  z-index: 10;
  
  &:hover {
    color: var(--color-text-primary);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const ModalTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: var(--color-text-primary);
  margin: 0;
  
  :root[data-theme='light'] & {
    color: #212529;
  }
  
  :root:not([data-theme='light']) & {
    color: #f1f3f5;
  }
`;


const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: var(--color-text-primary);
  font-size: 14px;
`;

const Input = styled.input<{ $centerText?: boolean }>`
  width: 100%;
  padding: 12px;
  border: 2px solid var(--color-border);
  border-radius: 8px;
  font-size: 14px;
  box-sizing: border-box;
  text-align: ${props => props.$centerText ? 'center' : 'left'};
  background: var(--color-surface);
  color: var(--color-text-primary);
  
  &:focus {
    outline: none;
    border-color: var(--color-accent-text);
  }
  
  &::placeholder {
    color: var(--color-text-muted);
    text-align: ${props => props.$centerText ? 'center' : 'left'};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 2px solid var(--color-border);
  border-radius: 8px;
  font-size: 14px;
  box-sizing: border-box;
  min-height: 100px;
  resize: vertical;
  font-family: inherit;
  background: var(--color-surface);
  color: var(--color-text-primary);
  
  &:focus {
    outline: none;
    border-color: var(--color-accent-text);
  }
  
  &::placeholder {
    color: var(--color-text-muted);
  }
`;

const SubmissionInput = styled(Input)`
  border: 2px solid var(--color-border-strong);

  &:focus {
    border-color: var(--color-accent-strong);
  }
`;

const SubmissionTextArea = styled(TextArea)`
  border: 2px solid var(--color-border-strong);

  &:focus {
    border-color: var(--color-accent-strong);
  }
`;

const ModalAssignmentInfo = styled.div`
  text-align: center;
  margin-bottom: 12px;
  position: relative;
`;

const ModalCategoryRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 8px;
  position: relative;
`;

const ModalCategoryDisplay = styled.div<{ color?: string }>`
  display: inline-block;
  background-color: ${props => props.color || 'var(--color-border)'};
  color: ${props => props.color ? '#fff' : '#6c757d'};
  padding: 2px 10px;
  border-radius: 14px;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 6px;
  text-shadow: ${props => props.color ? '0 1px 2px rgba(0,0,0,0.3)' : 'none'};
`;

const ModalAssignmentTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: var(--color-text-primary);
  margin: 0 0 4px 0;
  
  :root[data-theme='light'] & {
    color: #212529;
  }
  
  :root:not([data-theme='light']) & {
    color: #f1f3f5;
  }
`;

const ModalAssignmentMeta = styled.div`
  color: #6c757d;
  font-size: 14px;
`;

const ModalAssignmentMetaText = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const RewardInfo = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: 4px;
`;

const RewardItem = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;
`;

const ItemCardIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  flex-shrink: 0;
`;

const RewardText = styled.span`
  font-size: inherit;
  font-weight: 600;
  color: inherit;
`;

const ModalDivider = styled.hr`
  border: none;
  height: 1px;
  background: var(--color-border);
  margin: 12px 0;
`;

const SubmissionImageEditBlock = styled.div`
  margin-bottom: 0;
`;

const submissionImageFrameBorder = '2px solid var(--color-border-strong)';

const SubmissionEmptyImageSlot = styled.div`
  width: 100%;
  aspect-ratio: 16 / 9;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  border: ${submissionImageFrameBorder};
  background: var(--color-surface);
  font-size: 14px;
  line-height: 1.6;
  color: var(--color-text-secondary);
  text-align: center;
  padding: 12px;
  box-sizing: border-box;
`;

const SubmissionImagePreviewWrap = styled.div`
  position: relative;
  width: 100%;
  min-width: 0;
  border-radius: 8px;
  border: ${submissionImageFrameBorder};
  background: var(--color-surface);
  overflow: hidden;
  box-sizing: border-box;
`;

const SubmissionImagePreview = styled.img`
  width: 100%;
  height: auto;
  display: block;
  vertical-align: top;
`;

const SubmissionImageActionRow = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
  width: 100%;
`;

const SubmissionImageActionBtn = styled.button<{ $fullWidth?: boolean }>`
  flex: ${(p) => (p.$fullWidth ? 'none' : '1')};
  width: ${(p) => (p.$fullWidth ? '100%' : 'auto')};
  min-width: 0;
  padding: 10px 14px;
  margin-top: ${(p) => (p.$fullWidth ? '8px' : '0')};
  border-radius: 8px;
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  color: var(--color-text-muted);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  box-sizing: border-box;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;

  &:hover {
    background: var(--color-panel);
    border-color: var(--color-text-muted);
    color: var(--color-text-primary);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  :root[data-theme='light'] & {
    background: #ffffff;
  }
`;

/** JSX·HMR 호환용 동일 컴포넌트 별칭 */
const SubmissionImagePickBtn = SubmissionImageActionBtn;

const ModalSection = styled.div<{ $centeredTitle?: boolean }>`
  margin-bottom: 16px;
  background: var(--color-section-bg);
  padding: 16px;
  border-radius: 12px;
  border: 1px solid var(--color-section-border);
  ${props => props.$centeredTitle && 'text-align: center;'}
  
  &:first-of-type {
    margin-top: 4px;
  }
`;

const ModalSectionTitle = styled.h4`
  margin: 0 0 12px 0;
  font-size: 1.1rem;
  color: var(--color-settings-section-title);
  font-weight: 600;
  background: var(--color-section-title-bg);
  padding: 4px 16px;
  border: 1px solid var(--color-section-title-border);
  border-radius: 20px;
  display: inline-block;
  text-align: center;
  width: 100%;
  box-sizing: border-box;
`;

const ModalSectionHint = styled.p`
  margin: 0 0 16px 0;
  font-size: 13px;
  line-height: 1.55;
  color: var(--color-text-muted);
  text-align: center;
  padding: 0 8px;
`;

const ModalAssignmentNotesText = styled.div`
  text-align: center;
  color: var(--color-text-muted);
  font-size: 13px;
  line-height: 1.5;
  margin: 8px 0 0 0;
  padding: 12px 16px;
  background-color: var(--color-panel);
  border-radius: 6px;
  white-space: pre-wrap;
  word-wrap: break-word;
  max-width: 100%;
`;

/* 과제 제출 모달 버튼 영역 (클래스챗 페이지 새 문제 생성 모달 취소하기/저장하기 버튼 스타일과 동일) */
const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 16px;
  width: 100%;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  flex: 1;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: ${props => props.$variant === 'primary' ? '2px solid var(--color-accent-strong)' : '2px solid var(--color-text-muted)'};
  background: ${props => props.$variant === 'primary' ? 'var(--color-accent-strong)' : 'var(--color-surface)'};
  color: ${props => props.$variant === 'primary' ? 'white' : 'var(--color-text-secondary)'};

  /* secondary(닫기): data-variant="modal-secondary" 시 App.css에서 라이트 모드 초록 테두리/텍스트/호버 연한 초록 배경 적용 */
  ${props => props.$variant === 'secondary' && `
    &:disabled {
      background: var(--color-panel);
      border-color: var(--color-border);
      cursor: not-allowed;
      opacity: 0.6;
    }
  `}

  ${props => props.$variant === 'primary' && `
    &:hover:not(:disabled) {
      background: rgba(var(--color-accent-rgb), 0.9);
      border-color: rgba(var(--color-accent-rgb), 0.9);
    }
  &:disabled {
      background: var(--color-text-muted);
      border-color: var(--color-text-muted);
    cursor: not-allowed;
      opacity: 0.6;
    }
    /* 새 문제 생성 모달과 동일: 라이트 모드에서 비활성화 시 연한 회색 #c6c6c6 (불투명도 없이 동일 표현) */
    :root[data-theme='light'] &:disabled {
      background: #c6c6c6;
      border-color: var(--color-border-strong);
      opacity: 1;
    }
  `}
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const ExistingSubmissionInfo = styled.div`
  background: var(--color-panel);
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
  text-align: center;
`;

const InfoLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: 'var(--color-text-gray)';
  margin-bottom: 4px;
`;

const InfoValue = styled.div`
  font-size: 14px;
  color: var(--color-text-primary);
  word-break: break-all;
`;

const VerifyButton = styled.button<{ $verified?: boolean }>`
  width: 100%;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: 2px solid ${props => props.$verified ? 'var(--color-accent-strong)' : 'var(--color-accent-strong)'};
  background: ${props => props.$verified ? 'var(--color-accent-strong)' : 'var(--color-surface)'};
  color: ${props => props.$verified ? 'white' : 'var(--color-accent-strong)'};
  min-width: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 12px;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    background: ${props => props.$verified ? 'var(--color-accent-strong)' : 'var(--color-accent-soft)'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
  
  svg {
    width: 18px;
    height: 18px;
  }

  /* 다크 모드: 확인하기 버튼 텍스트·테두리 흰색 */
  :root:not([data-theme='light']) & {
    border-color: ${props => props.$verified ? 'var(--color-accent-strong)' : '#ffffff'};
    color: ${props => props.$verified ? 'white' : '#ffffff'};
  }
  :root:not([data-theme='light']) &:hover:not(:disabled) {
    border-color: #ffffff;
    color: #ffffff;
  }
`;

// 보상 토스트 스타일
const RewardToastOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 10000;
  animation: ${props => props.$isOpen ? 'fadeIn' : 'none'} 0.2s ease-in-out;
  cursor: pointer;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const RewardToastContent = styled.div`
  background: var(--color-surface);
  padding: 32px;
  border-radius: 16px;
  max-width: 90vw;
  width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  animation: slideUp 0.3s ease-out;
  box-shadow: var(--modal-shadow);
  border: var(--modal-border);
  
  @keyframes slideUp {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

// 토스트 메시지 스타일
const ToastOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10001;
  animation: fadeIn 0.2s ease-in-out;
  pointer-events: none;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ToastMessage = styled.div`
  background: #ffffff !important;
  color: #000000 !important;
  padding: 20px 32px;
  border-radius: 12px;
  font-size: 18px;
  font-weight: 600;
  text-align: center;
  box-shadow: var(--modal-shadow-large);
  animation: slideUp 0.3s ease-out;
  
  @keyframes slideUp {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

const RewardToastTitle = styled.h3`
  font-size: 24px;
  font-weight: 700;
  color: var(--color-text-primary);
  text-align: center;
  margin: 0 0 24px 0;
`;

const RewardMessage = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text-primary);
  text-align: center;
  margin-bottom: 24px;
  white-space: pre-line;
`;

const RewardSection = styled.div`
  margin-top: 16px;
  background: var(--color-panel);
  padding: 16px;
  border-radius: 12px;
  border: 1px solid var(--color-border);
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const RewardSectionTitle = styled.h4`
  margin: 0 auto 16px auto;
  font-size: 1.1rem;
  color: #ffffff;
  font-weight: 600;
  background: var(--color-surface);
  padding: 3px 14px;
  border: 2px solid var(--color-border);
  border-radius: 20px;
  display: block;
  text-align: center;
  width: fit-content;
  :root[data-theme='light'] & {
    color: var(--color-accent-strong);
    border-color: var(--color-accent-strong);
  }
`;

const RewardCoinsDisplay = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 24px;
  font-weight: 700;
  color: #f39c12;
  
  svg {
    width: 32px;
    height: 32px;
  }
`;

/* 획득 아이템 카드 래퍼: 그리드 컨테이너를 모달 중앙에 배치 */
const RewardItemCardsWrapper = styled.div`
  width: 100%;
  margin-top: 12px;
  display: flex;
  justify-content: center;
  box-sizing: border-box;
`;

/* 획득 아이템 카드 그리드: 열 개수는 아이템 수에 맞춰 width: fit-content로 중앙 정렬 */
const RewardItemCardsContainer = styled.div<{ $columnCount: number }>`
  display: grid;
  grid-template-columns: repeat(${props => Math.min(props.$columnCount, 4)}, 120px);
  gap: 8px;
  justify-items: center;
  width: fit-content;
  max-width: 100%;
  box-sizing: border-box;
`;

const RewardItemCard = styled.div`
  width: 120px;
  height: 200px;
  border: 2px solid var(--color-accent-strong);
  border-radius: 8px;
  background: var(--color-surface);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10%;
  position: relative;
  box-sizing: border-box;
  transition: all 0.2s ease;

  /* 다크모드에서 테두리 흰색으로 표시 */
  :root:not([data-theme='light']) & {
    border-color: rgba(255, 255, 255, 0.8);
  }
`;

const RewardItemCardIconArea = styled.div`
  width: 100%;
  aspect-ratio: 1 / 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  overflow: hidden;
  background: var(--color-panel);
  border: 2px solid var(--color-border);

  /* 다크모드에서 이미지 영역 테두리 흰색으로 표시 */
  :root:not([data-theme='light']) & {
    border-color: rgba(255, 255, 255, 0.8);
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const RewardItemCardName = styled.div`
  width: 100%;
  margin-top: 8%;
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary);
  text-align: center;
  line-height: 1.3;
  flex-shrink: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RewardItemCardMemo = styled.div`
  width: 100%;
  margin-top: 4%;
  margin-bottom: auto;
  font-size: 8px;
  color: #999;
  text-align: center;
  word-break: break-word;
  line-height: 1.3;
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  min-height: 0;
`;

const RewardCloseButton = styled.button`
  width: 100%;
  padding: 12px 24px;
  margin-top: 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: 2px solid var(--color-accent-strong);
  background: var(--color-accent-strong);
  color: white;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    background: rgba(var(--color-accent-rgb), 0.9);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

// 코인 히스토리 모달 스타일
const CoinHistoryModalContent = styled(ModalContent)`
  max-width: 800px;
`;

const CoinTable = styled.div`
  width: 100%;
  margin-top: 16px;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  overflow: hidden;
`;

const CoinTableHeader = styled.div`
  display: grid;
  grid-template-columns: 50px 60px 1fr 80px;
  background: var(--color-panel);
  border-bottom: 1px solid var(--color-border);

  @media (max-width: 600px) {
    grid-template-columns: 45px 55px 1fr 75px;
  }
`;

const CoinHeaderCell = styled.div<{ columnIndex?: number }>`
  padding: 8px 4px;
  text-align: center;
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-secondary);
  border-right: 1px solid var(--color-border);
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;

  &:last-child {
    border-right: none;
  }

  @media (max-width: 600px) {
    padding: 8px;
    font-size: 12px;
  }
`;

const CoinTableBody = styled.div``;

const CoinTableRow = styled.div<{ $isEmptyState?: boolean; $isDeleted?: boolean }>`
  display: grid;
  grid-template-columns: 50px 60px 1fr 80px;
  border-top: 1px solid var(--color-border);
  background: var(--color-surface);
  transition: all 0.2s ease;
  position: relative;

  ${props => props.$isEmptyState && `
    grid-template-columns: 1fr;
  `}

  @media (max-width: 600px) {
    grid-template-columns: 45px 55px 1fr 75px;

    ${props => props.$isEmptyState && `
      grid-template-columns: 1fr;
    `}
  }

  &:hover {
    background: #f1f3f5;
  }
  
  /* 다크 모드: 포인트 조회 페이지와 동일한 호버 색상 */
  :root:not([data-theme='light']) & {
    &:hover {
      background: var(--color-surface-highlight);
    }
  }
`;

const CoinCell = styled.div<{ columnIndex?: number; colSpan?: number; $coinValue?: number }>`
  position: relative;
  padding: ${props =>
    props.columnIndex === 2 ? '8px 12px' : '8px 4px'}; // 메모열 패딩 증가
  font-size: 14px;
  font-weight: ${props => props.columnIndex === 3 ? 700 : 400}; // 코인 값 볼드
  color: ${props => {
    // 획득 코인 열이고 음수인 경우 빨간색
    if (props.columnIndex === 3 && props.$coinValue && props.$coinValue < 0) {
      return '#ff6b6b';
    }
    return 'var(--color-text-primary)';
  }};
  border-right: 1px solid var(--color-border);
  text-align: ${props => {
    // 타입열(0), 날짜열(1), 획득코인열(3)은 가운데 정렬
    if (props.columnIndex === 0 || props.columnIndex === 1 || props.columnIndex === 3) {
      return 'center';
    }
    return 'left';
  }};
  display: ${props => props.columnIndex === 2 ? 'block' : 'flex'};
  align-items: ${props => props.columnIndex === 2 ? 'flex-start' : 'center'};
  justify-content: ${props => {
    // 타입열(0), 날짜열(1), 획득코인열(3)은 가운데 정렬
    if (props.columnIndex === 0 || props.columnIndex === 1 || props.columnIndex === 3) {
      return 'center';
    }
    return 'flex-start';
  }};
  word-wrap: break-word;
  word-break: break-word;
  white-space: ${props => props.columnIndex === 2 ? 'normal' : 'nowrap'};
  overflow: hidden;
  min-width: 0;
  max-width: 100%;

  &:last-child {
    border-right: none;
  }

  ${props => props.colSpan && `
    grid-column: 1 / -1;
    text-align: center;
    justify-content: center;
    border-right: none;
  `}

  @media (max-width: 600px) {
    padding: 6px 8px;
    font-size: 12px;
  }
`;

const CoinTypeBadge = styled.span<{ $coinType: 'General' | 'Levelup' }>`
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: bold;
  color: white;

  ${props => props.$coinType === 'General' ? `
    background: #6c757d;
  ` : `
    background: var(--color-accent-strong);
  `}
`;

// 과제 포인트 타입 뱃지
const TypeBadge = styled.span<{ type: string }>`
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: bold;
  color: white;
  
  ${props => props.type === 'general' ? `
    background: #6c757d; /* 일반과제 - 기본 회색 */
  ` : props.type === 'project' ? `
    background: var(--color-accent-strong); /* 프로젝트 - 메인 청록색 */
  ` : `
    background: #ff6b6b; /* 루틴과제 - 루틴과제 고유 카테고리 색상 */
  `}
`;

// 과제 포인트/긍정행동 포인트 테이블 스타일
const RewardTable = styled.div`
  width: 100%;
  margin-top: 16px;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  overflow: hidden;
`;

const RewardTableHeader = styled.div<{ $columns?: number }>`
  display: grid;
  grid-template-columns: ${props => props.$columns === 5 ? '50px 60px 200px 80px auto' : '50px 60px 1fr 80px'};
  background: var(--color-panel);
  border-bottom: 1px solid var(--color-border);

  @media (max-width: 600px) {
    grid-template-columns: ${props => props.$columns === 5 ? '50px 60px 1fr 70px' : '45px 55px 1fr 75px'};
    ${props => props.$columns === 5 && 'grid-template-rows: auto auto;'}
  }
`;

const RewardHeaderCell = styled.div<{ columnIndex?: number }>`
  padding: 8px 4px;
  text-align: center;
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary);
  border-right: 1px solid var(--color-border);
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;

  &:last-child {
    border-right: none;
  }

  @media (max-width: 600px) {
    padding: ${props => props.columnIndex === 4 ? '8px 12px' : '6px 8px'};
    font-size: 12px;
    
    ${props => props.columnIndex === 0 && `
      grid-row: 1; 
      grid-column: 1;
    `} /* 타입 */
    ${props => props.columnIndex === 1 && `
      grid-row: 1; 
      grid-column: 2;
    `} /* 마감일/날짜 */
    ${props => props.columnIndex === 2 && `
      grid-row: 1; 
      grid-column: 3;
    `} /* 과제명/메모 */
    ${props => props.columnIndex === 3 && `
      grid-row: 1; 
      grid-column: 4; 
      border-right: none;
    `} /* 포인트 */
    ${props => props.columnIndex === 4 && `
      grid-row: 2; 
      grid-column: 1 / 5; 
      border-top: 1px solid var(--color-border);
      border-right: none;
    `} /* 메모 (과제 포인트만) */
  }
`;

const RewardTableBody = styled.div``;

const RewardTableRow = styled.div<{ $isEmptyState?: boolean; $columns?: number }>`
  display: grid;
  grid-template-columns: ${props => props.$columns === 5 ? '50px 60px 200px 80px auto' : '50px 60px 1fr 80px'};
  border-top: 1px solid var(--color-border);
  background: var(--color-surface);
  transition: all 0.2s ease;
  position: relative;

  ${props => props.$isEmptyState && `
    grid-template-columns: 1fr;
  `}

  @media (max-width: 600px) {
    grid-template-columns: ${props => props.$columns === 5 ? '50px 60px 1fr 70px' : '45px 55px 1fr 75px'};
    ${props => props.$columns === 5 && 'grid-template-rows: auto auto;'}

    ${props => props.$isEmptyState && `
      grid-template-columns: 1fr;
      grid-template-rows: auto;
    `}
  }

  &:hover {
    background: #f1f3f5;
  }
  
  /* 다크 모드: 포인트 조회 페이지와 동일한 호버 색상 */
  :root:not([data-theme='light']) & {
    &:hover {
      background: var(--color-surface-highlight);
    }
  }
`;

const RewardTableCell = styled.div<{ columnIndex?: number; colSpan?: number }>`
  position: relative;
  padding: ${props =>
    props.columnIndex === 2 || props.columnIndex === 4 ? '8px 12px' : '8px 4px'};
  font-size: 14px;
  font-weight: ${props => props.columnIndex === 3 ? 700 : 400};
  color: var(--color-text-primary);
  border-right: 1px solid var(--color-border);
  text-align: ${props => {
    if (props.columnIndex === 0 || props.columnIndex === 1 || props.columnIndex === 3) {
      return 'center';
    }
    return 'left';
  }};
  display: ${props => props.columnIndex === 2 || props.columnIndex === 4 ? 'block' : 'flex'};
  align-items: ${props => props.columnIndex === 2 || props.columnIndex === 4 ? 'flex-start' : 'center'};
  justify-content: ${props => {
    if (props.columnIndex === 0 || props.columnIndex === 1 || props.columnIndex === 3) {
      return 'center';
    }
    return 'flex-start';
  }};
  word-wrap: break-word;
  word-break: break-word;
  white-space: ${props => props.columnIndex === 2 || props.columnIndex === 4 ? 'normal' : 'nowrap'};
  overflow: hidden;
  min-width: 0;
  max-width: 100%;

  &:last-child {
    border-right: none;
  }

  ${props => props.colSpan && `
    grid-column: 1 / -1;
    text-align: center;
    justify-content: center;
    border-right: none;
  `}

  @media (max-width: 600px) {
    padding: ${props => props.columnIndex === 4 ? '8px 12px' : '6px 8px'};
    font-size: 12px;
    
    ${props => props.columnIndex === 0 && `
      grid-row: 1; 
      grid-column: 1;
    `} /* 타입 */
    ${props => props.columnIndex === 1 && `
      grid-row: 1; 
      grid-column: 2;
    `} /* 마감일/날짜 */
    ${props => props.columnIndex === 2 && `
      grid-row: 1; 
      grid-column: 3;
    `} /* 과제명/메모 */
    ${props => props.columnIndex === 3 && `
      grid-row: 1; 
      grid-column: 4; 
      border-right: none;
    `} /* 포인트 */
    ${props => props.columnIndex === 4 && `
      grid-row: 2; 
      grid-column: 1 / 5; 
      border-top: 1px solid var(--color-border);
      border-right: none;
    `} /* 메모 (과제 포인트만) */
  }
`;

// 타임태그 관련 스타일
const MemoWithTimeTag = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

const TimeTagBadge = styled.span`
  background: var(--color-text-muted);
  color: white;
  font-size: 10px;
  font-weight: 500;
  padding: 2px 6px;
  border-radius: 4px;
  white-space: nowrap;
  flex-shrink: 0;
`;

const MemoText = styled.span`
  flex: 1;
  min-width: 0;
`;

interface StudentResultData extends StudentAccessResult {
  // 게스트 모드용 추가 정보 (LoginPage 멤버 입장에서 전달)
  guestName?: string;
  guestNumber?: string;
  guestId?: string;
}

// 레벨 색상 정의 (포인트 조회 페이지와 동일: 10의 배수는 메인테마, 나머지는 1~9 색상 순환)
const levelColors = [
  '#FC5555', // 레벨1, 11, 21...
  '#FF9933', // 레벨2, 12, 22...
  '#CCCC00', // 레벨3, 13, 23...
  '#669900', // 레벨4, 14, 24...
  '#119473', // 레벨5, 15, 25...
  '#0066CC', // 레벨6, 16, 26...
  '#9900CC', // 레벨7, 17, 27...
  '#920049', // 레벨8, 18, 28...
  '#D60093'  // 레벨9, 19, 29...
];

// 레벨별 배경 색상 가져오기 (포인트 조회 페이지와 동일)
const getLevelColor = (level: number): string => {
  if (level % 10 === 0) {
    return 'var(--color-level-0)';
  }
  const colorIndex = (level % 10) - 1;
  return levelColors[colorIndex];
};

// 레벨별 텍스트 색상 가져오기 (포인트 조회 페이지와 동일, 라이트/다크 모드 --color-level-0-text 사용)
const parseFirestoreDate = (value: unknown): Date | null => {
  if (value == null) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === 'object') {
    const v = value as { toDate?: () => Date; seconds?: number; nanoseconds?: number };
    if (typeof v.toDate === 'function') {
      const d = v.toDate();
      return Number.isNaN(d.getTime()) ? null : d;
    }
    if (typeof v.seconds === 'number') {
      const d = new Date(v.seconds * 1000 + (v.nanoseconds || 0) / 1_000_000);
      return Number.isNaN(d.getTime()) ? null : d;
    }
  }
  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
};

const formatSubmittedAt = (value: unknown): string => {
  const d = parseFirestoreDate(value);
  return d ? d.toLocaleString('ko-KR') : '정보 없음';
};

const getLevelTextColor = (level: number): string => {
  if (level % 10 === 0) {
    return 'var(--color-level-0-text)';
  }
  return 'white';
};

// 프로필 내 보유 아이템용 레벨 배지 (레벨 10: 다크=흰배경+검정글자, 라이트=메인테마 초록+흰글자)
const ProfileItemCardLevelBadge = styled.div<{ $levelColor: string; $level?: number }>`
  position: absolute;
  top: 2px;
  left: 2px;
  padding: 1px 4px;
  border-radius: 3px;
  font-size: 8px;
  font-weight: 700;
  z-index: 10;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  line-height: 1.2;
  ${props => props.$level !== undefined && props.$level % 10 === 0 ? `
    background: #ffffff;
    color: #000000;
    :root[data-theme='light'] & {
      background: var(--color-accent-strong);
      color: #ffffff;
    }
  ` : `
    background: ${props.$levelColor};
    color: ${props.$level !== undefined ? getLevelTextColor(props.$level) : 'white'};
  `}
`;

// 선택지 버튼용 색상 배열 (1~9번: levelColors, 10번: 테마 변수; hexToRgba 호환을 위해 10번은 별도 처리)
const choiceButtonColorByIndex = (oneBasedIndex: number): string => {
  if (oneBasedIndex === 10) return 'var(--color-level-0)';
  return levelColors[(oneBasedIndex % 10) - 1];
};

export const StudentChatPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [accessSession, setAccessSession] = useState<StudentResultData | undefined>(
    () => location.state as StudentResultData | undefined
  );
  useEffect(() => {
    setAccessSession(location.state as StudentResultData | undefined);
  }, [location.key]);

  const resultData = accessSession as StudentResultData;
  const isGuestMode = !!resultData?.isGuestMode;

  useEffect(() => {
    if (isGuestMode) return;
    if (!resultData?.pendingStudentSummary || !resultData.member || !resultData.classInfo) return;
    let cancelled = false;
    void (async () => {
      try {
        const student = await classCodeService.getStudentSummary(
          resultData.classInfo!.userId,
          resultData.classInfo!.classId,
          resultData.member!,
          resultData.classInfo!.memberGroup
        );
        if (!cancelled) {
          setAccessSession((prev) =>
            prev
              ? {
                  ...prev,
                  student,
                  member: undefined,
                  pendingStudentSummary: false
                }
              : prev
          );
        }
      } catch (e) {
        console.error('멤버 종합 정보 로드 실패:', e);
        if (!cancelled) {
          alert('멤버 정보를 불러오지 못했습니다. 다시 입장해 주세요.');
          navigate('/', { replace: true });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    isGuestMode,
    navigate,
    resultData?.pendingStudentSummary,
    resultData?.member,
    resultData?.classInfo?.userId,
    resultData?.classInfo?.classId,
    resultData?.classInfo?.memberGroup
  ]);
  // 게스트 모드일 때도 main 페이지부터 시작 (퀴즈 버튼 클릭 시 접속 정보 등록)
  const initialView: 'main' | 'quiz' | 'profile' = 'main';
  
  const [assignments, setAssignments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [todayRoutineAssignments, setTodayRoutineAssignments] = useState<Array<{ 
    id: string; 
    routineAssignmentId: string; 
    dateId: string; 
    title: string; 
    status: string; 
    basePoints: number;
    completionRate?: number;
    confirmedCount?: number;
    totalDates?: number;
    todayCount?: number;
  }>>([]);
  const [loadingTodayRoutine, setLoadingTodayRoutine] = useState(false);
  const [routineTogglingId, setRoutineTogglingId] = useState<string | null>(null);
  const [pendingRoutineChanges, setPendingRoutineChanges] = useState<Map<string, { routineId: string; dateId: string; newStatus: string }>>(new Map());
  const [isSavingRoutines, setIsSavingRoutines] = useState(false);
  const [initialRoutineStatuses, setInitialRoutineStatuses] = useState<Map<string, string>>(new Map());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState<any>(null);
  const [submissionLink, setSubmissionLink] = useState('');
  const [submissionMessage, setSubmissionMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [codiUserType, setCodiUserType] = useState<string | undefined>(undefined);
  const [isSubmissionCropOpen, setIsSubmissionCropOpen] = useState(false);
  const [pendingSubmissionImage, setPendingSubmissionImage] = useState<{ blob: Blob; mimeType: string } | null>(null);
  const [pendingImagePreviewUrl, setPendingImagePreviewUrl] = useState<string | null>(null);
  const [existingSubmissionImageUrl, setExistingSubmissionImageUrl] = useState<string | null>(null);
  const [existingSubmissionImageLoadFailed, setExistingSubmissionImageLoadFailed] = useState(false);
  const [removedExistingSubmissionImage, setRemovedExistingSubmissionImage] = useState(false);
  const [levelUpPoints, setLevelUpPoints] = useState(() =>
    normalizeLevelUpPoints(
      (location.state as StudentResultData | undefined)?.classInfo?.levelUpPoints,
      50
    )
  );
  const [memberGroupNames, setMemberGroupNames] = useState<Record<string, string>>(
    () => (location.state as StudentResultData | undefined)?.classInfo?.memberGroupNames ?? {}
  );

  useEffect(() => {
    const fromSession = resultData?.classInfo?.memberGroupNames;
    if (fromSession && Object.keys(fromSession).length > 0) {
      setMemberGroupNames(fromSession);
    }
  }, [resultData?.classInfo?.memberGroupNames]);
  /** 포인트 조회 테이블과 동일한 총점(과제+긍정행동) — 레벨 계산용 */
  const [profileTableTotalPoints, setProfileTableTotalPoints] = useState<number | null>(null);
  const [isThoughtSectionOpen, setIsThoughtSectionOpen] = useState(false);
  const [thoughtText, setThoughtText] = useState('');
  const [isPointsExpanded, setIsPointsExpanded] = useState(false);
  
  // 테마 모드 상태
  const [themeMode, setThemeMode] = useState<'dark' | 'light'>(() => {
    if (typeof window !== 'undefined') {
      const stored = window.localStorage.getItem('app-theme-mode');
      if (stored === 'dark' || stored === 'light') {
        return stored;
      }
    }
    return 'dark';
  });

  // 학생 소속 멤버 그룹 (입장코드 검증 시점에 이미 그룹 정보 포함됨. 초기값을 resultData에서 설정해 첫 로드 시 올바른 그룹 사용)
  const [studentMemberGroup, setStudentMemberGroup] = useState<string>(
    () => resultData?.classInfo?.memberGroup || 'A'
  );

  // resultData 로드 후 그룹 동기화
  useEffect(() => {
    const groupFromAccess = resultData?.classInfo?.memberGroup;
    if (groupFromAccess) setStudentMemberGroup(groupFromAccess);
  }, [resultData?.classInfo?.memberGroup]);

  const memberPageScope = useMemo(() => {
    if (!resultData?.classInfo?.userId || !resultData?.classInfo?.classId || isGuestMode) return null;
    const studentId = resultData?.student?.basicInfo?.id;
    if (!studentId) return null;
    return buildMemberPageScope({
      userId: resultData.classInfo.userId,
      classId: resultData.classInfo.classId,
      memberGroup: studentMemberGroup,
      studentId,
      classCode: resultData.classInfo.classCode
    });
  }, [
    isGuestMode,
    resultData?.classInfo?.userId,
    resultData?.classInfo?.classId,
    resultData?.classInfo?.classCode,
    resultData?.student?.basicInfo?.id,
    studentMemberGroup
  ]);
  
  // 햄버거 메뉴 상태
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  
  // 사용자 설정 모달 상태
  const [showProfileEditModal, setShowProfileEditModal] = useState(false);
  
  // 테마 모드 변경 시 document에 반영
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.dataset.theme = themeMode;
    }
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('app-theme-mode', themeMode);
    }
  }, [themeMode]);
  
  // 테마 토글 함수
  const handleThemeToggle = useCallback(() => {
    setThemeMode(prev => (prev === 'dark' ? 'light' : 'dark'));
  }, []);
  
  // 드롭다운 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.hamburger-menu') && !target.closest('.menu-dropdown')) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isMenuOpen]);
  
  const handleHamburgerClick = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleMenuLogout = useCallback(() => {
    setIsMenuOpen(false);
    signOut(auth)
      .then(() => {
        window.location.href = '/';
      })
      .catch((err) => {
        console.error('로그아웃 오류:', err);
        window.location.href = '/';
      });
  }, []);

  const effectiveStudentId =
    isGuestMode
      ? (resultData?.guestId ||
        `guest-${resultData?.classInfo?.classCode || 'unknown'}-${resultData?.guestNumber || '0'}`)
      : (resultData?.student?.basicInfo.id || '');

  const effectiveName = isGuestMode
    ? (resultData?.guestName || '게스트')
    : (resultData?.student?.basicInfo.name || '');

  const effectiveNumberLabel = isGuestMode
    ? 'G'
    : (resultData?.student?.basicInfo.number || '');
  
  // 메인 화면 상태 (퀴즈, 프로필, 매니저 중 선택)
  const [selectedView, setSelectedView] = useState<'main' | 'quiz' | 'profile' | 'manager'>(initialView);
  
  // 퀴즈 관련 상태
  const [activeQuiz, setActiveQuiz] = useState<ChatQuiz | null>(null);
  const [activeQuizId, setActiveQuizId] = useState<string | null>(null);
  const [activeQuizSetId, setActiveQuizSetId] = useState<string | null>(null);
  // 활성 퀴즈가 저장된 컨테이너(세트 문서) ID: 'main' 또는 setId
  // NOTE: 세트 "재생" 여부는 activeQuizSetId로만 판단한다.
  const [activeQuizContainerId, setActiveQuizContainerId] = useState<string | null>(null);
  const [studentCreateMode, setStudentCreateMode] = useState(false);
  const [studentCreateSetId, setStudentCreateSetId] = useState<string | null>(null);
  const [setQuizzes, setSetQuizzes] = useState<ChatQuiz[]>([]);
  const [setTitle, setSetTitle] = useState<string>('');
  const [setAnswers, setSetAnswers] = useState<Record<string, number[] | string>>({});
  const [isSubmittingSet, setIsSubmittingSet] = useState(false);
  const [isSetSubmitted, setIsSetSubmitted] = useState(false);
  const [setQuizStatuses, setSetQuizStatuses] = useState<Record<string, { submitted: boolean; isCorrect?: boolean | null }>>({});
  const [choiceAnswer, setChoiceAnswer] = useState<number[]>([]);
  const isSetMode = !!(activeQuizSetId && activeQuizId);
  // 제출된 답변들 (채팅 메시지처럼 쌓임)
  const [submittedAnswers, setSubmittedAnswers] = useState<Array<{
    id: string;
    type: 'choice' | 'short' | 'essay';
    answer: number[] | string;
    timestamp: Date;
  isCorrect?: boolean;
  hasContent: boolean;
  }>>([]);
  // 학생 문제 생성 상태
  const [isStudentCreateOpen, setIsStudentCreateOpen] = useState(false);
  const [studentQuestionType, setStudentQuestionType] = useState<'choice' | 'short' | 'essay'>('choice');
  const [studentQuestionText, setStudentQuestionText] = useState('');
  const [studentChoices, setStudentChoices] = useState(['', '', '', '']);
  const [studentChoiceAnswers, setStudentChoiceAnswers] = useState<number[]>([]);
  const [studentShortAnswers, setStudentShortAnswers] = useState<string[]>(['']);
  const [studentEssayGuide, setStudentEssayGuide] = useState<string>('');
  /** 문제 만들기에서 한 번이라도 제출 완료했으면 true → 유형 선택 버튼 숨기고 완료 메시지 표시 */
  const [hasSubmittedStudentCreate, setHasSubmittedStudentCreate] = useState(false);
  const studentQuestionInputRef = useRef<HTMLTextAreaElement | null>(null);
  
  // 학생용 전체공지노트 프리뷰/목록(읽기 전용)
  const [latestGeneralNoticePreview, setLatestGeneralNoticePreview] = useState<any | null>(null);
  const [isGeneralNoticeModalOpen, setIsGeneralNoticeModalOpen] = useState(false);
  const [generalNoticeNotes, setGeneralNoticeNotes] = useState<any[]>([]);
  const [isLoadingGeneralNoticeNotes, setIsLoadingGeneralNoticeNotes] = useState(false);
  const [expandedGeneralNoticeId, setExpandedGeneralNoticeId] = useState<string | null>(null);
  const [generalNoticePage, setGeneralNoticePage] = useState<number>(1);
  const GENERAL_NOTICE_PER_PAGE = 5;

  // 학생용 개별공지노트 프리뷰/목록(읽기 전용)
  const [latestIndividualNoticePreview, setLatestIndividualNoticePreview] = useState<any | null>(null);
  const [isIndividualNoticeModalOpen, setIsIndividualNoticeModalOpen] = useState(false);
  const [individualNoticeNotes, setIndividualNoticeNotes] = useState<any[]>([]);
  const [isLoadingIndividualNoticeNotes, setIsLoadingIndividualNoticeNotes] = useState(false);
  const [expandedIndividualNoticeId, setExpandedIndividualNoticeId] = useState<string | null>(null);
  const [individualNoticePage, setIndividualNoticePage] = useState<number>(1);
  const INDIVIDUAL_NOTICE_PER_PAGE = 5;
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 4,
      },
    })
  );

  // 학생 문제 만들기 모드가 켜지면 패널 자동 열기/닫기
  useEffect(() => {
    setIsStudentCreateOpen(studentCreateMode);
    if (!studentCreateMode) setHasSubmittedStudentCreate(false);
  }, [studentCreateMode]);

  const sortGeneralNoticesByDateDesc = useCallback((notes: any[]) => {
    const getCreatedAtMillis = (n: any) => {
      const c = n?.createdAt;
      return typeof c?.toMillis === 'function'
        ? c.toMillis()
        : typeof c === 'number'
          ? c
          : 0;
    };
    return [...notes].sort((a, b) => {
      const da = (a?.date || '').toString();
      const db = (b?.date || '').toString();
      if (da !== db) return db.localeCompare(da); // YYYY-MM-DD
      return getCreatedAtMillis(b) - getCreatedAtMillis(a);
    });
  }, []);

  const loadGeneralNoticeNotesForStudent = useCallback(async (force?: boolean) => {
    if (!memberPageScope) {
      setGeneralNoticeNotes([]);
      setLatestGeneralNoticePreview(null);
      return;
    }
    setIsLoadingGeneralNoticeNotes(true);
    try {
      const notes = await studentMemberPageData.loadGeneralNotices(memberPageScope, { force });
      const converted = sortGeneralNoticesByDateDesc(notes);
      setGeneralNoticeNotes(converted);
      setLatestGeneralNoticePreview(converted[0] || null);
    } catch (e) {
      setGeneralNoticeNotes([]);
      setLatestGeneralNoticePreview(null);
    } finally {
      setIsLoadingGeneralNoticeNotes(false);
    }
  }, [memberPageScope, sortGeneralNoticesByDateDesc]);

  useEffect(() => {
    loadGeneralNoticeNotesForStudent();
  }, [loadGeneralNoticeNotesForStudent]);

  const handleOpenGeneralNoticeModal = useCallback(async () => {
    await loadGeneralNoticeNotesForStudent();
    setExpandedGeneralNoticeId(null);
    setGeneralNoticePage(1);
    setIsGeneralNoticeModalOpen(true);
  }, [loadGeneralNoticeNotesForStudent]);

  const sortIndividualNoticesByDateDesc = useCallback((notes: any[]) => {
    const getCreatedAtMillis = (n: any) => {
      const c = n?.createdAt;
      return typeof c?.toMillis === 'function'
        ? c.toMillis()
        : typeof c === 'number'
          ? c
          : 0;
    };
    return [...notes].sort((a, b) => {
      const da = (a?.date || '').toString();
      const db = (b?.date || '').toString();
      if (da !== db) return db.localeCompare(da); // YYYY-MM-DD
      return getCreatedAtMillis(b) - getCreatedAtMillis(a);
    });
  }, []);

  const loadIndividualNoticeNotesForStudent = useCallback(async (force?: boolean) => {
    if (!memberPageScope) return;
    setIsLoadingIndividualNoticeNotes(true);
    try {
      const notes = await studentMemberPageData.loadIndividualNotices(memberPageScope, { force });
      const converted = sortIndividualNoticesByDateDesc(notes);
      setIndividualNoticeNotes(converted);
      setLatestIndividualNoticePreview(converted[0] || null);
    } catch (e) {
      setIndividualNoticeNotes([]);
      setLatestIndividualNoticePreview(null);
    } finally {
      setIsLoadingIndividualNoticeNotes(false);
    }
  }, [memberPageScope, sortIndividualNoticesByDateDesc]);

  useEffect(() => {
    loadIndividualNoticeNotesForStudent();
  }, [loadIndividualNoticeNotesForStudent]);

  const handleOpenIndividualNoticeModal = useCallback(async () => {
    await loadIndividualNoticeNotesForStudent();
    setExpandedIndividualNoticeId(null);
    setIndividualNoticePage(1);
    setIsIndividualNoticeModalOpen(true);
  }, [loadIndividualNoticeNotesForStudent]);

  const handleSetChoiceToggle = useCallback(
    (quizId: string, index: number, isMulti: boolean) => {
      setSetAnswers((prev) => {
        const current = prev[quizId];
        if (isMulti) {
          const arr = Array.isArray(current) ? [...current] : [];
          const exists = arr.includes(index);
          const next = exists ? arr.filter((v) => v !== index) : [...arr, index];
          return { ...prev, [quizId]: next };
        }
        return { ...prev, [quizId]: [index] };
      });
    },
    []
  );

  const handleSetTextChange = useCallback((quizId: string, text: string) => {
    setSetAnswers((prev) => ({ ...prev, [quizId]: text }));
  }, []);

  // 클래스 변경 시 접속 시도 플래그 리셋
  useEffect(() => {
    connectAttemptedRef.current = false;
    initialConnectRef.current = false;
    autoReconnectRef.current = false;
    setIsConnected(false);
  }, [resultData?.classInfo?.classCode]);

  const handleSubmitSetAnswers = useCallback(async () => {
    if (!isSetMode || !activeQuizSetId) return;
    if (!resultData?.classInfo?.classCode || !effectiveStudentId) return;
    if (isSubmittingSet) return;

    // 선택형/단답형의 경우 정답이 입력되어야 제출 가능
    const unanswered = setQuizzes.filter((q) => {
      const ans = setAnswers[q.id];
      if (q.type === 'choice') {
        return !Array.isArray(ans) || ans.length === 0;
      }
      if (q.type === 'short') {
      const text = typeof ans === 'string' ? ans.trim() : '';
      return !text;
      }
      // 서술형은 정답 입력 필수 아님
      return false;
    });

    if (unanswered.length > 0) {
      showToastMessage('선택형과 단답형 문항에 답변을 입력해주세요.');
      return;
    }

    setIsSubmittingSet(true);
    try {
      const classCode = resultData.classInfo.classCode;
      const targetSetId = activeQuizSetId;
      const nextStatuses: Record<string, { submitted: boolean; isCorrect?: boolean | null }> = { ...setQuizStatuses };

      for (const quiz of setQuizzes) {
        const ans = setAnswers[quiz.id];
        let submitted = false;
        let isCorrect: boolean | null = null;
        
        // quiz 객체에서 필요한 필드만 추출 (메모리 최적화 및 모바일 호환성)
        const quizForSubmission: ChatQuiz = {
          id: quiz.id,
          type: quiz.type,
          question: quiz.question,
          choices: quiz.choices,
          correctAnswers: quiz.correctAnswers,
          answers: quiz.answers,
          guide: quiz.guide,
          createdAt: quiz.createdAt,
          updatedAt: quiz.updatedAt,
          order: quiz.order,
          defaultMultiAnswerMode: quiz.defaultMultiAnswerMode,
          defaultPublicMode: quiz.defaultPublicMode,
          responses: {},
          answeredStudents: [],
        };
        
        if (quiz.type === 'choice') {
          const choiceAns = Array.isArray(ans) ? ans : [];
          if (choiceAns.length > 0) {
            await chatQuizService.submitResponseToSet(
              classCode,
              targetSetId,
              quiz.id,
              effectiveStudentId,
              choiceAns,
              quizForSubmission,
              quiz.defaultMultiAnswerMode ?? false,
              isGuestMode ? effectiveName : undefined,
              studentMemberGroup
            );
            const correctArr = (quiz.correctAnswers || []).map((v) => Number(v));
            const selectedArr = choiceAns.map((v) => Number(v));
            isCorrect =
              correctArr.length === selectedArr.length &&
              correctArr.every((v) => selectedArr.includes(v));
            submitted = true;
          } else {
            // 무응답도 제출로 처리하며 오답 표시
            submitted = true;
            isCorrect = false;
          }
        } else {
          const textAns = typeof ans === 'string' ? ans.trim() : '';
          if (textAns) {
            await chatQuizService.submitResponseToSet(
              classCode,
              targetSetId,
              quiz.id,
              effectiveStudentId,
              textAns,
              quizForSubmission,
              false,
              isGuestMode ? effectiveName : undefined,
              studentMemberGroup
            );
            if (quiz.type === 'short') {
              const lowered = textAns.toLowerCase();
              isCorrect = (quiz.answers || []).some((a) => a.toLowerCase().trim() === lowered);
            }
            if (quiz.type === 'essay') {
              isCorrect = null; // 채점 불가, 회색 처리
            }
            submitted = true;
          } else {
            // 무응답도 제출로 처리하며 오답 표시(서술형은 중립)
            submitted = true;
            isCorrect = quiz.type === 'essay' ? null : false;
          }
        }
        nextStatuses[quiz.id] = { submitted, isCorrect };
      }
      setSetQuizStatuses(nextStatuses);
      // 한 번 제출하면 전체 문항 잠금 (재생 재시작 시에만 해제)
      setIsSetSubmitted(true);
      alert('제출되었습니다.');
    } catch (error) {
      console.error('❌ 세트 일괄 제출 실패:', error);
      alert('제출 중 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setIsSubmittingSet(false);
    }
  }, [
    isSetMode,
    activeQuizSetId,
    resultData?.classInfo?.classCode,
    effectiveStudentId,
    isSubmittingSet,
    setQuizzes,
    setAnswers,
    isGuestMode,
    effectiveName,
    studentMemberGroup,
  ]);

  // 재생 종료 시 잠금 해제 (activeQuizId가 내려가면 세트 재시작 상태로 간주)
  useEffect(() => {
    if (!activeQuizId) {
      setIsSetSubmitted(false);
      setSetQuizStatuses((prev) => {
        const next: typeof prev = {};
        Object.keys(prev).forEach((k) => {
          next[k] = { ...prev[k], submitted: false, isCorrect: null };
        });
        return next;
      });
    }
  }, [activeQuizId]);
  
  // 입력창 표시 여부 상태
  const [showInputField, setShowInputField] = useState<boolean>(true);
  // 멀티답변 모드 상태
  const [isMultiAnswerMode, setIsMultiAnswerMode] = useState<boolean>(false);
  // 공개 모드 상태
  const [isPublicMode, setIsPublicMode] = useState<boolean>(false);
  const [isChatOpen, setIsChatOpen] = useState<boolean>(false);
  // connectedStudents에 등록되어 있는지 여부
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const connectAttemptedRef = useRef<boolean>(false);
  const initialConnectRef = useRef<boolean>(false);
  const autoReconnectRef = useRef<boolean>(false);
  // 자신이 답변을 제출했는지 여부
  const [hasSubmittedAnswer, setHasSubmittedAnswer] = useState<boolean>(false);
  // 다른 멤버들의 답변 (공개 모드일 때만 사용)
  const [otherStudentsAnswers, setOtherStudentsAnswers] = useState<Array<{
    id: string;
    studentId: string;
    studentName: string;
    studentProfileImage: string;
    type: 'choice' | 'short' | 'essay';
    answer: number[] | string;
    timestamp: Date;
  }>>([]);
  const otherResponsesQuizIdRef = useRef<string | null>(null);
  // 클래스 멤버 정보 (다른 멤버들의 이름과 프로필 이미지를 위해)
  const [classMembers, setClassMembers] = useState<any[]>([]);
  // 답변 제출 중 상태 (중복 제출 방지)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const hasReachedMultiAnswerLimit = isMultiAnswerMode && submittedAnswers.length >= MAX_MULTI_RESPONSES;
  
  // 퀴즈가 변경되면 제출된 답변 초기화 및 입력창 표시
  useEffect(() => {
    setSubmittedAnswers([]);
    setChoiceAnswer([]);
    setThoughtText('');
    setHasSubmittedAnswer(false);
    setOtherStudentsAnswers([]);
    setIsSubmitting(false);
  }, [activeQuizId]);

  // 활성 퀴즈가 있을 때 자동으로 퀴즈 페이지로 전환 (단, 이미 퀴즈 페이지에 있으면 제외)
  useEffect(() => {
    if (activeQuiz && activeQuizId && selectedView !== 'quiz') {
      setSelectedView('quiz');
    }
  }, [activeQuiz, activeQuizId, selectedView]);
  
  // 과제 완료 코드 관련 상태
  const [completionCode, setCompletionCode] = useState('');
  const [isCodeVerified, setIsCodeVerified] = useState(false);
  
  // 토스트 메시지 상태
  const [showToast, setShowToast] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string>('');
  
  // 토스트 표시 함수
  const showToastMessage = (message: string) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => {
      setShowToast(false);
    }, 1500);
  };
  
  // 보상 토스트 상태
  const [showRewardToast, setShowRewardToast] = useState(false);
  const [rewardToastData, setRewardToastData] = useState<{
    message?: string;
    coins?: number;
    itemCardIds?: string[];
  } | null>(null);
  
  // 프로젝트 보상 알람 관련 상태
  const [currentNotification, setCurrentNotification] = useState<any>(null);
  
  // localStorage에서 이미 표시한 알람 ID 목록 로드 (새로고침 시에도 유지)
  const getShownNotificationIds = (): Set<string> => {
    if (!effectiveStudentId) return new Set();
    const storageKey = `shownNotifications_${effectiveStudentId}`;
    const stored = localStorage.getItem(storageKey);
    return stored ? new Set(JSON.parse(stored)) : new Set();
  };
  
  const [shownNotificationIds, setShownNotificationIds] = useState<Set<string>>(() => getShownNotificationIds());
  
  // 표시한 알람 ID를 localStorage에 저장
  const saveShownNotificationIds = (ids: Set<string>) => {
    if (!effectiveStudentId) return;
    const storageKey = `shownNotifications_${effectiveStudentId}`;
    localStorage.setItem(storageKey, JSON.stringify(Array.from(ids)));
  };
  
  // 코인 히스토리 모달 상태
  const [isCoinHistoryModalOpen, setIsCoinHistoryModalOpen] = useState(false);
  const [coinHistoryPage, setCoinHistoryPage] = useState(1);
  
  // 과제 포인트 내역 모달 상태
  const [isAssignmentHistoryModalOpen, setIsAssignmentHistoryModalOpen] = useState(false);
  const [assignmentData, setAssignmentData] = useState<StudentAssignmentHistory | null>(null);
  const [isLoadingAssignment, setIsLoadingAssignment] = useState(false);
  const [assignmentHistoryPage, setAssignmentHistoryPage] = useState(1);
  const [assignmentFilterType, setAssignmentFilterType] = useState<'all' | 'general' | 'project' | 'routine'>('all');
  
  // 긍정행동 포인트 내역 모달 상태
  const [isPositiveHistoryModalOpen, setIsPositiveHistoryModalOpen] = useState(false);
  const [rewardPoints, setRewardPoints] = useState<PointRecord[]>([]);
  const [isLoadingPositive, setIsLoadingPositive] = useState(false);
  const [positiveHistoryPage, setPositiveHistoryPage] = useState(1);
  
  // 보유 아이템 카드 상태
  const [itemCards, setItemCards] = useState<ItemCard[]>([]);
  const [studentItemCards, setStudentItemCards] = useState<StudentItemCard[]>([]);
  const [isLoadingItemCards, setIsLoadingItemCards] = useState(false);
  const [selectedCardInstanceId, setSelectedCardInstanceId] = useState<string | null>(null);
  const [itemCardImageErrors, setItemCardImageErrors] = useState<Set<string>>(new Set());
  const [showStoreModal, setShowStoreModal] = useState(false);
  const [storeUserType, setStoreUserType] = useState<string>('standard');

  // 사용 완료 아이템 카드 보이기 (기본 OFF, 캐시 유지)
  const SHOW_EXHAUSTED_ITEM_CARDS_KEY = 'member_page_show_exhausted_item_cards';
  const [showExhaustedItemCards, setShowExhaustedItemCards] = useState<boolean>(() => {
    try {
      if (typeof window === 'undefined') return false;
      const v = window.localStorage.getItem(SHOW_EXHAUSTED_ITEM_CARDS_KEY);
      return v === 'true';
    } catch {
      return false;
    }
  });
  const toggleShowExhaustedItemCards = useCallback(() => {
    setShowExhaustedItemCards((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(SHOW_EXHAUSTED_ITEM_CARDS_KEY, next ? 'true' : 'false');
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);
  
  // 개별 접근 페이지 설정 상태
  const [routineSectionEnabled, setRoutineSectionEnabled] = useState<boolean>(true);
  const [storeIndividualPurchaseEnabled, setStoreIndividualPurchaseEnabled] = useState<boolean>(true);
  const [showStoreDisabledInfoModal, setShowStoreDisabledInfoModal] = useState(false);
  
  // 멤버 정보 상태 (과제 제출 후 업데이트를 위해)
  const [studentInfo, setStudentInfo] = useState(resultData?.student);

  useEffect(() => {
    setStudentInfo(resultData?.student);
  }, [resultData?.student]);

  
  // 프로필 이미지 ID 계산 (studentInfo 또는 resultData에서 가져오기)
  const effectiveProfileImageId = isGuestMode
    ? 'basic-0'
    : ((studentInfo || resultData?.student)?.profileImage || (studentInfo || resultData?.student)?.basicInfo?.profileImage || 'basic-0');

  const menuNameLine = isGuestMode
    ? `${effectiveNumberLabel} ${effectiveName}`.trim()
    : `${effectiveNumberLabel}번 ${effectiveName}`.trim();

  const menuClassLine = useMemo(() => {
    const className = resultData?.classInfo?.className?.trim() || '';
    if (isGuestMode) {
      const guestLabel =
        resultData?.guestNumber != null && String(resultData.guestNumber).trim() !== ''
          ? `게스트 ${resultData.guestNumber}`
          : '게스트';
      return className ? `${className} / ${guestLabel}` : guestLabel;
    }
    const groupKey = String(studentMemberGroup || resultData?.classInfo?.memberGroup || 'A')
      .trim()
      .toUpperCase();
    const groupLabel = memberGroupNames[groupKey]?.trim() || `${groupKey}그룹`;
    return className ? `${className} / ${groupLabel}` : groupLabel;
  }, [
    isGuestMode,
    memberGroupNames,
    resultData?.classInfo?.className,
    resultData?.classInfo?.memberGroup,
    resultData?.guestNumber,
    studentMemberGroup
  ]);
  
  // 프로필 이미지 선택 모달 상태
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);

  const handleBack = () => {
    navigate('/');
  };


  // 멤버 카드 클릭 핸들러
  const handleStudentCardClick = () => {
    setIsPointsExpanded(!isPointsExpanded);
  };
  
  const sessionLevelUpPoints = resultData?.classInfo?.levelUpPoints;

  useEffect(() => {
    const fromSession = normalizeLevelUpPoints(sessionLevelUpPoints, 0);
    if (fromSession > 0) {
      setLevelUpPoints(fromSession);
    }
  }, [sessionLevelUpPoints]);

  const loadClassLevelUpPoints = useCallback(
    async (force?: boolean): Promise<number> => {
      const sessionFallback = normalizeLevelUpPoints(resultData?.classInfo?.levelUpPoints, 50);
      if (!memberPageScope) return sessionFallback;
      try {
        const meta = await studentMemberPageData.loadClassMeta(memberPageScope, {
          force,
          sessionLevelUpPoints: resultData?.classInfo?.levelUpPoints,
          sessionMemberGroupNames: resultData?.classInfo?.memberGroupNames
        });
        setLevelUpPoints(meta.levelUpPoints);
        if (Object.keys(meta.memberGroupNames).length > 0) {
          setMemberGroupNames(meta.memberGroupNames);
        }
        return meta.levelUpPoints;
      } catch (error) {
        console.error('레벨업 포인트 설정 로드 실패:', error);
        return sessionFallback;
      }
    },
    [
      memberPageScope,
      resultData?.classInfo?.levelUpPoints,
      resultData?.classInfo?.memberGroupNames
    ]
  );

  useEffect(() => {
    if (!memberPageScope) return;
    void loadClassLevelUpPoints();
  }, [loadClassLevelUpPoints, memberPageScope]);

  const profilePointStudent = studentInfo || resultData?.student;

  const displayTotalPoints = useMemo(() => {
    if (!profilePointStudent || isGuestMode) return 0;
    if (profileTableTotalPoints != null && Number.isFinite(profileTableTotalPoints)) {
      return profileTableTotalPoints;
    }
    return (profilePointStudent.assignmentPoints ?? 0) + (profilePointStudent.positivePoints ?? 0);
  }, [profilePointStudent, profileTableTotalPoints, isGuestMode]);

  const profileLevelInfo = useMemo(
    () => getLevelDisplayInfo(displayTotalPoints, levelUpPoints),
    [displayTotalPoints, levelUpPoints]
  );
  
  // 코인 히스토리에서 총 코인 수 계산
  const calculateTotalCoins = (coinsHistory?: any[]): number => {
    if (!coinsHistory || coinsHistory.length === 0) return 0;
    return coinsHistory.reduce((total, history) => total + (history.earnedCoin || 0), 0);
  };

  const currentStoreCoins = useMemo(() => {
    const history = (studentInfo || resultData?.student)?.basicInfo?.coinsHistory || [];
    return calculateTotalCoins(history);
  }, [studentInfo, resultData]);

  useEffect(() => {
    if (!memberPageScope) return;
    void studentMemberPageData.loadDefaultSetAccess(memberPageScope).then((settings) => {
      setRoutineSectionEnabled(settings.routineSectionEnabled);
      setStoreIndividualPurchaseEnabled(settings.storeIndividualPurchaseEnabled);
    });
  }, [memberPageScope]);

  const handleOpenStoreModal = useCallback(async () => {
    // 스토어 개별 구매가 비활성화된 경우 안내 모달 표시
    if (!storeIndividualPurchaseEnabled) {
      setShowStoreDisabledInfoModal(true);
      return;
    }
    
    setShowStoreModal(true);
    if (!resultData?.classInfo?.classCode) return;
    try {
      const codiInfo = await classCodeService.getCodiInfoByClassCode(resultData.classInfo.classCode);
      if (codiInfo?.userType) {
        setStoreUserType(codiInfo.userType);
      }
    } catch (error) {
      console.error('코디 이용권 정보 조회 실패:', error);
    }
  }, [resultData?.classInfo?.classCode, storeIndividualPurchaseEnabled]);
  
  // 코인 히스토리 가져오기 (최근 데이터가 상위로 오도록 정렬)
  const getCoinHistory = (): any[] => {
    const history = (studentInfo || resultData?.student)?.basicInfo?.coinsHistory || [];
    if (!history || history.length === 0) return [];
    
    // earnedAt 기준으로 내림차순 정렬 (최근 데이터가 먼저)
    return [...history].sort((a, b) => {
      const getDateValue = (date: any): number => {
        if (!date) return 0;
        
        // Date 객체인 경우
        if (date instanceof Date) {
          return date.getTime();
        }
        
        // Firestore Timestamp 객체인 경우 (toDate 메서드가 있는 경우)
        if (date && typeof date === 'object' && 'toDate' in date && typeof date.toDate === 'function') {
          try {
            return (date as any).toDate().getTime();
          } catch {
            return 0;
          }
        }
        
        // 직렬화된 Firestore Timestamp ({seconds, nanoseconds} 형태)
        if (date && typeof date === 'object' && 'seconds' in date && 'nanoseconds' in date) {
          try {
            const timestamp = typeof date.seconds === 'number' 
              ? date.seconds * 1000 + (date.nanoseconds || 0) / 1000000
              : date.seconds;
            return new Date(timestamp).getTime();
          } catch {
            return 0;
          }
        }
        
        // 문자열 또는 숫자인 경우
        try {
          const parsed = new Date(date);
          return isNaN(parsed.getTime()) ? 0 : parsed.getTime();
        } catch {
          return 0;
        }
      };
      
      const dateA = getDateValue(a.earnedAt);
      const dateB = getDateValue(b.earnedAt);
      
      // 내림차순 정렬 (최신이 먼저) - dateB가 더 크면 음수 반환하여 b를 앞에 배치
      return dateB - dateA;
    });
  };
  
  // 날짜 포맷팅 (MM/DD) - StudentPointDetailModal과 동일한 방식
  const formatDateToMMDD = (dateValue: any): string => {
    try {
      let date: Date;
      
      // Firestore Timestamp 객체인지 확인 (toDate 메서드가 있는 경우)
      if (dateValue && typeof dateValue === 'object' && 'toDate' in dateValue && typeof dateValue.toDate === 'function') {
        date = dateValue.toDate();
      } 
      // 직렬화된 Firestore Timestamp ({seconds, nanoseconds} 형태)
      else if (dateValue && typeof dateValue === 'object' && 'seconds' in dateValue && 'nanoseconds' in dateValue) {
        // seconds가 밀리초인 경우와 초인 경우 모두 처리
        const timestamp = typeof dateValue.seconds === 'number' 
          ? dateValue.seconds * 1000 + (dateValue.nanoseconds || 0) / 1000000
          : dateValue.seconds;
        date = new Date(timestamp);
      }
      // Date 객체인 경우
      else if (dateValue instanceof Date) {
        date = dateValue;
      } 
      // 문자열 또는 숫자인 경우
      else if (typeof dateValue === 'string' || typeof dateValue === 'number') {
        date = new Date(dateValue);
      } 
      else {
        return '-';
      }
      
      if (isNaN(date.getTime())) {
        return '-';
      }
      
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      return `${month}/${day}`;
    } catch (error) {
      return '-';
    }
  };

  // 과제 포인트 내역 로드
  const loadAssignmentHistory = useCallback(async (force?: boolean) => {
    if (!memberPageScope) return;
    
    setIsLoadingAssignment(true);
    try {
      const targetStudent = await studentMemberPageData.loadAssignmentHistory(memberPageScope, { force });
      setAssignmentData(targetStudent);
    } catch (error) {
      console.error('과제 포인트 내역 로드 실패:', error);
    } finally {
      setIsLoadingAssignment(false);
    }
  }, [memberPageScope]);

  // 긍정행동 포인트 내역 로드
  const loadPositiveHistory = useCallback(async (force?: boolean) => {
    if (!memberPageScope) return;
    
    setIsLoadingPositive(true);
    try {
      const positivePoints = await studentMemberPageData.loadPositiveHistory(memberPageScope, { force });
      setRewardPoints(positivePoints);
    } catch (error) {
      console.error('긍정행동 포인트 내역 로드 실패:', error);
    } finally {
      setIsLoadingPositive(false);
    }
  }, [memberPageScope]);

  // 모달 열릴 때 데이터 로드 및 페이지 리셋
  useEffect(() => {
    if (isAssignmentHistoryModalOpen) {
      void loadAssignmentHistory();
      setAssignmentHistoryPage(1);
      setAssignmentFilterType('all');
    }
  }, [isAssignmentHistoryModalOpen, loadAssignmentHistory]);

  useEffect(() => {
    if (isPositiveHistoryModalOpen) {
      void loadPositiveHistory();
      setPositiveHistoryPage(1);
    }
  }, [isPositiveHistoryModalOpen, loadPositiveHistory]);

  useEffect(() => {
    if (isCoinHistoryModalOpen) {
      setCoinHistoryPage(1);
    }
  }, [isCoinHistoryModalOpen]);

  // 데이터가 없으면 메인 페이지로 리다이렉트 (멤버 요약 지연 로드 중에는 classInfo+member만 있고 student 없음)
  useEffect(() => {
    if (!resultData || !resultData.classInfo) {
      navigate('/');
      return;
    }
    if (resultData.isGuestMode) return;
    if (resultData.student) return;
    if (resultData.pendingStudentSummary && resultData.member) return;
    navigate('/');
  }, [resultData, navigate]);

  const reloadStudentInfo = useCallback(
    async (force?: boolean) => {
      if (!memberPageScope || !resultData?.student || isGuestMode) return;

      try {
        const cached = await studentMemberPageData.loadStudentProfile(memberPageScope, {
          force,
          sessionLevelUpPoints: resultData?.classInfo?.levelUpPoints
        });
        setLevelUpPoints(cached.levelUpPoints);
        setProfileTableTotalPoints(cached.profileTableTotalPoints);
        setStudentInfo(cached.summary);
        if (Object.keys(resultData?.classInfo?.memberGroupNames || {}).length === 0) {
          const meta = await studentMemberPageData.loadClassMeta(memberPageScope, {
            sessionLevelUpPoints: cached.levelUpPoints,
            sessionMemberGroupNames: resultData?.classInfo?.memberGroupNames
          });
          if (Object.keys(meta.memberGroupNames).length > 0) {
            setMemberGroupNames(meta.memberGroupNames);
          }
      }
    } catch (error) {
      console.error('❌ 멤버 정보 다시 로드 실패:', error);
    }
    },
    [memberPageScope, resultData?.student, isGuestMode, resultData?.classInfo?.levelUpPoints, resultData?.classInfo?.memberGroupNames]
  );

  useEffect(() => {
    if (!memberPageScope || isGuestMode) return;
    const cachedProfile = studentMemberPageCache.get<CachedStudentProfile>(
      memberPageScope,
      'studentProfile'
    );
    if (cachedProfile) {
      setStudentInfo(cachedProfile.summary);
      setProfileTableTotalPoints(cachedProfile.profileTableTotalPoints);
      setLevelUpPoints(cachedProfile.levelUpPoints);
    }
    const cachedPending = studentMemberPageCache.get<unknown[]>(
      memberPageScope,
      'pendingAssignments'
    );
    if (cachedPending) setAssignments(cachedPending);
    const cachedRoutine = studentMemberPageCache.get<CachedRoutineToday>(
      memberPageScope,
      'routineToday'
    );
    if (cachedRoutine) {
      setTodayRoutineAssignments(cachedRoutine.list);
      setInitialRoutineStatuses(new Map(Object.entries(cachedRoutine.initialStatuses)));
    }
  }, [memberPageScope, isGuestMode]);

  useEffect(() => {
    if (resultData?.classInfo && resultData?.student && !isGuestMode) {
      reloadStudentInfo();
    } else {
      setProfileTableTotalPoints(null);
    }
  }, [resultData?.classInfo?.userId, resultData?.classInfo?.classId, resultData?.student?.basicInfo?.id, isGuestMode, reloadStudentInfo]);

  // 프로필 이미지 변경 확인 핸들러
  const handleProfileImageConfirm = useCallback(async (imageId: string) => {
    if (!resultData?.classInfo || !resultData?.student || isGuestMode) return;

    try {
      console.log('🖼️ 프로필 이미지 업데이트:', {
        userId: resultData.classInfo.userId,
        classId: resultData.classInfo.classId,
        studentId: resultData.student.basicInfo.id,
        imageId
      });

      await userDataService.updateMemberProfileImage(
        resultData.classInfo.userId,
        resultData.classInfo.classId,
        resultData.student.basicInfo.id,
        imageId
      );

      if (memberPageScope) {
        userDataService.invalidateMembersCache(
          memberPageScope.userId,
          memberPageScope.classId,
          memberPageScope.memberGroup
        );
        invalidateMemberPageAfterProfileChange(memberPageScope);
      }
      await reloadStudentInfo(true);

      console.log('✅ 프로필 이미지 업데이트 완료');
    } catch (error: any) {
      console.error('❌ 프로필 이미지 업데이트 실패:', error);
      alert('프로필 이미지 업데이트에 실패했습니다: ' + (error?.message || '알 수 없는 오류'));
    }
  }, [resultData, isGuestMode, reloadStudentInfo, memberPageScope]);

  const loadPendingAssignments = useCallback(async (force?: boolean) => {
    if (!memberPageScope) return;
    
    setLoading(true);
    try {
      const pendingAssignments = await studentMemberPageData.loadPendingAssignments(memberPageScope, {
        force
      });
      setAssignments(pendingAssignments);
    } catch (error) {
      console.error('과제 로드 실패:', error);
    } finally {
      setLoading(false);
    }
  }, [memberPageScope]);

  useEffect(() => {
    loadPendingAssignments();
  }, [loadPendingAssignments]);

  const loadTodayRoutineAssignments = useCallback(
    async (force?: boolean) => {
      if (!memberPageScope || isGuestMode) return;
    setLoadingTodayRoutine(true);
    try {
        const cached = await studentMemberPageData.loadRoutineToday(memberPageScope, { force });
        setTodayRoutineAssignments(cached.list);
        setInitialRoutineStatuses(new Map(Object.entries(cached.initialStatuses)));
        if (force) {
          setPendingRoutineChanges(new Map());
        }
    } catch (e) {
      console.error('오늘 루틴 과제 로드 실패:', e);
      setTodayRoutineAssignments([]);
      setInitialRoutineStatuses(new Map());
      setPendingRoutineChanges(new Map());
    } finally {
      setLoadingTodayRoutine(false);
    }
    },
    [memberPageScope, isGuestMode]
  );

  useEffect(() => {
    loadTodayRoutineAssignments();
  }, [loadTodayRoutineAssignments]);

  const handleRoutineBlockToggle = useCallback((r: { id: string; routineAssignmentId: string; dateId: string; title: string; status: string; basePoints: number; completionRate?: number; confirmedCount?: number; totalDates?: number; todayCount?: number }) => {
    if (!resultData?.classInfo || !resultData?.student || routineTogglingId) return;
    setRoutineTogglingId(r.id);
    
    // 로컬 상태만 변경 (서버 저장은 하지 않음)
    const currentStatus = r.status;
    const newStatus = currentStatus === 'confirmed' ? 'pending' : 'confirmed';
    const initialStatus = initialRoutineStatuses.get(r.id) || currentStatus;
    
    // 변경사항 추적 (초기 상태와 비교)
    setPendingRoutineChanges((prev) => {
      const newMap = new Map(prev);
      const key = r.id;
      
      // 새 상태가 초기 상태와 같으면 변경사항에서 제거
      if (newStatus === initialStatus) {
        newMap.delete(key);
      } else {
        // 초기 상태와 다르면 변경사항에 추가
        newMap.set(key, {
          routineId: r.routineAssignmentId,
          dateId: r.dateId,
          newStatus
        });
      }
      return newMap;
    });
    
    // 로컬 상태 업데이트
    setTodayRoutineAssignments((prev) => {
      return prev.map((x) => {
        if (x.id !== r.id) return x;
        
        // 도달률 로컬 계산
        const totalDates = x.totalDates ?? 0;
        let newConfirmedCount = x.confirmedCount ?? 0;
        
        if (newStatus === 'confirmed' && currentStatus === 'pending') {
          newConfirmedCount += 1;
        } else if (newStatus === 'pending' && currentStatus === 'confirmed') {
          newConfirmedCount = Math.max(0, newConfirmedCount - 1);
        }
        
        const newCompletionRate = totalDates > 0 ? Number(((newConfirmedCount / totalDates) * 100).toFixed(2)) : 0;
        
        return {
          ...x,
          status: newStatus,
          confirmedCount: newConfirmedCount,
          completionRate: newCompletionRate
        };
      });
    });
    
    setRoutineTogglingId(null);
  }, [resultData?.classInfo, resultData?.student, routineTogglingId, initialRoutineStatuses]);

  // 루틴 과제 변경사항 일괄 저장
  const handleSaveRoutineChanges = useCallback(async () => {
    if (!resultData?.classInfo || !resultData?.student || pendingRoutineChanges.size === 0) return;
    
    setIsSavingRoutines(true);
    try {
      const { RoutineAssignmentConsolidatedService } = await import('../services/routineAssignmentConsolidatedService');
      const { routineAssignmentService } = await import('../services/routineAssignmentService');
      
      const routines = await routineAssignmentService.loadRoutineAssignments(
        resultData.classInfo.userId,
        resultData.classInfo.classId,
        studentMemberGroup
      );
      
      const consolidatedService = new RoutineAssignmentConsolidatedService(
        resultData.classInfo.userId,
        resultData.classInfo.classId,
        studentMemberGroup
      );
      
      // 모든 변경사항을 서버에 저장
      const changesArray = Array.from(pendingRoutineChanges.entries());
      for (const [assignmentId, change] of changesArray) {
        const routine = routines.find(rt => rt.id === change.routineId);
        if (!routine) continue;
        
        const points = change.newStatus === 'confirmed' ? (routine.basePoints ?? 10) : 0;
        await consolidatedService.updateStudentStatus(
          change.routineId,
          change.dateId,
          resultData.student.basicInfo.id,
          change.newStatus as 'pending' | 'confirmed',
          points,
          ''
        );
      }
      
      setPendingRoutineChanges(new Map());
      
      if (memberPageScope) {
        userDataService.invalidateMembersCache(
          memberPageScope.userId,
          memberPageScope.classId,
          memberPageScope.memberGroup
        );
        invalidateMemberPageAfterRoutineChange(memberPageScope);
      }
      await loadTodayRoutineAssignments(true);
      await reloadStudentInfo(true);
    } catch (e) {
      console.error('루틴 과제 저장 실패:', e);
      alert('저장 중 오류가 발생했습니다.');
    } finally {
      setIsSavingRoutines(false);
    }
  }, [
    resultData?.classInfo,
    resultData?.student,
    studentMemberGroup,
    pendingRoutineChanges,
    loadTodayRoutineAssignments,
    memberPageScope,
    reloadStudentInfo
  ]);

  // selectedView 변경 감지
  useEffect(() => {
    console.log('🔍 selectedView 변경됨:', selectedView);
  }, [selectedView]);

  // 활성 퀴즈 리스너 (채팅 아이콘 클릭 시에만 실행)
  useEffect(() => {
    console.log('🔄 listenToActiveQuiz useEffect 실행 - 조건 체크:', {
      hasClassCode: !!resultData?.classInfo?.classCode,
      hasStudentId: !!effectiveStudentId,
      selectedView: selectedView,
      condition: selectedView === 'quiz'
    });

    if (!resultData?.classInfo?.classCode || !effectiveStudentId || selectedView !== 'quiz') {
      console.log('⏸️ listenToActiveQuiz 조건 불충족, 실행 취소');
      return;
    }

    console.log('✅ listenToActiveQuiz 조건 충족, 실행 시작');

    const unsubscribe = chatQuizService.listenToActiveQuiz(
      resultData.classInfo.classCode,
      (quiz, activeId, isMultiAnswerModeFromServer, isPublicModeFromServer, metaInfo) => {
        console.log('📢 퀴즈 업데이트:', {
          quiz: quiz ? '있음' : '없음',
          activeId,
          isMultiAnswerMode: isMultiAnswerModeFromServer,
          isChatOpen: metaInfo?.isChatOpen,
          selectedView: selectedView,
          isPublicMode: isPublicModeFromServer,
        });

        setActiveQuiz(quiz);
        setActiveQuizId(activeId || null);
        setActiveQuizSetId(metaInfo?.activeQuizSetId ?? null);
        setActiveQuizContainerId((metaInfo as any)?.activeQuizContainerId ?? metaInfo?.activeQuizSetId ?? null);
        setStudentCreateMode(metaInfo?.studentCreateMode ?? false);
        setStudentCreateSetId(metaInfo?.studentCreateSetId ?? null);
        setIsMultiAnswerMode((prev) => {
          const newValue = isMultiAnswerModeFromServer || false;
          return prev !== newValue ? newValue : prev;
        });
        setIsPublicMode((prev) => {
          const newValue = isPublicModeFromServer || false;
          return prev !== newValue ? newValue : prev;
        });
        setIsChatOpen((prev) => {
          const newValue = metaInfo?.isChatOpen ?? false;
          return prev !== newValue ? newValue : prev;
        });
        
        // connectedStudents에 현재 멤버가 등록되어 있는지 확인
        const connectedStudents = metaInfo?.connectedStudents || {};
        const studentConnected = effectiveStudentId ? connectedStudents[effectiveStudentId] !== undefined : false;
        setIsConnected(studentConnected);
        console.log('🔍 접속 상태 확인:', {
          studentId: effectiveStudentId,
          isConnected: studentConnected,
          connectedStudentsCount: Object.keys(connectedStudents).length,
        });
        // 메타에 접속 정보가 없고, 챗이 열려 있으면 자동 접속 시도 (한 번만)
        if (metaInfo?.isChatOpen && !studentConnected && !connectAttemptedRef.current && resultData?.classInfo?.classCode) {
          connectAttemptedRef.current = true;
          const classCode = resultData.classInfo.classCode;
          const studentInfo: ConnectedStudentInfo = {
            name: effectiveName,
            number: isGuestMode ? resultData?.guestNumber?.toString() : resultData?.student?.basicInfo?.number,
            isGuest: isGuestMode,
          };
          void chatQuizService
            .addConnectedStudentToChat(classCode, effectiveStudentId, studentInfo, studentMemberGroup)
            .catch((error) => {
            console.error('❌ 자동 접속 기록 실패:', error);
            // 실패 시 다시 시도할 수 있도록 플래그를 낮춘다
            connectAttemptedRef.current = false;
          });
        }
      },
      studentMemberGroup
    );

    return () => {
      console.log('🔌 퀴즈 리스너 해제');
      unsubscribe();
    };
  }, [resultData?.classInfo?.classCode, effectiveStudentId, studentMemberGroup, selectedView]);

  // 질문 입력 자동 높이 조절
  useEffect(() => {
    const el = studentQuestionInputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.max(el.scrollHeight, 40)}px`;
  }, [studentQuestionText]);

  // 세트가 활성화되면 전체 문항 로드 (재생 시작마다 오답/미응답을 재응답 가능하도록 submitted 리셋)
  useEffect(() => {
    const loadSet = async () => {
      const targetSetId = activeQuizSetId || studentCreateSetId;
      if ((!isSetMode && !studentCreateMode) || !targetSetId || !resultData?.classInfo?.classCode) {
        setSetQuizzes([]);
        setSetAnswers({});
        setIsSetSubmitted(false);
        setSetQuizStatuses({});
        setSetTitle('');
        return;
      }
      try {
        const setDoc = await chatQuizService.getQuizSetById(resultData.classInfo.classCode, targetSetId);
        if (!setDoc || !setDoc.quizzes) {
          setSetQuizzes([]);
          setSetAnswers({});
          setIsSetSubmitted(false);
          setSetQuizStatuses({});
          setSetTitle('');
          return;
        }
        const quizzes = Object.entries(setDoc.quizzes || {}).map(([id, q]: any) => ({
          ...(q as ChatQuiz),
          id,
        }));
        quizzes.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        setSetQuizzes(quizzes);
        setSetTitle(setDoc.title || '');
        const nextAnswers: Record<string, number[] | string> = {};
        const nextStatuses: Record<string, { submitted: boolean; isCorrect?: boolean | null }> = {};
        const group = (studentMemberGroup || 'A').toString().trim().toUpperCase();
        quizzes.forEach((q) => {
          const groupResponses = (q as any).responsesByGroup?.[group] ?? (group === 'A' ? (q as any).responses : undefined) ?? {};
          const resp = groupResponses[effectiveStudentId];
          const arr = Array.isArray(resp) ? resp : resp ? [resp] : [];
          const last = arr.length > 0 ? arr[arr.length - 1] : null;
          if (last) {
            if (q.type === 'choice') {
              nextAnswers[q.id] = Array.isArray(last.answer) ? (last.answer as number[]) : [];
            } else {
              nextAnswers[q.id] = typeof last.answer === 'string' ? last.answer : '';
            }

            const hasContent =
              q.type === 'choice'
                ? Array.isArray(last.answer) && last.answer.length > 0
                : typeof last.answer === 'string' && last.answer.trim().length > 0;
            // 정답인 경우 또는 서술형/단답형에서 응답이 존재하는 경우 비활성화
            const submittedFlag =
              last.isCorrect === true || (q.type === 'essay' && hasContent);

            nextStatuses[q.id] = { submitted: submittedFlag, isCorrect: last.isCorrect ?? null };
          } else {
            nextAnswers[q.id] = q.type === 'choice' ? [] : '';
            nextStatuses[q.id] = { submitted: false, isCorrect: null };
          }
        });
        setSetAnswers(nextAnswers);
        setSetQuizStatuses(nextStatuses);
        setIsSetSubmitted(false); // 재생 시작 시 잠금 해제
      } catch (err) {
        console.error('❌ 세트 문항 로드 실패:', err);
        setSetQuizzes([]);
        setIsSetSubmitted(false);
        setSetQuizStatuses({});
        setSetTitle('');
      }
    };
    void loadSet();
  }, [isSetMode, studentCreateMode, activeQuizSetId, studentCreateSetId, activeQuizId, resultData?.classInfo?.classCode, effectiveStudentId, studentMemberGroup]);

  // 페이지 진입 시 1회 접속 기록 (isChatOpen 여부와 무관)
  useEffect(() => {
    const tryInitialConnect = async () => {
      if (initialConnectRef.current) return;
      if (!resultData?.classInfo?.classCode || !effectiveStudentId) return;

      initialConnectRef.current = true;
      const classCode = resultData.classInfo.classCode;
      const studentInfo: ConnectedStudentInfo = {
        name: effectiveName,
        number: isGuestMode ? resultData?.guestNumber?.toString() : resultData?.student?.basicInfo?.number,
        isGuest: isGuestMode,
      };

      // 초기 접속 시에는 별도 처리하지 않음 (퀴즈 페이지 진입 시 처리)
    };

    void tryInitialConnect();
  }, [
    resultData?.classInfo?.classCode,
    effectiveStudentId,
    effectiveName,
    isGuestMode,
    resultData?.guestNumber,
    resultData?.student?.basicInfo?.number,
  ]);

  // 퀴즈 페이지 진입 시 메타 문서 생성 (코디의 클래스챗 시작과 유사하게)
  useEffect(() => {
    const initializeQuizPage = async () => {
      if (selectedView !== 'quiz') return;
      if (!resultData?.classInfo?.classCode) return;

      try {
        console.log('🎯 퀴즈 페이지 진입: 메타 문서 생성 시작');
        await chatQuizService.ensureMetaExists(resultData.classInfo.classCode);
        console.log('✅ 퀴즈 페이지 진입: 메타 문서 생성 완료');
      } catch (error) {
        console.error('❌ 퀴즈 페이지 진입: 메타 문서 생성 실패:', error);
      }
    };

    void initializeQuizPage();
  }, [selectedView, resultData?.classInfo?.classCode]);

  // 퀴즈 페이지 진입 시 학생 접속 정보 기록 (meta 문서가 존재할 때만)
  const quizConnectRef = useRef<boolean>(false);
  useEffect(() => {
    const recordStudentConnection = async () => {
      if (selectedView !== 'quiz') return;
      if (quizConnectRef.current) return; // 이미 기록했으면 스킵
      if (!resultData?.classInfo?.classCode || !effectiveStudentId) return;

      quizConnectRef.current = true;
      const classCode = resultData.classInfo.classCode;
      const studentInfo: ConnectedStudentInfo = {
        name: effectiveName,
        number: isGuestMode ? resultData?.guestNumber?.toString() : resultData?.student?.basicInfo?.number,
        isGuest: isGuestMode,
      };

      try {
        console.log('🎯 퀴즈 페이지 진입: 학생 접속 정보 기록 시작');
        await chatQuizService.addConnectedStudentToChat(classCode, effectiveStudentId, studentInfo, studentMemberGroup);
        console.log('✅ 퀴즈 페이지 진입: 학생 접속 정보 기록 완료');
      } catch (error) {
        console.error('❌ 퀴즈 페이지 진입: 학생 접속 정보 기록 실패:', error);
        // 실패 시 재시도 가능하도록 플래그 초기화
        quizConnectRef.current = false;
      }
    };

    void recordStudentConnection();
  }, [selectedView, resultData?.classInfo?.classCode, effectiveStudentId, effectiveName, isGuestMode, resultData?.guestNumber, resultData?.student?.basicInfo?.number, studentMemberGroup]);

  // isChatOpen 상태에서 아직 연결되지 않은 경우 자동 재시도 (퀴즈 페이지에서만)
  useEffect(() => {
    const tryAutoReconnect = async () => {
      if (selectedView !== 'quiz') return; // 퀴즈 페이지에서만 동작
      if (!isChatOpen || isConnected) return;
      if (autoReconnectRef.current) return;
      if (!resultData?.classInfo?.classCode || !effectiveStudentId) return;

      autoReconnectRef.current = true;
      const classCode = resultData.classInfo.classCode;
      const studentInfo: ConnectedStudentInfo = {
        name: effectiveName,
        number: isGuestMode ? resultData?.guestNumber?.toString() : resultData?.student?.basicInfo?.number,
        isGuest: isGuestMode,
      };

      try {
        await chatQuizService.addConnectedStudentToChat(classCode, effectiveStudentId, studentInfo, studentMemberGroup);
      } catch (error) {
        console.error('❌ 자동 재접속 실패:', error);
        // 실패 시 다시 시도 가능하도록 플래그 해제
        autoReconnectRef.current = false;
      }
    };

    void tryAutoReconnect();
  }, [
    isChatOpen,
    isConnected,
    resultData?.classInfo?.classCode,
    effectiveStudentId,
    effectiveName,
    isGuestMode,
    resultData?.guestNumber,
    resultData?.student?.basicInfo?.number,
  ]);

  // 클래스 멤버 정보 로드 (다른 멤버들의 이름과 프로필 이미지를 위해)
  useEffect(() => {
    const loadMembers = async () => {
      if (!resultData?.classInfo?.userId || !resultData?.classInfo?.classId) return;
      
      try {
        const members = await userDataService.loadClassMembers(
          resultData.classInfo.userId,
          resultData.classInfo.classId,
          studentMemberGroup
        );
        setClassMembers(members);
      } catch (error) {
        console.error('클래스 멤버 로드 실패:', error);
      }
    };
    
    loadMembers();
  }, [resultData?.classInfo?.userId, resultData?.classInfo?.classId, studentMemberGroup]);

  const buildOwnAnswers = useCallback((studentId: string, responses: { [studentId: string]: StudentQuizResponse | StudentQuizResponse[] }) => {
    const response = responses?.[studentId];
    if (!response) return [] as typeof submittedAnswers;

    const arrayResponse = Array.isArray(response) ? response : [response];

    return arrayResponse
      .filter((resp) => resp?.submittedAt)
      .map((resp) => {
        let timestamp: Date;
        if (resp.submittedAt && typeof resp.submittedAt === 'object' && 'toDate' in resp.submittedAt && typeof resp.submittedAt.toDate === 'function') {
          timestamp = resp.submittedAt.toDate();
        } else if (resp.submittedAt && typeof resp.submittedAt === 'object' && 'seconds' in resp.submittedAt) {
          const seconds = (resp.submittedAt as any).seconds;
          const nanoseconds = (resp.submittedAt as any).nanoseconds || 0;
          timestamp = new Date(seconds * 1000 + nanoseconds / 1000000);
        } else {
          const submittedAtAny = resp.submittedAt as any;
          timestamp = submittedAtAny instanceof Date ? submittedAtAny : new Date(submittedAtAny);
        }

        return {
          id: `own-${timestamp.getTime()}`,
          type: activeQuiz?.type || 'short',
          answer: resp.answer,
          timestamp,
          isCorrect: resp.isCorrect,
          hasContent: Array.isArray(resp.answer)
            ? resp.answer.length > 0
            : (resp.answer ?? '').toString().trim().length > 0,
        };
      })
      .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
  }, [activeQuiz?.type]);

  const applyOwnResponses = useCallback((studentId: string, responses: { [studentId: string]: StudentQuizResponse | StudentQuizResponse[] }) => {
    const answers = buildOwnAnswers(studentId, responses || {});
    if (answers.length > 0) {
      setSubmittedAnswers(answers.map((answer) => ({
        id: answer.id,
        type: answer.type,
        answer: answer.answer,
        timestamp: answer.timestamp,
        isCorrect: answer.isCorrect,
        hasContent: answer.hasContent,
      })));
      setHasSubmittedAnswer(true);
    } else {
      setSubmittedAnswers([]);
      setHasSubmittedAnswer(false);
    }
  }, [buildOwnAnswers]);

  const buildOtherAnswers = useCallback((responses: { [studentId: string]: StudentQuizResponse | StudentQuizResponse[] }) => {
    if (!effectiveStudentId) return [] as typeof otherStudentsAnswers;

    const currentStudentId = effectiveStudentId;
    const answers: typeof otherStudentsAnswers = [];

    Object.keys(responses || {}).forEach((responseStudentId) => {
      if (responseStudentId === currentStudentId) return;

      const response = responses[responseStudentId];
      const responseArray = Array.isArray(response) ? response : [response];

      const member = classMembers.find((m) => m.id === responseStudentId || m.basicInfo?.id === responseStudentId);
      const studentName = member?.name || member?.basicInfo?.name || '멤버';
      const studentProfileImage = member?.profileImage || member?.basicInfo?.profileImage || 'basic-0';

      responseArray.forEach((resp, index) => {
        if (!resp?.submittedAt) return;

        let timestamp: Date;

        if (resp.submittedAt && typeof resp.submittedAt === 'object' && 'toDate' in resp.submittedAt && typeof resp.submittedAt.toDate === 'function') {
          timestamp = resp.submittedAt.toDate();
        } else if (resp.submittedAt && typeof resp.submittedAt === 'object' && 'seconds' in resp.submittedAt) {
          const seconds = (resp.submittedAt as any).seconds;
          const nanoseconds = (resp.submittedAt as any).nanoseconds || 0;
          timestamp = new Date(seconds * 1000 + nanoseconds / 1000000);
        } else {
          const submittedAtAny = resp.submittedAt as any;
          timestamp = submittedAtAny instanceof Date ? submittedAtAny : new Date(submittedAtAny);
        }

        const timestampMillis = timestamp.getTime();

        answers.push({
          id: `${responseStudentId}-${index}-${timestampMillis}`,
          studentId: responseStudentId,
          studentName,
          studentProfileImage,
          type: activeQuiz?.type || 'short',
          answer: resp.answer,
          timestamp,
        });
      });
    });

    return answers.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
  }, [classMembers, activeQuiz?.type, effectiveStudentId]);

  const applyOtherResponses = useCallback((quizId: string, responses: { [studentId: string]: StudentQuizResponse | StudentQuizResponse[] }) => {
    otherResponsesQuizIdRef.current = quizId;
    const answers = buildOtherAnswers(responses || {});
    setOtherStudentsAnswers(answers);
  }, [buildOtherAnswers]);

  const handleSubmissionError = useCallback((error: unknown) => {
    if (error instanceof Error && error.message === 'MAX_MULTI_RESPONSES_REACHED') {
      console.warn('⚠️ 멀티 응답 제한 초과:', error);
      alert('멀티 응답은 최대 5개까지 제출할 수 있습니다.');
    } else {
      console.error('❌ 퀴즈 제출 실패:', error);
      console.error('❌ 에러 상세:', {
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        name: error instanceof Error ? error.name : undefined,
      });
      const errorMessage = error instanceof Error ? error.message : '알 수 없는 오류';
      alert(`퀴즈 제출에 실패했습니다: ${errorMessage}`);
    }
  }, []);

  useEffect(() => {
    if (!activeQuizId || !resultData?.classInfo?.classCode || !effectiveStudentId) {
      return;
    }

    const classCode = resultData.classInfo.classCode;
    const studentId = effectiveStudentId;
    
    const unsubscribe = chatQuizService.listenToResponses(
      classCode,
      activeQuizId,
      (responses) => {
        applyOwnResponses(studentId, responses || {});
        
        if (isPublicMode && hasSubmittedAnswer) {
          applyOtherResponses(activeQuizId, responses || {});
    } else {
          setOtherStudentsAnswers([]);
        }
      },
      undefined,
      studentMemberGroup
    );

    return () => unsubscribe();
  }, [activeQuizId, resultData?.classInfo?.classCode, effectiveStudentId, applyOwnResponses, isPublicMode, hasSubmittedAnswer, applyOtherResponses, studentMemberGroup]);

  useEffect(() => {
    if (!activeQuizId || !resultData?.classInfo?.classCode || !effectiveStudentId) {
      return;
    }

    let cancelled = false;
    
    const classCode = resultData.classInfo.classCode;
    const studentId = effectiveStudentId;
    
    const fetchResponses = async () => {
      try {
        const { responses } = await chatQuizService.getQuizResponses(
          classCode,
          activeQuizId,
          undefined,
          studentMemberGroup
        );
        if (cancelled) return;
        applyOwnResponses(studentId, responses || {});

        if (isPublicMode && hasSubmittedAnswer) {
          applyOtherResponses(activeQuizId, responses || {});
    } else {
          setOtherStudentsAnswers([]);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('❌ 퀴즈 응답 로드 실패:', error);
        }
      }
    };

    fetchResponses();

    return () => {
      cancelled = true;
    };
  }, [activeQuizId, resultData?.classInfo?.classCode, effectiveStudentId, applyOwnResponses, applyOtherResponses, isPublicMode, hasSubmittedAnswer, studentMemberGroup]);

  useEffect(() => {
    setShowInputField(!!activeQuiz && !!activeQuizId && isChatOpen);
  }, [activeQuiz, activeQuizId, isChatOpen]);

  // connectedStudents 등록은 "클래스 챗 연결하기" 버튼으로만 수행 (useEffect 제거)

  // 페이지 언마운트 시 접속 정보 제거 (브라우저를 닫을 때)
  useEffect(() => {
    return () => {
      if (!resultData?.classInfo?.classCode || !effectiveStudentId) {
        return;
      }

      const classCode = resultData.classInfo.classCode;
      console.log('🔌 페이지 언마운트: 멤버 접속 정보 제거 시작:', { classCode, studentId: effectiveStudentId });
      chatQuizService.removeConnectedStudentFromChat(classCode, effectiveStudentId)
        .then(() => {
          console.log('✅ 페이지 언마운트: 멤버 접속 정보 제거 완료:', { classCode, studentId: effectiveStudentId });
        })
        .catch((error) => {
          console.error('❌ 페이지 언마운트: 멤버 접속 정보 제거 실패:', error, { classCode, studentId: effectiveStudentId });
        });
    };
  }, [resultData?.classInfo?.classCode, effectiveStudentId]); // 페이지 언마운트 시에만 실행

  // 보유 아이템 카드 로드
  const loadItemCards = useCallback(
    async (force?: boolean) => {
      if (!memberPageScope || !resultData?.student) return;
    
    try {
      setIsLoadingItemCards(true);
        const cached = await studentMemberPageData.loadItemCards(memberPageScope, { force });
        const allItemCards = cached.allItemCards;
        const itemCardSet = cached.itemCardSet;
        setItemCards(allItemCards);
      
        const normalizeAcquiredAt = (value: unknown) => {
        if (!value) return new Date();
        if (value instanceof Date) return value;
          if (typeof value === 'object' && value !== null && 'toDate' in value && typeof (value as { toDate: () => Date }).toDate === 'function') {
            return (value as { toDate: () => Date }).toDate();
          }
        if (typeof value === 'string' || typeof value === 'number') {
          const parsed = new Date(value);
          return isNaN(parsed.getTime()) ? new Date() : parsed;
        }
        return new Date();
      };

        const sourceCards =
          cached.studentItemCards.length > 0
            ? cached.studentItemCards
            : resultData.student.basicInfo.itemCards || [];

        setStudentItemCards(
          sourceCards.map((item: StudentItemCard & { category?: string }) => {
            const itemCardDef = allItemCards.find((ic) => ic.itemCardId === item.itemCardId);
        let category = item.category;
        if (!category) {
          if (item.assignmentId) {
            category = 'assignment';
          } else if (itemCardSet && itemCardDef) {
                if (itemCardSet.storeItem.some((ic) => ic.itemCardId === item.itemCardId)) {
              category = 'store';
                } else if (itemCardSet.assignmentItem?.some((ic) => ic.itemCardId === item.itemCardId)) {
              category = 'assignment';
                } else if (itemCardSet.levelupItem.some((ic) => ic.itemCardId === item.itemCardId)) {
              category = 'levelup';
            } else {
                  category = 'levelup';
            }
          } else {
                category = 'levelup';
          }
        }
        return {
        instanceId: item.instanceId,
        itemCardId: item.itemCardId,
        type: item.type,
        remainingUseCount: item.remainingUseCount,
        acquiredAt: normalizeAcquiredAt(item.acquiredAt),
              category,
        assignmentId: item.assignmentId,
        acquiredLevel: item.acquiredLevel,
        isLocked: item.isLocked
        };
          })
        );
    } catch (error) {
      console.error('아이템 카드 로드 실패:', error);
    } finally {
      setIsLoadingItemCards(false);
    }
    },
    [memberPageScope, resultData?.student]
  );

  useEffect(() => {
    loadItemCards();
  }, [loadItemCards]);

  // 페이지 로드 시 프로젝트 보상 알람 확인
  useEffect(() => {
    if (!resultData?.student?.basicInfo?.id) return;
    
    const studentId = resultData.student.basicInfo.id;
    const notifications = resultData?.student?.basicInfo?.pendingNotifications || [];
    
    // localStorage에서 현재 표시한 알람 ID 목록 로드
    const storageKey = `shownNotifications_${studentId}`;
    const storedIds = localStorage.getItem(storageKey);
    const shownIds = storedIds ? new Set<string>(JSON.parse(storedIds)) : new Set<string>();
    
    // 서버에 없는 알람은 localStorage에서도 제거 (동기화)
    const currentNotificationIds = new Set(notifications.map(n => n.id));
    const syncedShownIds = new Set<string>();
    
    shownIds.forEach(id => {
      if (currentNotificationIds.has(id)) {
        syncedShownIds.add(id);
      }
    });
    
    // 동기화된 목록 저장
    if (syncedShownIds.size !== shownIds.size) {
      localStorage.setItem(storageKey, JSON.stringify(Array.from(syncedShownIds)));
      setShownNotificationIds(syncedShownIds);
    } else {
      setShownNotificationIds(shownIds);
    }
    
    // 서버에 알람이 없으면 localStorage 정리
    if (notifications.length === 0) {
      localStorage.removeItem(storageKey);
      return;
    }
    
    // 이미 표시하지 않은 첫 번째 알람 찾기
    const firstUnshownNotification = notifications.find(
      notification => !syncedShownIds.has(notification.id)
    );
    
    if (firstUnshownNotification) {
      // 첫 번째 알람 표시
      setCurrentNotification(firstUnshownNotification);
      
      // 보상 토스트 데이터 설정
      setRewardToastData({
        message: firstUnshownNotification.message || `${firstUnshownNotification.projectTitle} 프로젝트를 완료하였습니다!`,
        coins: firstUnshownNotification.coins,
        itemCardIds: firstUnshownNotification.itemCardIds
      });
      
      // 보상 토스트 표시
      setShowRewardToast(true);
      
      // 표시한 알람 ID 추가 (중복 표시 방지)
      const updatedIds = new Set(syncedShownIds).add(firstUnshownNotification.id);
      localStorage.setItem(storageKey, JSON.stringify(Array.from(updatedIds)));
      setShownNotificationIds(updatedIds);
      
      console.log('🔔 프로젝트 보상 알람 표시:', firstUnshownNotification);
    }
  }, [resultData]);

  // 프로젝트 보상 알람 제거
  const handleRemoveNotification = async () => {
    if (!currentNotification || !resultData?.classInfo || !resultData?.student) return;
    
    try {
      const result = await classCodeService.removeStudentNotification(
        resultData.classInfo.userId,
        resultData.classInfo.classId,
        resultData.student.basicInfo.id,
        currentNotification.id,
        studentMemberGroup
      );
      
      if (result.success) {
        console.log('✅ 알람 제거 성공:', currentNotification.id);
        
        // 표시한 알람 ID 목록에서도 제거 (서버에서 제거되었으므로)
        setShownNotificationIds(prev => {
          const updated = new Set(prev);
          updated.delete(currentNotification.id);
          saveShownNotificationIds(updated);
          return updated;
        });
        
        // 현재 알람 제거
        setCurrentNotification(null);
        setShowRewardToast(false);
        setRewardToastData(null);
        
        // resultData에서도 알람 제거 (UI 업데이트)
        if (resultData.student.basicInfo.pendingNotifications) {
          const updatedNotifications = resultData.student.basicInfo.pendingNotifications.filter(
            n => n.id !== currentNotification.id
          );
          resultData.student.basicInfo.pendingNotifications = updatedNotifications;
          
          // 다음 알람이 있고, 아직 표시하지 않은 알람이 있으면 표시
          const nextUnshownNotification = updatedNotifications.find(
            notification => !shownNotificationIds.has(notification.id)
          );
          
          if (nextUnshownNotification) {
            setCurrentNotification(nextUnshownNotification);
            setRewardToastData({
              message: nextUnshownNotification.message || `${nextUnshownNotification.projectTitle} 프로젝트를 완료하였습니다!`,
              coins: nextUnshownNotification.coins,
              itemCardIds: nextUnshownNotification.itemCardIds
            });
            setShowRewardToast(true);
            
            // 표시한 알람 ID 추가
            setShownNotificationIds(prev => {
              const updated = new Set(prev).add(nextUnshownNotification.id);
              saveShownNotificationIds(updated);
              return updated;
            });
          }
        }
      } else {
        console.error('❌ 알람 제거 실패:', result.error);
      }
    } catch (error) {
      console.error('❌ 알람 제거 중 오류:', error);
    }
  };

  // 과제 블럭 클릭
  const handleAssignmentClick = async (assignment: any) => {
    setSelectedAssignment(assignment);
    setSubmissionLink(assignment.studentStatus?.submissionLink || '');
    setSubmissionMessage(assignment.studentStatus?.submissionMessage || '');
    setCompletionCode('');
    setIsCodeVerified(false);
    setPendingSubmissionImage(null);
    setRemovedExistingSubmissionImage(false);
    if (pendingImagePreviewUrl) {
      URL.revokeObjectURL(pendingImagePreviewUrl);
      setPendingImagePreviewUrl(null);
    }
    setIsModalOpen(true);
    if (resultData?.classInfo?.classCode) {
      try {
        const codiInfo = await classCodeService.getCodiInfoByClassCode(resultData.classInfo.classCode);
        setCodiUserType(codiInfo?.userType);
      } catch {
        setCodiUserType(undefined);
      }
    }
  };

  const canSubmitAssignmentImage = useMemo(() => {
    if (!codiUserType) return false;
    return checkAssignmentImageSubmissionAccess(codiUserType).allowed;
  }, [codiUserType]);

  const selectedExistingSubmissionImagePath = selectedAssignment?.studentStatus?.submissionImagePath as
    | string
    | undefined;
  const selectedExistingSubmissionImageUpdatedAt = selectedAssignment?.studentStatus
    ?.submissionImageUpdatedAt as string | undefined;

  const showSubmissionImageSection =
    canSubmitAssignmentImage || !!selectedExistingSubmissionImagePath;

  useEffect(() => {
    if (!isModalOpen || !selectedExistingSubmissionImagePath) {
      setExistingSubmissionImageUrl(null);
      setExistingSubmissionImageLoadFailed(false);
      return;
    }
    let cancelled = false;
    setExistingSubmissionImageLoadFailed(false);
    const loadImage = async () => {
      const cached = assignmentSubmissionImageCache.getCached(
        selectedExistingSubmissionImagePath,
        selectedExistingSubmissionImageUpdatedAt
      );
      if (cached) {
        setExistingSubmissionImageUrl(cached);
        setExistingSubmissionImageLoadFailed(false);
        return;
      }

      const classInfo = resultData?.classInfo;
      const member = resultData?.student?.basicInfo ?? resultData?.member;
      if (!member || !classInfo?.classCode) {
        setExistingSubmissionImageUrl(null);
        setExistingSubmissionImageLoadFailed(true);
        return;
      }

      const result = await classCodeService.getOwnSubmissionImageUrl({
        classCode: classInfo.classCode,
        studentNumber: String(member.number ?? ''),
        studentName: String(member.name ?? ''),
        accessCode: member.accessCode,
        memberGroup: classInfo.memberGroup,
        memberId: member.id,
        submissionImagePath: selectedExistingSubmissionImagePath
      });
      if (cancelled) return;
      if (result.success && result.url) {
        assignmentSubmissionImageCache.remember(
          selectedExistingSubmissionImagePath,
          result.url,
          selectedExistingSubmissionImageUpdatedAt
        );
        setExistingSubmissionImageUrl(result.url);
        setExistingSubmissionImageLoadFailed(false);
        return;
      }
      setExistingSubmissionImageUrl(null);
      setExistingSubmissionImageLoadFailed(true);
    };
    void loadImage();
    return () => {
      cancelled = true;
    };
  }, [
    isModalOpen,
    selectedExistingSubmissionImagePath,
    selectedExistingSubmissionImageUpdatedAt,
    resultData?.student,
    resultData?.member,
    resultData?.classInfo?.classCode,
    resultData?.classInfo?.memberGroup
  ]);

  const handlePendingImageSave = (blob: Blob, mimeType: string) => {
    setRemovedExistingSubmissionImage(false);
    setPendingSubmissionImage({ blob, mimeType });
    if (pendingImagePreviewUrl) URL.revokeObjectURL(pendingImagePreviewUrl);
    setPendingImagePreviewUrl(URL.createObjectURL(blob));
  };

  const clearPendingSubmissionImage = () => {
    setPendingSubmissionImage(null);
    if (pendingImagePreviewUrl) {
      URL.revokeObjectURL(pendingImagePreviewUrl);
      setPendingImagePreviewUrl(null);
    }
  };

  const handleRemoveSubmissionImage = () => {
    if (pendingSubmissionImage || pendingImagePreviewUrl) {
      clearPendingSubmissionImage();
      return;
    }
    if (!selectedExistingSubmissionImagePath) return;
    setRemovedExistingSubmissionImage(true);
    setExistingSubmissionImageUrl(null);
    setExistingSubmissionImageLoadFailed(false);
    assignmentSubmissionImageCache.revoke(
      selectedExistingSubmissionImagePath,
      selectedExistingSubmissionImageUpdatedAt
    );
  };

  // 모달 닫기
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedAssignment(null);
    setSubmissionLink('');
    setSubmissionMessage('');
    setCompletionCode('');
    setIsCodeVerified(false);
    clearPendingSubmissionImage();
    setRemovedExistingSubmissionImage(false);
    setExistingSubmissionImageUrl(null);
    setExistingSubmissionImageLoadFailed(false);
  };
  
  // 과제 완료 코드 검증
  const handleVerifyCode = () => {
    if (!selectedAssignment || !selectedAssignment.rewardEnabled || !selectedAssignment.rewardCode) {
      return;
    }
    
    if (completionCode.trim() === selectedAssignment.rewardCode.trim()) {
      setIsCodeVerified(true);
    } else {
      setIsCodeVerified(false);
      alert('과제 완료 코드가 일치하지 않습니다.');
    }
  };

  // 과제 제출
  const handleSubmitAssignment = async () => {
    if (!selectedAssignment || !resultData?.classInfo || !resultData?.student) return;
    
    const hasSubmissionImage =
      !!pendingSubmissionImage ||
      (!!selectedExistingSubmissionImagePath && !removedExistingSubmissionImage);

    // 코드가 검증되지 않은 경우에만 링크 또는 메시지 필수
    if (
      !isCodeVerified &&
      !submissionLink.trim() &&
      !submissionMessage.trim() &&
      !hasSubmissionImage
    ) {
      alert('과제 링크, 메시지 또는 사진 중 하나 이상을 입력해주세요.');
      return;
    }
    
    setSubmitting(true);
    
    try {
      let submissionImage: { path: string; updatedAt: string; sizeBytes: number } | undefined;

      if (pendingSubmissionImage && canSubmitAssignmentImage) {
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const dataUrl = String(reader.result || '');
            const comma = dataUrl.indexOf(',');
            resolve(comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl);
          };
          reader.onerror = reject;
          reader.readAsDataURL(pendingSubmissionImage.blob);
        });

        const uploadResult = await classCodeService.uploadAssignmentSubmissionImage({
          classCode: resultData.classInfo.classCode,
          studentNumber: resultData.student.basicInfo.number || '',
          studentName: resultData.student.basicInfo.name || '',
          accessCode: resultData.student.basicInfo.accessCode,
          memberGroup: studentMemberGroup,
          memberId: resultData.student.basicInfo.id,
          assignmentType: selectedAssignment.type,
          assignmentId: selectedAssignment.id,
          subTaskId: selectedAssignment.subTaskId,
          imageBase64: base64,
          mimeType: pendingSubmissionImage.mimeType
        });

        if (!uploadResult.success || !uploadResult.submissionImagePath) {
          showToastMessage(uploadResult.error || '이미지 업로드에 실패했습니다.');
          setSubmitting(false);
          return;
        }

        submissionImage = {
          path: uploadResult.submissionImagePath,
          updatedAt: uploadResult.submissionImageUpdatedAt || new Date().toISOString(),
          sizeBytes: uploadResult.submissionImageSizeBytes || pendingSubmissionImage.blob.size
        };
      }

      const shouldClearExistingImage =
        removedExistingSubmissionImage && !pendingSubmissionImage;

      if (shouldClearExistingImage && selectedExistingSubmissionImagePath) {
        const deleteResult = await classCodeService.deleteOwnAssignmentSubmissionImage({
          classCode: resultData.classInfo.classCode,
          studentNumber: resultData.student.basicInfo.number || '',
          studentName: resultData.student.basicInfo.name || '',
          accessCode: resultData.student.basicInfo.accessCode,
          memberGroup: studentMemberGroup,
          memberId: resultData.student.basicInfo.id,
          submissionImagePath: selectedExistingSubmissionImagePath
        });
        if (!deleteResult.success) {
          showToastMessage(deleteResult.error || '기존 사진 삭제에 실패했습니다.');
          setSubmitting(false);
          return;
        }
        assignmentSubmissionImageCache.revoke(
          selectedExistingSubmissionImagePath,
          selectedExistingSubmissionImageUpdatedAt
        );
      }

      const result = await classCodeService.submitAssignment(
        resultData.classInfo.userId,
        resultData.classInfo.classId,
        resultData.student.basicInfo.id,
        selectedAssignment.id,
        selectedAssignment.type,
        submissionLink.trim(),
        submissionMessage.trim(),
        selectedAssignment.dateId, // 루틴 과제인 경우 필요
        selectedAssignment.subTaskId, // 프로젝트 과제인 경우 필요
        isCodeVerified ? completionCode.trim() : undefined, // 과제 완료 코드
        studentMemberGroup,
        submissionImage,
        { clearSubmissionImage: shouldClearExistingImage }
      );
      
      if (result.success) {
        if (isCodeVerified) {
          // 보상 정보 토스트 표시
          if (selectedAssignment.rewardEnabled) {
            setRewardToastData({
              message: selectedAssignment.rewardMessage || `${selectedAssignment.title} 과제를 완료하였습니다!`,
              coins: selectedAssignment.rewardCoins,
              itemCardIds: selectedAssignment.rewardItemCardIds
            });
            setShowRewardToast(true);
          } else {
          alert('과제가 완료되었습니다! 보상이 지급되었습니다.');
          }
        } else {
          alert('과제가 제출되었습니다!');
        }
        handleCloseModal();
        if (memberPageScope) {
          userDataService.invalidateMembersCache(
            memberPageScope.userId,
            memberPageScope.classId,
            memberPageScope.memberGroup
          );
          invalidateMemberPageAfterAssignmentChange(memberPageScope);
        }
        await loadPendingAssignments(true);
        await reloadStudentInfo(true);
      } else {
        alert(result.error || '과제 제출에 실패했습니다.');
      }
    } catch (error) {
      console.error('과제 제출 실패:', error);
      alert('과제 제출 중 오류가 발생했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!resultData || !resultData.classInfo) {
    return null;
  }

  // 멤버 요약 지연 로드 중: 전체 UI는 student 없이 렌더하면 크래시하므로 로딩만 표시
  const showMemberBootstrapOnly =
    !isGuestMode &&
    !!resultData.pendingStudentSummary &&
    !!resultData.member &&
    !resultData.student;

  if (showMemberBootstrapOnly) {
    return (
      <Container>
        <BackgroundLayer />
        <MemberPageBootstrapOverlay role="status" aria-live="polite">
          <MemberPageBootstrapSpinner aria-hidden />
          <MemberPageBootstrapText>데이터를 불러오는 중입니다…</MemberPageBootstrapText>
          {resultData.classInfo?.className ? (
            <MemberPageBootstrapSub>{resultData.classInfo.className}</MemberPageBootstrapSub>
          ) : null}
        </MemberPageBootstrapOverlay>
      </Container>
    );
  }

  if (!resultData.student) {
    return null;
  }

  const getAssignmentTypeLabel = (type: string) => {
    if (type === 'general') return '일반과제';
    if (type === 'project') return '프로젝트';
    if (type === 'routine') return '루틴과제';
    return '과제';
  };

  const formatDate = (date: Date) => {
    const d = new Date(date);
    const weekday = ['일','월','화','수','목','금','토'][d.getDay()];
    return `${d.getMonth() + 1}/${d.getDate()} (${weekday})`;
  };

  const pendingAssignments = assignments.filter(a => a.studentStatus?.status === 'pending');
  const submittedAssignments = assignments.filter(a => a.studentStatus?.status === 'submitted');
  const displayAssignments = [...pendingAssignments, ...submittedAssignments];
  const hasAnyCorrectResponse = submittedAnswers.some((a) => a.isCorrect === true);
  const hasEssayResponse = activeQuiz?.type === 'essay' && submittedAnswers.some((a) => a.hasContent);
  // 멀티 응답 모드일 때는 정답 여부와 관계없이 계속 응답할 수 있도록 함
  const isLockedByCompletedAnswer = !!activeQuiz && !isMultiAnswerMode && (hasAnyCorrectResponse || !!hasEssayResponse);
  const isInputDisabled =
    !isChatOpen ||
    !activeQuiz ||
    (!isMultiAnswerMode && hasSubmittedAnswer) ||
    hasReachedMultiAnswerLimit ||
    isLockedByCompletedAnswer;
  const disabledPlaceholderText = hasReachedMultiAnswerLimit
    ? '멀티 응답은 최대 5개까지 제출할 수 있습니다.'
    : '이미 답변을 제출했습니다.';

  return (
    <Container>
      <BackgroundLayer />
      {selectedView !== 'manager' && (
      <NavBar>
        {selectedView === 'main' ? (
          <BackButtonPlaceholder />
        ) : (
          <BackButton onClick={() => {
            // 퀴즈/학생 문제 만들기 진행 중에는 이동 방지
            if (activeQuiz && activeQuizId) {
              showToastMessage('퀴즈 참여 중에는 페이지를 이동할 수 없습니다');
              return;
            }
            if (studentCreateMode) {
              showToastMessage('퀴즈 만들기 참여 중에는 페이지를 이동할 수 없습니다');
              return;
            }
            setSelectedView('main');
          }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </BackButton>
        )}
        <NavTitle>
          {selectedView === 'main' ? '나의 상태 확인' : selectedView === 'quiz' ? '퀴즈' : '프로필'}
        </NavTitle>
        <RightButtonContainer>
          <StudentNameTag>{effectiveName}</StudentNameTag>
          <HamburgerButton 
            className="hamburger-menu"
            onClick={handleHamburgerClick}
            aria-label="메뉴"
          >
            <svg 
              width="20" 
              height="20" 
              viewBox="0 0 24 24" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
            >
              <path 
                d="M3 12h18M3 6h18M3 18h18" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </svg>
          </HamburgerButton>
          
          {/* 햄버거 메뉴 드롭다운 */}
          {isMenuOpen && (
            <MenuDropdown className="menu-dropdown">
              <MenuIdentityHeader>
                <MenuIdentityTopRow>
                  <MenuProfileThumb>
                    <CachedProfileImage
                      imageId={effectiveProfileImageId}
                      alt=""
                      fallbackImageId="basic-0"
                    />
                  </MenuProfileThumb>
                  <MenuLogoutButton
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleMenuLogout();
                    }}
                  >
                    로그아웃
                  </MenuLogoutButton>
                </MenuIdentityTopRow>
                <MenuIdentityName>{menuNameLine}</MenuIdentityName>
                {menuClassLine ? <MenuIdentityMeta>{menuClassLine}</MenuIdentityMeta> : null}
              </MenuIdentityHeader>

              {!isGuestMode && resultData?.student && (
                  <MenuItem
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowProfileEditModal(true);
                      setIsMenuOpen(false);
                    }}
                  >
                  설정
                  </MenuItem>
              )}

              <MenuDivider />

              <MenuFooter>
                <NavThemeToggleButton
                  $isDark={themeMode === 'dark'}
                  onClick={(e) => e.stopPropagation()}
                  aria-label="테마 전환"
                >
                  <NavThemeToggleOption
                    $isActive={themeMode === 'light'}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (themeMode === 'dark') handleThemeToggle();
                    }}
                  >
                    일반
                  </NavThemeToggleOption>
                  <NavThemeToggleOption
                    $isActive={themeMode === 'dark'}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                      if (themeMode === 'light') handleThemeToggle();
                  }}
                >
                    다크
                  </NavThemeToggleOption>
                </NavThemeToggleButton>
              </MenuFooter>
            </MenuDropdown>
          )}
        </RightButtonContainer>
      </NavBar>
      )}

      {selectedView === 'manager' && !isGuestMode && resultData?.student?.basicInfo?.isManager === true && resultData?.classInfo ? (
        <ManagerViewFullWidth>
          <ManagerAssignmentView
            userId={resultData.classInfo.userId}
            classId={resultData.classInfo.classId}
            memberGroup={studentMemberGroup}
            onBack={() => setSelectedView('main')}
          />
        </ManagerViewFullWidth>
      ) : (
      <Content>
        {selectedView === 'main' && (
          <>
            <MainCardContainer>
              <MainCard 
                onClick={() => {
                  console.log('🎯 채팅 아이콘 클릭됨, selectedView 변경: main → quiz');
                  // 퀴즈 버튼 클릭 시 퀴즈 페이지로 전환 (connectedStudents 등록은 연결하기 버튼으로만)
                  setSelectedView('quiz');
                }}
              >
                <MainCardIcon>
                  <img src="/Chat2.svg" alt="퀴즈" />
                </MainCardIcon>
              </MainCard>
              {!isGuestMode && (
                <MainCard onClick={() => setSelectedView('profile')}>
                  <MainCardIcon>
                    <img src="/ProfileCard.svg" alt="프로필" />
                  </MainCardIcon>
                </MainCard>
              )}
              {!isGuestMode && resultData?.student?.basicInfo?.isManager === true && (
                <MainCard $isManagerCard onClick={() => setSelectedView('manager')}>
                  <MainCardIcon>
                    <img src="/Check.svg" alt="매니저" />
                  </MainCardIcon>
                </MainCard>
              )}
            </MainCardContainer>
            
            {!isGuestMode && (
              <AssignmentSection>
                <PendingAssignmentHintBox>
                <GeneralNoticePreviewCard
                  role="button"
                  tabIndex={0}
                  onClick={handleOpenGeneralNoticeModal}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      void handleOpenGeneralNoticeModal();
                    }
                  }}
                  aria-label="전체공지노트 열람"
                  title="전체공지노트 열람"
                >
                  <GeneralNoticeHeader aria-hidden="true">
                    <GeneralNoticeHeaderIcon>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M13.73 21a2 2 0 0 1-3.46 0"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </GeneralNoticeHeaderIcon>
                    <GeneralNoticeHeaderTitle>전체공지노트</GeneralNoticeHeaderTitle>
                  </GeneralNoticeHeader>
                  <GeneralNoticePreviewTitle>전체공지노트</GeneralNoticePreviewTitle>
                  {latestGeneralNoticePreview?.date && (
                    <GeneralNoticePreviewDate>
                      {(() => {
                        try {
                          const dateStr = latestGeneralNoticePreview.date;
                          if (dateStr && dateStr.includes('-')) {
                            const [year, month, day] = dateStr.split('-');
                            return `${parseInt(month)}월 ${parseInt(day)}일`;
                          }
                          return dateStr;
                        } catch {
                          return latestGeneralNoticePreview.date;
                        }
                      })()}
                    </GeneralNoticePreviewDate>
                  )}
                  <GeneralNoticePreviewContent $fontSize={latestGeneralNoticePreview?.fontSize || 16}>
                    {latestGeneralNoticePreview?.content?.trim()
                      ? latestGeneralNoticePreview.content
                      : isLoadingGeneralNoticeNotes
                        ? '불러오는 중...'
                        : '등록된 전체공지노트가 없습니다.'}
                  </GeneralNoticePreviewContent>
                </GeneralNoticePreviewCard>

                <IndividualNoticePreviewCard
                  role="button"
                  tabIndex={0}
                  onClick={handleOpenIndividualNoticeModal}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      void handleOpenIndividualNoticeModal();
                    }
                  }}
                  aria-label="개별공지노트 열람"
                  title="개별공지노트 열람"
                >
                  <IndividualNoticeHeader aria-hidden="true">
                    <IndividualNoticeHeaderIcon>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M13.73 21a2 2 0 0 1-3.46 0"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </IndividualNoticeHeaderIcon>
                    <IndividualNoticeHeaderTitle>개별공지노트</IndividualNoticeHeaderTitle>
                  </IndividualNoticeHeader>
                  <IndividualNoticePreviewTitle>개별공지노트</IndividualNoticePreviewTitle>
                  {latestIndividualNoticePreview?.date && (
                    <IndividualNoticePreviewDate>
                      {(() => {
                        try {
                          const dateStr = latestIndividualNoticePreview.date;
                          if (dateStr && dateStr.includes('-')) {
                            const [year, month, day] = dateStr.split('-');
                            return `${parseInt(month)}월 ${parseInt(day)}일`;
                          }
                          return dateStr;
                        } catch {
                          return latestIndividualNoticePreview.date;
                        }
                      })()}
                    </IndividualNoticePreviewDate>
                  )}
                  <IndividualNoticePreviewContent $fontSize={latestGeneralNoticePreview?.fontSize || 16}>
                    {latestIndividualNoticePreview?.content?.trim()
                      ? latestIndividualNoticePreview.content
                      : isLoadingIndividualNoticeNotes
                        ? '불러오는 중...'
                        : '등록된 개별공지노트가 없습니다.'}
                  </IndividualNoticePreviewContent>
                </IndividualNoticePreviewCard>
                </PendingAssignmentHintBox>

                {routineSectionEnabled && (
                  <TodayRoutineSectionBox>
                    <TodayRoutineSectionTitleBox>
                      <TodayRoutineSectionTitleText>
                  {(() => {
                    const d = new Date();
                    const weekdays = ['일', '월', '화', '수', '목', '금', '토'];
                    return `${d.getMonth() + 1}월 ${d.getDate()}일 (${weekdays[d.getDay()]}) - 오늘의 루틴 과제`;
                  })()}
                      </TodayRoutineSectionTitleText>
                    </TodayRoutineSectionTitleBox>
                {!loadingTodayRoutine && todayRoutineAssignments.length > 0 && (
                  <SaveRoutineButton
                    onClick={handleSaveRoutineChanges}
                    disabled={isSavingRoutines || pendingRoutineChanges.size === 0}
                  >
                    {isSavingRoutines ? '저장 중...' : '저장하기'}
                  </SaveRoutineButton>
                )}
                {loadingTodayRoutine ? (
                  <LoadingState>오늘 루틴 과제를 불러오는 중...</LoadingState>
                ) : todayRoutineAssignments.length === 0 ? (
                      <EmptyStateTextOnly>오늘 해당 루틴 과제가 없습니다</EmptyStateTextOnly>
                ) : (
                  <RoutineBlockRow>
                    {todayRoutineAssignments.map((r) => {
                      const isPending = r.status !== 'confirmed';
                      const statusColor = isPending ? '#FF316B' : '#227971';
                      const statusLabel = isPending ? '미완료' : '과제완료';
                      const isToggling = routineTogglingId === r.id;
                      const completionRate = r.completionRate ?? 0;
                      const confirmedCount = r.confirmedCount ?? 0;
                      const totalDates = r.totalDates ?? 0;
                      const todayCount = r.todayCount ?? 0;
                      const roundedCompletionRate = Math.round(completionRate);
                      return (
                        <RoutineBlock
                          key={r.id}
                          $status={isPending ? 'pending' : 'confirmed'}
                          onClick={() => !isToggling && handleRoutineBlockToggle(r)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if ((e.key === 'Enter' || e.key === ' ') && !isToggling) {
                              e.preventDefault();
                              handleRoutineBlockToggle(r);
                            }
                          }}
                        >
                          <RoutineBlockTopRow>
                            <RoutineBlockStatusLabel $color={statusColor}>
                              {isToggling ? '…' : statusLabel}
                            </RoutineBlockStatusLabel>
                            <RoutineBlockName>{r.title}</RoutineBlockName>
                          </RoutineBlockTopRow>
                          {totalDates > 0 && (
                            <RoutineProgressContainer>
                              <RoutineProgressPercent>{roundedCompletionRate}% ({confirmedCount}/{totalDates}회)</RoutineProgressPercent>
                              <RoutineProgressBar>
                                <RoutineProgressBarToday style={{ width: `${Math.min((todayCount / Math.max(totalDates, 1)) * 100, 100)}%` }} />
                                <RoutineProgressBarFill style={{ width: `${Math.min(completionRate, 100)}%` }} />
                              </RoutineProgressBar>
                            </RoutineProgressContainer>
                          )}
                        </RoutineBlock>
                      );
                    })}
                  </RoutineBlockRow>
                    )}
                  </TodayRoutineSectionBox>
                )}

                <IncompleteAssignmentSectionBox>
                  <IncompleteAssignmentSectionTitleBox>
                    <IncompleteAssignmentSectionTitleText>미완료 일반,프로젝트 과제</IncompleteAssignmentSectionTitleText>
                  </IncompleteAssignmentSectionTitleBox>
                {loading ? (
                <LoadingState>과제를 불러오는 중...</LoadingState>
              ) : displayAssignments.length === 0 ? (
                <EmptyStateTextOnly>제출할 과제가 없습니다</EmptyStateTextOnly>
              ) : (
                <AssignmentList>
                  {displayAssignments.map((assignment, index) => (
                    <AssignmentBlockWrapper
                      key={`${assignment.id}-${assignment.type}-${index}`}
                      $isSubmitted={assignment.studentStatus?.status === 'submitted'}
                      onClick={() => handleAssignmentClick(assignment)}
                      role="button"
                      tabIndex={0}
                    >
                      <AssignmentHeader>
                        <AssignmentBadge $type={assignment.type} $projectColor={assignment.type === 'project' ? assignment.color : undefined}>
                          {getAssignmentTypeLabel(assignment.type)}
                        </AssignmentBadge>
                        <AssignmentTitle $isSubmitted={assignment.studentStatus?.status === 'submitted'}>
                          {assignment.title}
                          {assignment.type === 'project' && assignment.subTaskTitle && (
                            <> - {assignment.subTaskTitle}</>
                          )}
                        </AssignmentTitle>
                      </AssignmentHeader>
                      <AssignmentInfo $isSubmitted={assignment.studentStatus?.status === 'submitted'}>
                        <AssignmentInfoText>
                        마감: {formatDate(assignment.dueDate)} • 획득포인트: {assignment.basePoints}점
                          {(assignment.rewardEnabled && assignment.rewardCoins && assignment.rewardCoins >= 1) || 
                           (assignment.rewardItemCardIds && assignment.rewardItemCardIds.length > 0) ? (
                            <RewardInfo>
                              {assignment.rewardEnabled && assignment.rewardCoins && assignment.rewardCoins >= 1 && (
                                <RewardItem>
                                  <CoinIcon>
                                    <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <circle cx="10" cy="10" r="8" fill="#FFD700" stroke="#FFA500" strokeWidth="1.5"/>
                                      <circle cx="10" cy="10" r="5" fill="#FFA500" opacity="0.3"/>
                                      <text x="10" y="13" fontSize="8" fontWeight="bold" fill="#FFA500" textAnchor="middle">₩</text>
                                    </svg>
                                  </CoinIcon>
                                  <RewardText>{assignment.rewardCoins}</RewardText>
                                </RewardItem>
                              )}
                              {assignment.rewardItemCardIds && assignment.rewardItemCardIds.length > 0 && (
                                <RewardItem>
                                  <ItemCardIcon>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <rect x="2" y="6" width="12" height="16" rx="2" fill="#ffffff" stroke="#555" strokeWidth="1.5" transform="rotate(-10 8 14)"/>
                                      <rect x="6" y="4" width="12" height="16" rx="2" fill="#f8f8f8" stroke="#555" strokeWidth="1.5" transform="rotate(0 12 12)"/>
                                      <rect x="10" y="6" width="12" height="16" rx="2" fill="#fafafa" stroke="#555" strokeWidth="1.5" transform="rotate(10 16 14)"/>
                                    </svg>
                                  </ItemCardIcon>
                                  <RewardText>{assignment.rewardItemCardIds.length}</RewardText>
                                </RewardItem>
                              )}
                            </RewardInfo>
                          ) : null}
                        </AssignmentInfoText>
                      </AssignmentInfo>
                      {assignment.studentStatus?.status === 'submitted' && (
                        <SubmittedTag>
                          ✓ 제출 완료 (확인 대기중)
                        </SubmittedTag>
                      )}
                    </AssignmentBlockWrapper>
                  ))}
                </AssignmentList>
                )}
                </IncompleteAssignmentSectionBox>
              </AssignmentSection>
            )}
          </>
        )}
        
        {selectedView === 'quiz' && (
          <ThoughtSection>
            {!isConnected && isChatOpen && (
              <ReconnectButton
                onClick={async () => {
                  console.log('🔄 클래스챗 재접속 버튼 클릭:', {
                    hasClassCode: !!resultData?.classInfo?.classCode,
                    classCode: resultData?.classInfo?.classCode,
                    hasStudentId: !!effectiveStudentId,
                    studentId: effectiveStudentId,
                  });
                  
                  if (!resultData?.classInfo?.classCode) {
                    console.error('❌ 클래스 코드가 없어 접속 정보 기록 불가:', { resultData });
                    alert('클래스 코드를 찾을 수 없습니다. 페이지를 새로고침해주세요.');
                    return;
                  }
                  
                  if (!effectiveStudentId) {
                    console.error('❌ 멤버 ID가 없어 접속 정보 기록 불가:', { effectiveStudentId });
                    alert('멤버 정보를 찾을 수 없습니다. 페이지를 새로고침해주세요.');
                    return;
                  }
                  
                  const classCode = resultData.classInfo.classCode;
                  const studentInfo: ConnectedStudentInfo = {
                    name: effectiveName,
                    number: isGuestMode ? resultData?.guestNumber?.toString() : resultData?.student?.basicInfo?.number,
                    isGuest: isGuestMode,
                  };
                  
                  console.log('🔌 클래스챗 재접속 버튼: 멤버 접속 정보 기록 시작:', { classCode, studentId: effectiveStudentId, studentInfo });
                  try {
                    await chatQuizService.addConnectedStudentToChat(
                      classCode,
                      effectiveStudentId,
                      studentInfo,
                      studentMemberGroup
                    );
                    console.log('✅ 클래스챗 재접속 버튼: 멤버 접속 정보 기록 완료:', { classCode, studentId: effectiveStudentId });
                  } catch (error: any) {
                    console.error('❌ 클래스챗 재접속 버튼: 멤버 접속 정보 기록 실패:', error, { 
                      classCode, 
                      studentId: effectiveStudentId,
                      errorCode: error?.code,
                      errorMessage: error?.message,
                    });
                    alert(`접속 정보 기록 실패: ${error?.message || '알 수 없는 오류가 발생했습니다.'}`);
                  }
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                </svg>
                클래스 챗 연결하기
              </ReconnectButton>
            )}
            {!isChatOpen ? (
              <ThoughtGuideMessage>
                코디가 클래스 챗을 시작하면 활성화 됩니다.
              </ThoughtGuideMessage>
            ) : (activeQuizSetId && !activeQuizId) ? (
              <ThoughtGuideMessage>
                문제 세트가 준비되었습니다. 코디가 세트 재생을 시작하면 문제가 표시됩니다.
              </ThoughtGuideMessage>
            ) : studentCreateMode ? (
              <>
                {setTitle && (
                  <div style={{ marginBottom: 10, display: 'flex', justifyContent: 'center' }}>
                    <SetTitleBadge>{setTitle}</SetTitleBadge>
                  </div>
                )}
                {hasSubmittedStudentCreate ? (
                  <div style={{ textAlign: 'center', fontWeight: 600, fontSize: '16px', lineHeight: 1.6, marginBottom: 12, color: '#fff' }}>
                    문제 제출이 완료되었습니다.
                  </div>
                ) : (
                  <>
                    <div style={{ textAlign: 'center', fontWeight: 600, fontSize: '16px', lineHeight: 1.6, marginBottom: 12, color: '#fff' }}>
                      문제 유형을 선택하고 문제를 만든 후<br />
                      문제를 제출해 주세요.
                    </div>
                    <StudentTypeTabs>
                      <StudentTypeTab
                        type="button"
                        $active={studentQuestionType === 'choice'}
                        onClick={() => setStudentQuestionType('choice')}
                      >
                        선택형
                      </StudentTypeTab>
                      <StudentTypeTab
                        type="button"
                        $active={studentQuestionType === 'short'}
                        onClick={() => setStudentQuestionType('short')}
                      >
                        단답형
                      </StudentTypeTab>
                      <StudentTypeTab
                        type="button"
                        $active={studentQuestionType === 'essay'}
                        onClick={() => setStudentQuestionType('essay')}
                      >
                        서술형
                      </StudentTypeTab>
                    </StudentTypeTabs>
                  </>
                )}
                {isStudentCreateOpen && isChatOpen && (studentCreateSetId || activeQuizSetId) && !hasSubmittedStudentCreate && (
                  <>
                    <StudentCreatePanel>
                      <StudentQuestionInput
                        ref={studentQuestionInputRef}
                        rows={1}
                        value={studentQuestionText}
                        onChange={(e) => setStudentQuestionText(e.target.value)}
                        placeholder="질문을 입력하세요..."
                      />

                      {studentQuestionType === 'choice' && (
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={({ active, over }) => {
                          if (!over || active.id === over.id) return;
                          const oldIndex = studentChoices.findIndex((_, idx) => `choice-${idx}` === active.id);
                          const newIndex = studentChoices.findIndex((_, idx) => `choice-${idx}` === over.id);
                          if (oldIndex === -1 || newIndex === -1) return;
                          setStudentChoices((prev) => arrayMove(prev, oldIndex, newIndex));
                          setStudentChoiceAnswers((prev) =>
                            prev.map((v) => {
                              if (v === oldIndex) return newIndex;
                              if (oldIndex < newIndex && v > oldIndex && v <= newIndex) return v - 1;
                              if (oldIndex > newIndex && v < oldIndex && v >= newIndex) return v + 1;
                              return v;
                            })
                          );
                        }}
                      >
                        <SortableContext
                          items={studentChoices.map((_, idx) => `choice-${idx}`)}
                          strategy={verticalListSortingStrategy}
                        >
                          <StudentChoiceList>
                            {studentChoices.map((choice, idx) => (
                              <StudentSortableChoiceRow
                                key={`choice-${idx}`}
                                id={`choice-${idx}`}
                                index={idx}
                                selected={studentChoiceAnswers.includes(idx)}
                                choiceText={choice}
                                onSelect={() => {
                                  setStudentChoiceAnswers((prev) => {
                                    if (prev.includes(idx)) {
                                      return prev.filter((v) => v !== idx);
                                    }
                                    return [...prev, idx];
                                  });
                                }}
                                onChange={(text) => {
                                  const next = [...studentChoices];
                                  next[idx] = text;
                                  setStudentChoices(next);
                                }}
                                onReset={() => {
                                  const next = [...studentChoices];
                                  next[idx] = '';
                                  setStudentChoices(next);
                                }}
                                onDelete={() => {
                                  if (studentChoices.length <= 2) return;
                                  const next = studentChoices.filter((_, i) => i !== idx);
                                  setStudentChoices(next);
                                  setStudentChoiceAnswers((prev) =>
                                    prev
                                      .filter((v) => v !== idx)
                                      .map((v) => (v > idx ? v - 1 : v))
                                  );
                                }}
                              />
                            ))}
                            <StudentAddAnswerBox
                              type="button"
                              onClick={() => {
                                setStudentChoices((prev) => [...prev, '']);
                              }}
                            >
                              + 선택지 추가
                            </StudentAddAnswerBox>
                          </StudentChoiceList>
                        </SortableContext>
                      </DndContext>
                    )}
                    {studentQuestionType === 'short' && (
                      <>
                        {studentShortAnswers.map((ans, idx) => (
                          <StudentChoiceRow key={idx} style={{ gridTemplateColumns: '1fr auto' }}>
                            <StudentChoiceInputWrapper>
                              <StudentChoiceInput
                                value={ans}
                                onChange={(e) => {
                                  const next = [...studentShortAnswers];
                                  next[idx] = e.target.value;
                                  setStudentShortAnswers(next);
                                }}
                                placeholder="정답을 입력하세요"
                              />
          <div style={{ display: 'flex', gap: 0, paddingLeft: 0 }}>
                                <StudentIconButton
                                  type="button"
                                  onClick={() => {
                                    const next = [...studentShortAnswers];
                                    next[idx] = '';
                                    setStudentShortAnswers(next);
                                  }}
                                  title="정답 초기화"
                                >
                                  <RefreshIcon viewBox="0 0 24 24" strokeWidth="2">
                                    <path d="M1 4v6h6" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M23 20v-6h-6" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" strokeLinecap="round" strokeLinejoin="round" />
                                  </RefreshIcon>
                                </StudentIconButton>
                                <StudentIconButton
                                  type="button"
                                  disabled={studentShortAnswers.length <= 1}
                                  onClick={() => {
                                    if (studentShortAnswers.length <= 1) return;
                                    const next = studentShortAnswers.filter((_, i) => i !== idx);
                                    setStudentShortAnswers(next);
                                  }}
                                  title="정답 삭제"
                                >
                                  <TrashIcon viewBox="0 0 24 24" strokeWidth="2">
                                    <path d="M3 6h18" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M10 11v6" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M14 11v6" strokeLinecap="round" strokeLinejoin="round" />
                                  </TrashIcon>
                                </StudentIconButton>
                              </div>
                            </StudentChoiceInputWrapper>
                          </StudentChoiceRow>
                        ))}
                        <StudentAddAnswerBox
                          type="button"
                          onClick={() => setStudentShortAnswers((prev) => [...prev, ''])}
                        >
                          + 정답 추가
                        </StudentAddAnswerBox>
                      </>
                    )}
                    {studentQuestionType === 'essay' && (
                      <StudentCreateTextArea
                        value={studentEssayGuide}
                        onChange={(e) => setStudentEssayGuide(e.target.value)}
                        placeholder="답변 가이드를 입력하세요 (선택사항)"
                        rows={4}
                      />
                    )}
                    </StudentCreatePanel>

                    <StudentCreateActions style={{ marginTop: 20 }}>
                      <StudentCreateSubmit
                        onClick={async () => {
                          const targetSetId = studentCreateSetId || activeQuizSetId;
                          if (!isChatOpen || !targetSetId || !resultData?.classInfo?.classCode) return;
                          const trimmedQuestion = studentQuestionText.trim();
                          if (!trimmedQuestion) {
                            showToastMessage('문제를 입력해주세요.');
                            return;
                          }
                          if (studentQuestionType === 'choice') {
                            if (studentChoiceAnswers.length === 0) {
                              showToastMessage('선택형 문제는 정답을 1개 이상 선택해주세요.');
                              return;
                            }
                            const hasValidChoice = studentChoiceAnswers.some(
                              (idx) => studentChoices[idx]?.trim() !== ''
                            );
                            if (!hasValidChoice) {
                              showToastMessage('선택형 문제는 정답으로 선택한 보기에 내용을 입력해주세요.');
                              return;
                            }
                          }
                          if (studentQuestionType === 'short') {
                            const hasAnswer = studentShortAnswers.some(
                              (a) => (typeof a === 'string' ? a.trim() : '') !== ''
                            );
                            if (!hasAnswer) {
                              showToastMessage('단답형 문제는 정답을 1개 이상 입력해주세요.');
                              return;
                            }
                          }
                          // 세트 문항 개수 확인
                          let existingQuizzes: ChatQuiz[] = [];
                          try {
                            const setDoc = await chatQuizService.getQuizSetById(resultData.classInfo.classCode, targetSetId);
                            if (setDoc?.quizzes) {
                              existingQuizzes = Object.values(setDoc.quizzes as any).sort(
                                (a: any, b: any) => (a.order ?? 0) - (b.order ?? 0)
                              ) as ChatQuiz[];
                            }
                          } catch (err) {
                            console.error('❌ 세트 로드 실패(학생 문제 추가)', err);
                          }
                          if (existingQuizzes.length >= 30) {
                            showToastMessage('문제가 가득차 더이상 제출할 수 없습니다.');
                            return;
                          }

                          const newQuizId = `quiz_${Date.now()}`;
                          const now = Date.now();
                          const baseQuiz: any = {
                            id: newQuizId,
                            type: studentQuestionType,
                            question: trimmedQuestion,
                            createdAt: now,
                            updatedAt: now,
                            order: existingQuizzes.length,
                            defaultMultiAnswerMode: false,
                            defaultPublicMode: false,
                            responses: {},
                            answeredStudents: [],
                            createdByStudentId: effectiveStudentId,
                          };

                          let newQuiz;
                          if (studentQuestionType === 'choice') {
                            const normalizedChoices = studentChoices.map((c, idx) => ({
                              id: `choice_${newQuizId}_${idx}`,
                              text: c.trim(),
                            }));
                            const correctAnswers = studentChoiceAnswers
                              .filter((v) => Number.isFinite(v))
                              .map((v) => Math.max(0, Math.min(v, normalizedChoices.length - 1)));
                            newQuiz = {
                              ...baseQuiz,
                              choices: normalizedChoices,
                              correctAnswers,
                            };
                          } else if (studentQuestionType === 'short') {
                            const trimmedList = studentShortAnswers
                              .map((a) => (typeof a === 'string' ? a.trim() : ''))
                              .filter((a) => a);
                            newQuiz = {
                              ...baseQuiz,
                              answers: trimmedList,
                            };
                          } else {
                            // essay
                            newQuiz = {
                              ...baseQuiz,
                              guide: studentEssayGuide.trim(),
                            };
                          }

                          try {
                            await chatQuizService.updateQuizSet(resultData.classInfo.classCode, targetSetId, {
                              quizzes: [...existingQuizzes, newQuiz],
                            });
                            const updatedSet = await chatQuizService.getQuizSetById(resultData.classInfo.classCode, targetSetId);
                            if (updatedSet?.quizzes) {
                              const nextQuizzes = Object.values(updatedSet.quizzes || {}).sort(
                                (a: any, b: any) => (a.order ?? 0) - (b.order ?? 0)
                              ) as ChatQuiz[];
                              setSetQuizzes(nextQuizzes);
                            }
                            setStudentQuestionText('');
                            setStudentChoices(['', '', '', '']);
                            setStudentChoiceAnswers([]);
                            setStudentShortAnswers(['']);
                            setStudentEssayGuide('');
                            setIsStudentCreateOpen(false);
                            setHasSubmittedStudentCreate(true);
                            showToastMessage('학생이 만든 문제가 세트에 추가되었습니다.');
                          } catch (error) {
                            console.error('❌ 학생 문제 추가 실패:', error);
                            showToastMessage('문제를 추가하지 못했습니다.');
                          }
                        }}
                      >
                        문제 출제하기
                      </StudentCreateSubmit>
                    </StudentCreateActions>
                  </>
                )}
              </>
            ) : isSetMode && setQuizzes.length > 0 ? (
              <>
                {setTitle && (
                  <div style={{ marginBottom: 10, display: 'flex', justifyContent: 'center' }}>
                    <SetTitleBadge>{setTitle}</SetTitleBadge>
                  </div>
                )}
                <ThoughtGuideMessage>
                  문제세트가 제시되었습니다.{"\n"}
                  문항에 답변한 뒤 제출하기를 눌러주세요.{"\n"}
                  제출 후에는 응답을 추가, 수정할 수 없습니다.
                </ThoughtGuideMessage>
                <SetQuizListContainer>
                  {setQuizzes.map((quiz, idx) => {
                    const ans = setAnswers[quiz.id];
                    // 문제세트 재생 시 선택형 문제는 항상 복수 선택 가능
                    // defaultMultiAnswerMode는 응답 제출 횟수 제한과 관련된 것이고, 선택지 선택과는 무관
                    const isMulti = quiz.type === 'choice' ? true : (quiz.defaultMultiAnswerMode ?? false);
                    const status = setQuizStatuses[quiz.id];
                    const isDisabled = isSetSubmitted || !!status?.submitted;
                    const pillStatus =
                      status?.isCorrect === true
                        ? 'correct'
                        : status?.isCorrect === false
                        ? 'wrong'
                        : 'neutral';
                    return (
                      <SetQuizCard key={quiz.id}>
                        <SetQuizTitle>
                          {idx + 1}. {quiz.question}
                        </SetQuizTitle>
                        {quiz.questionImageUrl && quiz.questionImageUrl.trim() && (
                          <SetQuizImageBox>
                            <SetQuizImage 
                              src={quiz.questionImageUrl} 
                              alt="질문 이미지"
                            />
                          </SetQuizImageBox>
                        )}
                        {status?.submitted && (
                          <SetStatusBadge $status={pillStatus}>
                            {pillStatus === 'correct' ? (
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                <circle cx="12" cy="12" r="9" stroke="currentColor" fill="none" />
                              </svg>
                            ) : pillStatus === 'wrong' ? (
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            ) : (
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                <circle cx="12" cy="12" r="9" stroke="currentColor" fill="none" />
                              </svg>
                            )}
                          </SetStatusBadge>
                        )}
                        {quiz.type === 'choice' && quiz.choices ? (
                          <SetChoiceList>
                            {quiz.choices.map((choice, cIdx) => {
                              const selected = Array.isArray(ans) ? ans.includes(cIdx) : false;
                              const isCorrectChoice =
                                Array.isArray(quiz.correctAnswers) &&
                                quiz.correctAnswers.includes(cIdx);
                              const showChoiceStatus = isSetSubmitted || status?.submitted;
                              return (
                                <SetChoiceButton
                                  key={choice.id}
                                  $selected={selected}
                                  onClick={() => !isDisabled && handleSetChoiceToggle(quiz.id, cIdx, isMulti)}
                                  disabled={isDisabled}
                                >
                              <SetChoiceNumber $levelIndex={cIdx + 1}>
                                {selected && (
                                  <SetChoiceSelectedBadge>
                                    <svg viewBox="0 0 24 24">
                                      <path d="M5 13l4 4L19 7" />
                                    </svg>
                                  </SetChoiceSelectedBadge>
                                )}
                                {cIdx + 1}
                              </SetChoiceNumber>
                              <SetChoiceText>{choice.text || '(선택지 없음)'}</SetChoiceText>
                                </SetChoiceButton>
                              );
                            })}
                          </SetChoiceList>
                        ) : (
                          <SetTextInput
                            value={typeof ans === 'string' ? ans : ''}
                            onChange={(e) => handleSetTextChange(quiz.id, e.target.value)}
                            placeholder="답변을 입력하세요"
                            readOnly={isDisabled}
                          />
                        )}
                        {quiz.type === 'essay' && quiz.guide && (
                          <QuizGuideMessage style={{ marginTop: 8 }}>{quiz.guide}</QuizGuideMessage>
                        )}
                      </SetQuizCard>
                    );
                  })}
                </SetQuizListContainer>
                <SetSubmitButton
                  onClick={handleSubmitSetAnswers}
                  disabled={isSubmittingSet || isSetSubmitted}
                >
                  {isSubmittingSet
                    ? '제출 중...'
                    : isSetSubmitted
                      ? '제출 완료'
                      : '제출하기'}
                </SetSubmitButton>
              </>
            ) : (
              <>
            <QuizFlowDashedSection>
            <QuizPanelSectionTitleWrap>
              <QuizPanelSectionTitle>퀴즈</QuizPanelSectionTitle>
            </QuizPanelSectionTitleWrap>
            {activeQuiz ? (
              <>
                <ThoughtGuideMessage>
                  {activeQuiz.question}
                </ThoughtGuideMessage>
                {activeQuiz.questionImageUrl && activeQuiz.questionImageUrl.trim() && (
                  <StudentQuizImageBox>
                    <StudentQuizImage 
                      src={activeQuiz.questionImageUrl} 
                      alt="질문 이미지"
                      onError={(e) => {
                        console.error('❌ StudentChatPage - 이미지 로드 실패:', activeQuiz.questionImageUrl);
                      }}
                      onLoad={() => {
                        console.log('✅ StudentChatPage - 이미지 로드 성공:', activeQuiz.questionImageUrl);
                      }}
                    />
                  </StudentQuizImageBox>
                )}
              </>
            ) : (
              <>
                <ThoughtGuideMessage>
                  클래스 챗 대화 대기 중입니다.
                </ThoughtGuideMessage>
                <TypingIndicatorWaiting aria-label="대화 대기 중">
                  <span />
                  <span />
                  <span />
                </TypingIndicatorWaiting>
              </>
            )}
            {/* 입력 영역 아래로 응답 표시 (최신 응답이 위) */}

            {showInputField && (
              <>
                {activeQuiz && activeQuiz.type === 'choice' && activeQuiz.choices ? (
                  <>
                  {/* 선택형: 메시지 창 안에 선택지 버튼 표시 */}
                  <ChatInputContainer $isChoiceMode={true}>
                    <ChoiceOptionsContainer $hasSendButton={true}>
                      {activeQuiz.choices.map((choice, index) => {
                        const isSelected = choiceAnswer.includes(index);
                        // 선택 버튼 번호는 1번부터 시작하므로, index + 1을 levelIndex로 사용
                        // 1번 버튼(index 0)은 levelColors[1] 사용, 2번 버튼(index 1)은 levelColors[2] 사용
                        const levelIndex = index < 6 ? index + 1 : undefined;
                        
                        return (
                          <ChoiceButton
                        key={choice.id}
                            $levelIndex={levelIndex}
                            $isSelected={isSelected}
                            onClick={() => {
                              if (isInputDisabled) {
                                return;
                              }
                              const newChoiceAnswer = choiceAnswer.includes(index)
                                ? choiceAnswer.filter((i) => i !== index)
                                : [...choiceAnswer, index];
                              
                              setChoiceAnswer(newChoiceAnswer);
                            }}
                            disabled={isInputDisabled}
                          >
                            <ChoiceButtonNumber $levelIndex={levelIndex}>
                              {index + 1}
                              {isSelected && (
                                <ChoiceSelectedBadge>
                                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                    <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                </ChoiceSelectedBadge>
                              )}
                            </ChoiceButtonNumber>
                            <ChoiceButtonText>{choice.text}</ChoiceButtonText>
                          </ChoiceButton>
                        );
                      })}
                    </ChoiceOptionsContainer>
                    <SendIconButton
                      $show={true}
                      $isChoiceMode={true}
                      disabled={choiceAnswer.length === 0 || isInputDisabled || isSubmitting}
                onClick={async () => {
                  if (!activeQuiz || !activeQuizId || !resultData?.classInfo?.classCode || !effectiveStudentId) {
                    return;
                  }

                        if (hasReachedMultiAnswerLimit) {
                          alert('멀티 응답은 최대 5개까지 제출할 수 있습니다.');
                          return;
                        }

                        if (choiceAnswer.length === 0 || isInputDisabled) {
                          return;
                        }

                        // 중복 제출 방지
                        if (isSubmitting) {
                          return;
                        }

                        setIsSubmitting(true);

                  // 퀴즈 제출 로직
                  try {
                          const submitTimestamp = Date.now();
                          console.log('📤 선택형 답변 제출 시작:', {
                            classCode: resultData.classInfo.classCode,
                            quizId: activeQuizId,
                            studentId: effectiveStudentId,
                            choiceAnswer,
                            isMultiAnswerMode,
                            activeQuiz: activeQuiz ? '있음' : '없음'
                          });
                          
                          // quiz 객체에서 필요한 필드만 추출 (메모리 최적화 및 모바일 호환성)
                          const quizForSubmission: ChatQuiz = {
                            id: activeQuiz!.id,
                            type: activeQuiz!.type,
                            question: activeQuiz!.question,
                            choices: activeQuiz!.choices,
                            correctAnswers: activeQuiz!.correctAnswers,
                            answers: activeQuiz!.answers,
                            guide: activeQuiz!.guide,
                            createdAt: activeQuiz!.createdAt,
                            updatedAt: activeQuiz!.updatedAt,
                            order: activeQuiz!.order,
                            defaultMultiAnswerMode: activeQuiz!.defaultMultiAnswerMode,
                            defaultPublicMode: activeQuiz!.defaultPublicMode,
                            responses: {},
                            answeredStudents: [],
                          };

                          const containerId =
                            activeQuizContainerId && activeQuizContainerId !== 'main'
                              ? activeQuizContainerId
                              : null;
                          if (containerId) {
                            await chatQuizService.submitResponseToSet(
                              resultData.classInfo.classCode,
                              containerId,
                              activeQuizId,
                              effectiveStudentId,
                              choiceAnswer,
                              quizForSubmission,
                              isMultiAnswerMode,
                              isGuestMode ? effectiveName : undefined,
                              studentMemberGroup
                            );
                          } else {
                            await chatQuizService.submitResponse(
                              resultData.classInfo.classCode,
                              activeQuizId,
                              effectiveStudentId,
                              choiceAnswer,
                              quizForSubmission,
                              isMultiAnswerMode,
                              isGuestMode ? effectiveName : undefined,
                              studentMemberGroup
                            );
                          }
                          
                          console.log('✅ 선택형 답변 제출 완료');
                          setHasSubmittedAnswer(true); // 답변 제출 완료 표시
                          setChoiceAnswer([]);
                          await chatQuizService.updateTypingStatus(
                            resultData.classInfo.classCode,
                            activeQuizId,
                            effectiveStudentId,
                            false,
                            isMultiAnswerMode
                          );
                        } catch (error) {
                          handleSubmissionError(error);
                        } finally {
                          setIsSubmitting(false);
                        }
                      }}
                    >
                      <svg viewBox="0 0 24 24">
                        <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                      </svg>
                    </SendIconButton>
                  </ChatInputContainer>
                  {!isMultiAnswerMode && hasSubmittedAnswer ? (
                    <QuizSubmitCompleteNotice role="status" aria-live="polite">
                      응답이 제출 완료되었습니다.
                    </QuizSubmitCompleteNotice>
                  ) : isMultiAnswerMode && hasReachedMultiAnswerLimit ? (
                    <QuizSubmitCompleteNotice role="status" aria-live="polite">
                      모든 응답이 제출완료 되었습니다.
                    </QuizSubmitCompleteNotice>
                  ) : activeQuiz && !isInputDisabled ? (
                    <QuizSubmitPromptNotice role="note">
                      응답을 제출해 주세요
                    </QuizSubmitPromptNotice>
                  ) : null}
                  </>
                ) : (
                  <>
                  {/* 단답형/서술형: 채팅 스타일 입력 필드 */}
                  <ChatInputContainer>
                    <ChatInputField
                      type="text"
                    value={thoughtText}
                      $hasSendButton={
                        activeQuiz?.type === 'short'
                          ? !!activeQuiz
                          : thoughtText.trim().length > 0 && !!activeQuiz && !isInputDisabled
                      }
                    onChange={(e) => {
                      setThoughtText(e.target.value);
                    }}
                      placeholder={
                        !activeQuiz
                          ? "대화가 시작되면 활성화 됩니다."
                          : isInputDisabled
                            ? disabledPlaceholderText
                            : "메시지 보내기..."
                      }
                      disabled={!activeQuiz || isInputDisabled}
                      onKeyDown={async (e) => {
                        if (e.key === 'Enter' && !e.shiftKey && thoughtText.trim() && activeQuiz) {
                          e.preventDefault();
                          
                          if (!activeQuizId || !resultData?.classInfo?.classCode || !effectiveStudentId) {
                            console.log('생각 제출:', thoughtText);
                        return;
                      }

                          if (hasReachedMultiAnswerLimit) {
                            alert('멀티 응답은 최대 5개까지 제출할 수 있습니다.');
                        return;
                      }

                          if (isSubmitting || isInputDisabled) {
                    return;
                  }

                          setIsSubmitting(true);

                          try {
                            const answer = thoughtText.trim();
                            const submitTimestamp = Date.now();
                            
                            console.log('📤 단답형/서술형 답변 제출 시작 (Enter):', {
                              classCode: resultData.classInfo.classCode,
                              quizId: activeQuizId,
                            studentId: effectiveStudentId,
                              answer,
                              isMultiAnswerMode,
                              activeQuiz: activeQuiz ? '있음' : '없음'
                            });

                            // quiz 객체에서 필요한 필드만 추출 (메모리 최적화 및 모바일 호환성)
                            const quizForSubmission: ChatQuiz = {
                              id: activeQuiz!.id,
                              type: activeQuiz!.type,
                              question: activeQuiz!.question,
                              choices: activeQuiz!.choices,
                              correctAnswers: activeQuiz!.correctAnswers,
                              answers: activeQuiz!.answers,
                              guide: activeQuiz!.guide,
                              createdAt: activeQuiz!.createdAt,
                              updatedAt: activeQuiz!.updatedAt,
                              order: activeQuiz!.order,
                              defaultMultiAnswerMode: activeQuiz!.defaultMultiAnswerMode,
                              defaultPublicMode: activeQuiz!.defaultPublicMode,
                              responses: {},
                              answeredStudents: [],
                            };
                            
                            const containerId =
                              activeQuizContainerId && activeQuizContainerId !== 'main'
                                ? activeQuizContainerId
                                : null;
                            if (containerId) {
                              await chatQuizService.submitResponseToSet(
                                resultData.classInfo.classCode,
                                containerId,
                                activeQuizId,
                                effectiveStudentId,
                                answer,
                                quizForSubmission,
                                isMultiAnswerMode,
                                isGuestMode ? effectiveName : undefined,
                                studentMemberGroup
                              );
                            } else {
                              await chatQuizService.submitResponse(
                                resultData.classInfo.classCode,
                                activeQuizId,
                                effectiveStudentId,
                                answer,
                                quizForSubmission,
                                isMultiAnswerMode,
                                isGuestMode ? effectiveName : undefined,
                                studentMemberGroup
                              );
                            }
                            
                            console.log('✅ 단답형/서술형 답변 제출 완료 (Enter)');
                            setHasSubmittedAnswer(true); // 답변 제출 완료 표시
                            
                            setThoughtText('');
                            await chatQuizService.updateTypingStatus(
                              resultData.classInfo.classCode,
                              activeQuizId,
                              effectiveStudentId,
                              false,
                              isMultiAnswerMode
                            );
                          } catch (error) {
                            handleSubmissionError(error);
                          } finally {
                            setIsSubmitting(false);
                          }
                        }
                      }}
                    />
                    <SendIconButton
                      $show={
                        activeQuiz?.type === 'short'
                          ? !!activeQuiz
                          : (thoughtText.trim().length > 0 && !!activeQuiz) || isInputDisabled
                      }
                      onClick={async () => {
                        if (!activeQuiz || !activeQuizId || !resultData?.classInfo?.classCode || !effectiveStudentId) {
                          console.log('생각 제출:', thoughtText);
                        return;
                      }

                      if (thoughtText.trim() === '') {
                          alert('답변을 입력하세요.');
                        return;
                      }

                      // 단답형의 경우 정답이 입력되어야 제출 가능
                      if (activeQuiz.type === 'short' && thoughtText.trim() === '') {
                        showToastMessage('답변을 입력해주세요.');
                        return;
                      }

                        if (hasReachedMultiAnswerLimit) {
                          alert('멀티 응답은 최대 5개까지 제출할 수 있습니다.');
                          return;
                        }

                        if (isInputDisabled) {
                          return;
                        }

                        // 중복 제출 방지
                        if (isSubmitting) {
                          return;
                        }

                        setIsSubmitting(true);

                        // 퀴즈 제출 로직
                        try {
                          const answer = thoughtText.trim();
                          const submitTimestamp = Date.now();
                          
                          console.log('📤 단답형/서술형 답변 제출 시작 (버튼):', {
                            classCode: resultData.classInfo.classCode,
                            quizId: activeQuizId,
                            studentId: effectiveStudentId,
                            answer,
                            isMultiAnswerMode,
                            activeQuiz: activeQuiz ? '있음' : '없음'
                          });

                            // quiz 객체에서 필요한 필드만 추출 (메모리 최적화 및 모바일 호환성)
                            const quizForSubmission: ChatQuiz = {
                              id: activeQuiz!.id,
                              type: activeQuiz!.type,
                              question: activeQuiz!.question,
                              choices: activeQuiz!.choices,
                              correctAnswers: activeQuiz!.correctAnswers,
                              answers: activeQuiz!.answers,
                              guide: activeQuiz!.guide,
                              createdAt: activeQuiz!.createdAt,
                              updatedAt: activeQuiz!.updatedAt,
                              order: activeQuiz!.order,
                              defaultMultiAnswerMode: activeQuiz!.defaultMultiAnswerMode,
                              defaultPublicMode: activeQuiz!.defaultPublicMode,
                              responses: {},
                              answeredStudents: [],
                            };

                            const containerId =
                              activeQuizContainerId && activeQuizContainerId !== 'main'
                                ? activeQuizContainerId
                                : null;
                            if (containerId) {
                              await chatQuizService.submitResponseToSet(
                                resultData.classInfo.classCode,
                                containerId,
                                activeQuizId,
                                effectiveStudentId,
                                answer,
                                quizForSubmission,
                                isMultiAnswerMode,
                                isGuestMode ? effectiveName : undefined,
                                studentMemberGroup
                              );
                            } else {
                              await chatQuizService.submitResponse(
                                resultData.classInfo.classCode,
                                activeQuizId,
                                effectiveStudentId,
                                answer,
                                quizForSubmission,
                                isMultiAnswerMode,
                                isGuestMode ? effectiveName : undefined,
                                studentMemberGroup
                              );
                            }

                          console.log('✅ 단답형/서술형 답변 제출 완료 (버튼)');
                          setHasSubmittedAnswer(true); // 답변 제출 완료 표시
                          setThoughtText('');
                    await chatQuizService.updateTypingStatus(
                      resultData.classInfo.classCode,
                      activeQuizId,
                      effectiveStudentId,
                            false,
                            isMultiAnswerMode
                    );
                  } catch (error) {
                          handleSubmissionError(error);
                        } finally {
                          setIsSubmitting(false);
                        }
                      }}
                      disabled={thoughtText.trim().length === 0 || !activeQuiz || isInputDisabled}
                    >
                      <svg viewBox="0 0 24 24">
                        <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                      </svg>
                    </SendIconButton>
                  </ChatInputContainer>
                  {!isMultiAnswerMode &&
                  hasSubmittedAnswer &&
                  (activeQuiz?.type === 'short' || activeQuiz?.type === 'essay') ? (
                    <QuizSubmitCompleteNotice role="status" aria-live="polite">
                      응답이 제출 완료되었습니다.
                    </QuizSubmitCompleteNotice>
                  ) : isMultiAnswerMode &&
                    hasReachedMultiAnswerLimit &&
                    (activeQuiz?.type === 'short' || activeQuiz?.type === 'essay') ? (
                    <QuizSubmitCompleteNotice role="status" aria-live="polite">
                      모든 응답이 제출완료 되었습니다.
                    </QuizSubmitCompleteNotice>
                  ) : activeQuiz &&
                    !isInputDisabled &&
                    (activeQuiz.type === 'short' || activeQuiz.type === 'essay') ? (
                    <QuizSubmitPromptNotice role="note">
                      응답을 제출해 주세요
                    </QuizSubmitPromptNotice>
                  ) : null}

                {/* 서술형 가이드 메시지 */}
                {activeQuiz?.type === 'essay' && activeQuiz.guide && (
                  <QuizGuideMessage>{activeQuiz.guide}</QuizGuideMessage>
                )}
                  </>
                )}
              </>
            )}
            </QuizFlowDashedSection>

            {(submittedAnswers.length > 0 ||
              (isPublicMode && hasSubmittedAnswer && otherStudentsAnswers.length > 0)) && (
              <QuizSubmittedDashedSection>
                <QuizPanelSectionTitleWrap>
                  <QuizPanelSectionTitle as="h3">퀴즈 응답</QuizPanelSectionTitle>
                </QuizPanelSectionTitleWrap>
                {submittedAnswers.map((answer) => (
                  <SubmittedAnswerBubble key={answer.id}>
                    <SubmittedAnswerContent>
                      {answer.type === 'choice' && Array.isArray(answer.answer) ? (
                        <SubmittedChoiceBadgesContainer>
                          {[...answer.answer].sort((a, b) => a - b).map((index) => {
                            const levelIndex = index < 6 ? index + 1 : undefined;
                            return (
                              <SubmittedChoiceBadge key={index} $levelIndex={levelIndex}>
                                {index + 1}
                              </SubmittedChoiceBadge>
                            );
                          })}
                        </SubmittedChoiceBadgesContainer>
                      ) : (
                        <SubmittedAnswerText>{answer.answer}</SubmittedAnswerText>
                      )}
                    </SubmittedAnswerContent>
                    <SubmittedAnswerProfileContainer>
                      <SubmittedAnswerProfileName>
                        {resultData?.student?.basicInfo?.name || '나'}
                      </SubmittedAnswerProfileName>
                      <SubmittedAnswerProfile>
                        <CachedProfileImage
                          imageId={
                            (studentInfo || resultData?.student)?.profileImage ||
                            (studentInfo || resultData?.student)?.basicInfo?.profileImage ||
                            'basic-1'
                          }
                          alt="프로필"
                        />
                      </SubmittedAnswerProfile>
                    </SubmittedAnswerProfileContainer>
                  </SubmittedAnswerBubble>
                ))}
                {isPublicMode &&
                  hasSubmittedAnswer &&
                  otherStudentsAnswers.map((answer) => (
                    <SubmittedAnswerBubble key={answer.id} $isOtherStudent={true}>
                      <SubmittedAnswerProfileContainer>
                        <SubmittedAnswerProfileName>{answer.studentName}</SubmittedAnswerProfileName>
                        <SubmittedAnswerProfile>
                          <CachedProfileImage imageId={answer.studentProfileImage} alt={answer.studentName} />
                        </SubmittedAnswerProfile>
                      </SubmittedAnswerProfileContainer>
                      <SubmittedAnswerContent $isOtherStudent={true}>
                        {answer.type === 'choice' && Array.isArray(answer.answer) ? (
                          <SubmittedChoiceBadgesContainer>
                            {[...answer.answer].sort((a, b) => a - b).map((index) => {
                              const levelIndex = index < 6 ? index + 1 : undefined;
                              return (
                                <SubmittedChoiceBadge key={index} $levelIndex={levelIndex}>
                                  {index + 1}
                                </SubmittedChoiceBadge>
                              );
                            })}
                          </SubmittedChoiceBadgesContainer>
                        ) : (
                          <SubmittedAnswerText>{answer.answer}</SubmittedAnswerText>
                        )}
                      </SubmittedAnswerContent>
                    </SubmittedAnswerBubble>
                  ))}
              </QuizSubmittedDashedSection>
            )}
              </>
            )}
          </ThoughtSection>
        )}

        {selectedView === 'profile' && !isGuestMode && resultData.student && (
          <>
        <SectionBox $whiteBorder>
          <ProfileSectionTitleBox>
            <ProfileSectionTitleText>내 포인트</ProfileSectionTitleText>
          </ProfileSectionTitleBox>
        <PointsSummaryCard>
          <StudentProfileCard>
            <StudentCardLayout>
              {/* 왼쪽: 프로필 이미지 */}
              <ProfileImageArea
                style={{ 
                  cursor: 'default', 
                  pointerEvents: 'none',
                  opacity: '1',
                  transform: 'scale(1)'
                }}
              >
                <CachedProfileImage
                  imageId={effectiveProfileImageId}
                  alt="프로필"
                  fallbackImageId="basic-0"
                />
              </ProfileImageArea>
              
              {/* 중앙: 멤버 정보 */}
              <InfoArea>
                <InfoTopRow>
                    <StudentInfoGroup>
                  <StudentNumber>{effectiveNumberLabel}{!isGuestMode && '번'}</StudentNumber>
                  <StudentName>{effectiveName}</StudentName>
                    </StudentInfoGroup>
                    <LevelDisplayBox 
                      $levelColor={getLevelColor(profileLevelInfo.level)}
                      $level={profileLevelInfo.level}
                    >
                      {profileLevelInfo.level}
                    </LevelDisplayBox>
                </InfoTopRow>
                  <CoinInfoRow>
                    <CoinIcon>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="10" fill="#f39c12"/>
                        <circle cx="12" cy="12" r="8" fill="#f1c40f"/>
                        <text x="12" y="16" fontSize="12" fontWeight="bold" fill="#e67e22" textAnchor="middle">₩</text>
                      </svg>
                    </CoinIcon>
                    <MultiplySign>×</MultiplySign>
                    {!isGuestMode && (
                    <CoinAmount>
                      {calculateTotalCoins((studentInfo || resultData.student).basicInfo?.coinsHistory)}
                    </CoinAmount>
                    )}
                  </CoinInfoRow>
                <TotalPointsDisplay $digitCount={displayTotalPoints.toString().length}>
                  {displayTotalPoints.toString().padStart(3, '0')}
                </TotalPointsDisplay>
              </InfoArea>
              
                {/* 오른쪽: 레벨 진행 막대 (세로) */}
              <RightArea>
                  <LevelProgressBar 
                    $progress={profileLevelInfo.progress}
                    $levelColor={getLevelColor(profileLevelInfo.level)}
                    $level={profileLevelInfo.level}
                  />
              </RightArea>
            </StudentCardLayout>
          </StudentProfileCard>
          
            <PointsGrid>
              <PointItem
                onClick={(e) => {
                  e.stopPropagation();
                  setIsAssignmentHistoryModalOpen(true);
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsAssignmentHistoryModalOpen(true);
                  }
                }}
              >
                <PointLabel>과제 포인트</PointLabel>
                <PointValue>{(studentInfo || resultData.student).assignmentPoints}점</PointValue>
              </PointItem>
              <PointItem
                onClick={(e) => {
                  e.stopPropagation();
                  setIsPositiveHistoryModalOpen(true);
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsPositiveHistoryModalOpen(true);
                  }
                }}
              >
                <PointLabel>긍정행동 포인트</PointLabel>
                <PointValue>{(studentInfo || resultData.student).positivePoints}점</PointValue>
              </PointItem>
            </PointsGrid>
                <CoinBlock
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsCoinHistoryModalOpen(true);
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsCoinHistoryModalOpen(true);
                    }
                  }}
                >
                  <CoinBlockLabel>보유 코인</CoinBlockLabel>
                  <CoinBlockValue>
                    <CoinBlockIconWrapper>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="10" fill="#f39c12"/>
                        <circle cx="12" cy="12" r="8" fill="#f1c40f"/>
                        <text x="12" y="16" fontSize="12" fontWeight="bold" fill="#e67e22" textAnchor="middle">₩</text>
                      </svg>
                    </CoinBlockIconWrapper>
                    <CoinBlockText>×</CoinBlockText>
                    <CoinBlockText>
                      {calculateTotalCoins((studentInfo || resultData.student).basicInfo?.coinsHistory)}
                    </CoinBlockText>
                  </CoinBlockValue>
                </CoinBlock>
        </PointsSummaryCard>
        </SectionBox>

        <SectionBox $whiteBorder>
          <ProfileSectionTitleBox>
            <ProfileSectionTitleText>내 보유 아이템</ProfileSectionTitleText>
          </ProfileSectionTitleBox>
          <PointsSummaryCard>
            <ItemSectionToolbarRow>
              <ShowExhaustedLeftGroup>
                <ShowExhaustedLabel>사용 완료 아이템 카드 보이기</ShowExhaustedLabel>
                <ShowExhaustedToggle
                  type="button"
                  $isEnabled={showExhaustedItemCards}
                  onClick={toggleShowExhaustedItemCards}
                  aria-pressed={showExhaustedItemCards}
                >
                  <ShowExhaustedToggleOption $isActive={!showExhaustedItemCards}>OFF</ShowExhaustedToggleOption>
                  <ShowExhaustedToggleOption $isActive={showExhaustedItemCards}>ON</ShowExhaustedToggleOption>
                </ShowExhaustedToggle>
              </ShowExhaustedLeftGroup>
              {!isGuestMode && (
                <StoreButton 
                  onClick={handleOpenStoreModal} 
                  title="스토어"
                  $disabled={!storeIndividualPurchaseEnabled}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="var(--store-icon-color)" width="20" height="20">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 21v-7.5a.75.75 0 01.75-.75h3a.75.75 0 01.75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349m-16.5 11.65V9.35m0 0a3.001 3.001 0 003.75-.615A2.993 2.993 0 009.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 002.25 1.016c.896 0 1.7-.393 2.25-1.016a3.001 3.001 0 003.75.614m-16.5 0a3.004 3.004 0 01-.621-4.72L4.318 3.44A1.5 1.5 0 015.378 3h13.243a1.5 1.5 0 011.06.44l1.19 1.189a3 3 0 01-.621 4.72m-13.5 8.65h3.75a.75.75 0 00.75-.75V13.5a.75.75 0 00-.75-.75H6.75a.75.75 0 00-.75.75v3.75c0 .415.336.75.75.75z" />
                  </svg>
                </StoreButton>
              )}
            </ItemSectionToolbarRow>
            
            {isLoadingItemCards ? (
              <LoadingState>아이템 카드를 불러오는 중...</LoadingState>
            ) : (
              <>
                {/* 레벨업 카드 */}
                {(() => {
                  const levelupCardDataList = studentItemCards
                        .filter(card => card.category === 'levelup')
                        .map((studentCard) => {
                          const itemCard = itemCards.find(ic => ic.itemCardId === studentCard.itemCardId);
                          return itemCard ? { ...itemCard, studentCard } : null;
                        })
                    .filter(Boolean) as any[];
                  const isExhaustedCard = (c: any) => c.type === 'active' && (c.studentCard?.remainingUseCount ?? 0) === 0;
                  const levelupVisibleList = (showExhaustedItemCards ? levelupCardDataList : levelupCardDataList.filter(c => !isExhaustedCard(c)))
                    .filter(c => c.studentCard?.isLocked !== true);
                  return levelupVisibleList.length > 0 ? (
                  <ItemCardCategorySection>
                    <CategoryTitle>
                      레벨업 카드 <span className="count">{levelupVisibleList.length}개</span>
                    </CategoryTitle>
                    <ItemCardScrollContainer>
                      {levelupVisibleList
                        .map((cardData: any, index: number) => {
                          const isLocked = cardData.studentCard.isLocked === true;
                          const isExhausted = cardData.type === 'active' && (cardData.studentCard.remainingUseCount || 0) === 0;
                          return (
                          <ItemCardBlock 
                            key={`${cardData.studentCard.instanceId}-${index}`}
                            $isSelected={!isLocked && !isExhausted && selectedCardInstanceId === cardData.studentCard.instanceId}
                            $isLocked={isLocked}
                            $isExhausted={!isLocked && isExhausted}
                            onClick={() => {
                              if (isLocked || isExhausted) return; // 잠금된 카드나 사용 완료된 카드는 클릭 불가
                              setSelectedCardInstanceId(
                              selectedCardInstanceId === cardData.studentCard.instanceId ? null : cardData.studentCard.instanceId
                              );
                            }}
                          >
                            <ItemCardIconArea>
                              {cardData.cardImage === 'default' || itemCardImageErrors.has(cardData.itemCardId) ? (
                                <ItemCardPlaceholderIcon>
                                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <rect x="2" y="6" width="12" height="16" rx="2" fill="#ffffff" stroke="#555" strokeWidth="1.5" transform="rotate(-10 8 14)"/>
                                    <rect x="6" y="4" width="12" height="16" rx="2" fill="#f8f8f8" stroke="#555" strokeWidth="1.5" transform="rotate(0 12 12)"/>
                                    <rect x="10" y="6" width="12" height="16" rx="2" fill="#fafafa" stroke="#555" strokeWidth="1.5" transform="rotate(10 16 14)"/>
                                  </svg>
                                </ItemCardPlaceholderIcon>
                              ) : cardData.cardImage && /^\d+$/.test(cardData.cardImage) ? (
                                <img 
                                  src={`/CardIcon/IconBasic/IconBasic-${cardData.cardImage}.png`} 
                                  alt={cardData.name} 
                                  onError={() => setItemCardImageErrors(prev => new Set(prev).add(cardData.itemCardId))}
                                />
                              ) : cardData.cardImage ? (
                                <img 
                                  src={withImageVersion(cardData.cardImage, cardData.updatedAt)}
                                  alt={cardData.name} 
                                  onError={() => setItemCardImageErrors(prev => new Set(prev).add(cardData.itemCardId))}
                                />
                              ) : cardData.icon ? (
                                <img 
                                  src={withImageVersion(cardData.icon, cardData.updatedAt)}
                                  alt={cardData.name} 
                                  onError={() => setItemCardImageErrors(prev => new Set(prev).add(cardData.itemCardId))}
                                />
                              ) : (
                                <ItemCardPlaceholderIcon>
                                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <rect x="2" y="6" width="12" height="16" rx="2" fill="#ffffff" stroke="#555" strokeWidth="1.5" transform="rotate(-10 8 14)"/>
                                    <rect x="6" y="4" width="12" height="16" rx="2" fill="#f8f8f8" stroke="#555" strokeWidth="1.5" transform="rotate(0 12 12)"/>
                                    <rect x="10" y="6" width="12" height="16" rx="2" fill="#fafafa" stroke="#555" strokeWidth="1.5" transform="rotate(10 16 14)"/>
                                  </svg>
                                </ItemCardPlaceholderIcon>
                              )}
                              {(() => {
                                const raw = cardData.studentCard?.acquiredLevel;
                                const level = raw !== undefined && raw !== null && raw !== '' ? Number(raw) : NaN;
                                if (!Number.isFinite(level)) return null;
                                return (
                                  <ProfileItemCardLevelBadge
                                    $levelColor={getLevelColor(level)}
                                    $level={level}
                                  >
                                    {level}
                                  </ProfileItemCardLevelBadge>
                                );
                              })()}
                            </ItemCardIconArea>
                            <ItemCardName>{cardData.name}</ItemCardName>
                            <ItemCardMemo>{cardData.memo}</ItemCardMemo>
                            <ItemCardMetaGroup>
                              {isLocked && (
                                <ItemCardLockedBadge>
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                                    <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="currentColor" strokeWidth="2"/>
                                  </svg>
                                  <span>잠금</span>
                                </ItemCardLockedBadge>
                              )}
                              {!isLocked && cardData.cost !== 'Nocharge' && (
                                <ItemCardCost>
                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="12" cy="12" r="10" fill="#f39c12"/>
                                    <circle cx="12" cy="12" r="8" fill="#f1c40f"/>
                                    <text x="12" y="16" fontSize="12" fontWeight="bold" fill="#e67e22" textAnchor="middle">₩</text>
                                  </svg>
                                  <span>{cardData.cost}</span>
                                </ItemCardCost>
                              )}
                              {!isLocked && cardData.type === 'active' && cardData.studentCard.remainingUseCount !== undefined && (
                                <ItemCardUseCount>
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12 21.35L10.55 20.03C5.4 15.36 2 12.27 2 8.5C2 5.41 4.42 3 7.5 3C9.24 3 10.91 3.81 12 5.08C13.09 3.81 14.76 3 16.5 3C19.58 3 22 5.41 22 8.5C22 12.27 18.6 15.36 13.45 20.03L12 21.35Z" fill="#ff4757"/>
                                  </svg>
                                  <span>×{cardData.studentCard.remainingUseCount}</span>
                                </ItemCardUseCount>
                              )}
                            </ItemCardMetaGroup>
                          </ItemCardBlock>
                          );
                        })}
                    </ItemCardScrollContainer>
                  </ItemCardCategorySection>
                  ) : null;
                })()}

                {/* 과제보상 카드 */}
                {(() => {
                  const assignmentCardDataList = studentItemCards
                        .filter(card => card.category === 'assignment')
                        .map((studentCard) => {
                          const itemCard = itemCards.find(ic => ic.itemCardId === studentCard.itemCardId);
                          return itemCard ? { ...itemCard, studentCard } : null;
                        })
                    .filter(Boolean) as any[];
                  const isExhaustedCard = (c: any) => c.type === 'active' && (c.studentCard?.remainingUseCount ?? 0) === 0;
                  const assignmentVisibleList = (showExhaustedItemCards ? assignmentCardDataList : assignmentCardDataList.filter(c => !isExhaustedCard(c)))
                    .filter(c => c.studentCard?.isLocked !== true);
                  return assignmentVisibleList.length > 0 ? (
                  <ItemCardCategorySection>
                    <CategoryTitle>
                      과제보상 카드 <span className="count">{assignmentVisibleList.length}개</span>
                    </CategoryTitle>
                    <ItemCardScrollContainer>
                      {assignmentVisibleList
                        .map((cardData: any, index: number) => {
                          const isLocked = cardData.studentCard.isLocked === true;
                          const isExhausted = cardData.type === 'active' && (cardData.studentCard.remainingUseCount || 0) === 0;
                          return (
                          <ItemCardBlock 
                            key={`${cardData.studentCard.instanceId}-${index}`}
                            $isSelected={!isLocked && !isExhausted && selectedCardInstanceId === cardData.studentCard.instanceId}
                            $isLocked={isLocked}
                            $isExhausted={!isLocked && isExhausted}
                            onClick={() => {
                              if (isLocked || isExhausted) return; // 잠금된 카드나 사용 완료된 카드는 클릭 불가
                              setSelectedCardInstanceId(
                              selectedCardInstanceId === cardData.studentCard.instanceId ? null : cardData.studentCard.instanceId
                              );
                            }}
                          >
                            <ItemCardIconArea>
                              {cardData.cardImage === 'default' || itemCardImageErrors.has(cardData.itemCardId) ? (
                                <ItemCardPlaceholderIcon>
                                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <rect x="2" y="6" width="12" height="16" rx="2" fill="#ffffff" stroke="#555" strokeWidth="1.5" transform="rotate(-10 8 14)"/>
                                    <rect x="6" y="4" width="12" height="16" rx="2" fill="#f8f8f8" stroke="#555" strokeWidth="1.5" transform="rotate(0 12 12)"/>
                                    <rect x="10" y="6" width="12" height="16" rx="2" fill="#fafafa" stroke="#555" strokeWidth="1.5" transform="rotate(10 16 14)"/>
                                  </svg>
                                </ItemCardPlaceholderIcon>
                              ) : cardData.cardImage && /^\d+$/.test(cardData.cardImage) ? (
                                <img 
                                  src={`/CardIcon/IconBasic/IconBasic-${cardData.cardImage}.png`} 
                                  alt={cardData.name} 
                                  onError={() => setItemCardImageErrors(prev => new Set(prev).add(cardData.itemCardId))}
                                />
                              ) : cardData.cardImage ? (
                                <img 
                                  src={withImageVersion(cardData.cardImage, cardData.updatedAt)}
                                  alt={cardData.name} 
                                  onError={() => setItemCardImageErrors(prev => new Set(prev).add(cardData.itemCardId))}
                                />
                              ) : cardData.icon ? (
                                <img 
                                  src={withImageVersion(cardData.icon, cardData.updatedAt)}
                                  alt={cardData.name} 
                                  onError={() => setItemCardImageErrors(prev => new Set(prev).add(cardData.itemCardId))}
                                />
                              ) : (
                                <ItemCardPlaceholderIcon>
                                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <rect x="2" y="6" width="12" height="16" rx="2" fill="#ffffff" stroke="#555" strokeWidth="1.5" transform="rotate(-10 8 14)"/>
                                    <rect x="6" y="4" width="12" height="16" rx="2" fill="#f8f8f8" stroke="#555" strokeWidth="1.5" transform="rotate(0 12 12)"/>
                                    <rect x="10" y="6" width="12" height="16" rx="2" fill="#fafafa" stroke="#555" strokeWidth="1.5" transform="rotate(10 16 14)"/>
                                  </svg>
                                </ItemCardPlaceholderIcon>
                              )}
                            </ItemCardIconArea>
                            <ItemCardName>{cardData.name}</ItemCardName>
                            <ItemCardMemo>{cardData.memo}</ItemCardMemo>
                            <ItemCardMetaGroup>
                              {isLocked && (
                                <ItemCardLockedBadge>
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                                    <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="currentColor" strokeWidth="2"/>
                                  </svg>
                                  <span>잠금</span>
                                </ItemCardLockedBadge>
                              )}
                              {!isLocked && cardData.cost !== 'Nocharge' && (
                                <ItemCardCost>
                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="12" cy="12" r="10" fill="#f39c12"/>
                                    <circle cx="12" cy="12" r="8" fill="#f1c40f"/>
                                    <text x="12" y="16" fontSize="12" fontWeight="bold" fill="#e67e22" textAnchor="middle">₩</text>
                                  </svg>
                                  <span>{cardData.cost}</span>
                                </ItemCardCost>
                              )}
                              {!isLocked && cardData.type === 'active' && cardData.studentCard.remainingUseCount !== undefined && (
                                <ItemCardUseCount>
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12 21.35L10.55 20.03C5.4 15.36 2 12.27 2 8.5C2 5.41 4.42 3 7.5 3C9.24 3 10.91 3.81 12 5.08C13.09 3.81 14.76 3 16.5 3C19.58 3 22 5.41 22 8.5C22 12.27 18.6 15.36 13.45 20.03L12 21.35Z" fill="#ff4757"/>
                                  </svg>
                                  <span>×{cardData.studentCard.remainingUseCount}</span>
                                </ItemCardUseCount>
                              )}
                            </ItemCardMetaGroup>
                          </ItemCardBlock>
                          );
                        })}
                    </ItemCardScrollContainer>
                  </ItemCardCategorySection>
                  ) : null;
                })()}

                {/* 스토어 카드 */}
                {(() => {
                  const storeCardDataList = studentItemCards
                        .filter(card => card.category === 'store')
                        .map((studentCard) => {
                          const itemCard = itemCards.find(ic => ic.itemCardId === studentCard.itemCardId);
                          return itemCard ? { ...itemCard, studentCard } : null;
                        })
                    .filter(Boolean) as any[];
                  const isExhaustedCard = (c: any) => c.type === 'active' && (c.studentCard?.remainingUseCount ?? 0) === 0;
                  const storeVisibleList = (showExhaustedItemCards ? storeCardDataList : storeCardDataList.filter(c => !isExhaustedCard(c)))
                    .filter(c => c.studentCard?.isLocked !== true);
                  return storeVisibleList.length > 0 ? (
                  <ItemCardCategorySection>
                    <CategoryTitle>
                      스토어 카드 <span className="count">{storeVisibleList.length}개</span>
                    </CategoryTitle>
                    <ItemCardScrollContainer>
                      {storeVisibleList
                        .map((cardData: any, index: number) => {
                          const isLocked = cardData.studentCard.isLocked === true;
                          const isExhausted = cardData.type === 'active' && (cardData.studentCard.remainingUseCount || 0) === 0;
                          return (
                          <ItemCardBlock 
                            key={`${cardData.studentCard.instanceId}-${index}`}
                            $isSelected={!isLocked && !isExhausted && selectedCardInstanceId === cardData.studentCard.instanceId}
                            $isLocked={isLocked}
                            $isExhausted={!isLocked && isExhausted}
                            onClick={() => {
                              if (isLocked || isExhausted) return; // 잠금된 카드나 사용 완료된 카드는 클릭 불가
                              setSelectedCardInstanceId(
                              selectedCardInstanceId === cardData.studentCard.instanceId ? null : cardData.studentCard.instanceId
                              );
                            }}
                          >
                            <ItemCardIconArea>
                              {cardData.cardImage === 'default' || itemCardImageErrors.has(cardData.itemCardId) ? (
                                <ItemCardPlaceholderIcon>
                                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <rect x="2" y="6" width="12" height="16" rx="2" fill="#ffffff" stroke="#555" strokeWidth="1.5" transform="rotate(-10 8 14)"/>
                                    <rect x="6" y="4" width="12" height="16" rx="2" fill="#f8f8f8" stroke="#555" strokeWidth="1.5" transform="rotate(0 12 12)"/>
                                    <rect x="10" y="6" width="12" height="16" rx="2" fill="#fafafa" stroke="#555" strokeWidth="1.5" transform="rotate(10 16 14)"/>
                                  </svg>
                                </ItemCardPlaceholderIcon>
                              ) : cardData.cardImage && /^\d+$/.test(cardData.cardImage) ? (
                                <img 
                                  src={`/CardIcon/IconBasic/IconBasic-${cardData.cardImage}.png`} 
                                  alt={cardData.name} 
                                  onError={() => setItemCardImageErrors(prev => new Set(prev).add(cardData.itemCardId))}
                                />
                              ) : cardData.cardImage ? (
                                <img 
                                  src={withImageVersion(cardData.cardImage, cardData.updatedAt)}
                                  alt={cardData.name} 
                                  onError={() => setItemCardImageErrors(prev => new Set(prev).add(cardData.itemCardId))}
                                />
                              ) : cardData.icon ? (
                                <img 
                                  src={withImageVersion(cardData.icon, cardData.updatedAt)}
                                  alt={cardData.name} 
                                  onError={() => setItemCardImageErrors(prev => new Set(prev).add(cardData.itemCardId))}
                                />
                              ) : (
                                <ItemCardPlaceholderIcon>
                                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <rect x="2" y="6" width="12" height="16" rx="2" fill="#ffffff" stroke="#555" strokeWidth="1.5" transform="rotate(-10 8 14)"/>
                                    <rect x="6" y="4" width="12" height="16" rx="2" fill="#f8f8f8" stroke="#555" strokeWidth="1.5" transform="rotate(0 12 12)"/>
                                    <rect x="10" y="6" width="12" height="16" rx="2" fill="#fafafa" stroke="#555" strokeWidth="1.5" transform="rotate(10 16 14)"/>
                                  </svg>
                                </ItemCardPlaceholderIcon>
                              )}
                            </ItemCardIconArea>
                            <ItemCardName>{cardData.name}</ItemCardName>
                            <ItemCardMemo>{cardData.memo}</ItemCardMemo>
                            <ItemCardMetaGroup>
                              {isLocked && (
                                <ItemCardLockedBadge>
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                                    <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="currentColor" strokeWidth="2"/>
                                  </svg>
                                  <span>잠금</span>
                                </ItemCardLockedBadge>
                              )}
                              {!isLocked && cardData.cost !== 'Nocharge' && (
                                <ItemCardCost>
                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="12" cy="12" r="10" fill="#f39c12"/>
                                    <circle cx="12" cy="12" r="8" fill="#f1c40f"/>
                                    <text x="12" y="16" fontSize="12" fontWeight="bold" fill="#e67e22" textAnchor="middle">₩</text>
                                  </svg>
                                  <span>{cardData.cost}</span>
                                </ItemCardCost>
                              )}
                              {!isLocked && cardData.type === 'active' && cardData.studentCard.remainingUseCount !== undefined && (
                                <ItemCardUseCount>
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12 21.35L10.55 20.03C5.4 15.36 2 12.27 2 8.5C2 5.41 4.42 3 7.5 3C9.24 3 10.91 3.81 12 5.08C13.09 3.81 14.76 3 16.5 3C19.58 3 22 5.41 22 8.5C22 12.27 18.6 15.36 13.45 20.03L12 21.35Z" fill="#ff4757"/>
                                  </svg>
                                  <span>×{cardData.studentCard.remainingUseCount}</span>
                                </ItemCardUseCount>
                              )}
                            </ItemCardMetaGroup>
                          </ItemCardBlock>
                          );
                        })}
                    </ItemCardScrollContainer>
                  </ItemCardCategorySection>
                  ) : null;
                })()}
              </>
            )}
          </PointsSummaryCard>
        </SectionBox>
          </>
        )}
      </Content>
      )}

      {/* 과제 제출 모달 */}
      <ModalOverlay $isOpen={isModalOpen} onClick={handleCloseModal}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <ModalCloseButton onClick={handleCloseModal}>×</ModalCloseButton>
          </ModalHeader>

          {/* 과제 정보 섹션 */}
          {selectedAssignment && (
            <ModalAssignmentInfo>
              <ModalCategoryRow>
                <ModalCategoryDisplay color={selectedAssignment.type === 'general' ? '#6c757d' : (selectedAssignment.color && String(selectedAssignment.color).trim() ? selectedAssignment.color : '#FC5555')}>
                  {selectedAssignment.type === 'general' ? '일반과제' : '프로젝트 과제'}
                </ModalCategoryDisplay>
              </ModalCategoryRow>
              <ModalAssignmentTitle>
                {selectedAssignment.title}
                {selectedAssignment.type === 'project' && selectedAssignment.subTaskTitle && (
                  <> - {selectedAssignment.subTaskTitle}</>
                )}
              </ModalAssignmentTitle>
              <ModalAssignmentMeta>
                <ModalAssignmentMetaText>
                마감: {formatDate(selectedAssignment.dueDate)} • 획득 포인트: {selectedAssignment.basePoints}점
                  {(selectedAssignment.rewardEnabled && selectedAssignment.rewardCoins && selectedAssignment.rewardCoins >= 1) || 
                   (selectedAssignment.rewardItemCardIds && selectedAssignment.rewardItemCardIds.length > 0) ? (
                    <RewardInfo>
                      {selectedAssignment.rewardEnabled && selectedAssignment.rewardCoins && selectedAssignment.rewardCoins >= 1 && (
                        <RewardItem>
                          <CoinIcon>
                            <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <circle cx="10" cy="10" r="8" fill="#FFD700" stroke="#FFA500" strokeWidth="1.5"/>
                              <circle cx="10" cy="10" r="5" fill="#FFA500" opacity="0.3"/>
                              <text x="10" y="13" fontSize="8" fontWeight="bold" fill="#FFA500" textAnchor="middle">₩</text>
                            </svg>
                          </CoinIcon>
                          <RewardText>{selectedAssignment.rewardCoins}</RewardText>
                        </RewardItem>
                      )}
                      {selectedAssignment.rewardItemCardIds && selectedAssignment.rewardItemCardIds.length > 0 && (
                        <RewardItem>
                          <ItemCardIcon>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <rect x="2" y="6" width="12" height="16" rx="2" fill="#ffffff" stroke="#555" strokeWidth="1.5" transform="rotate(-10 8 14)"/>
                              <rect x="6" y="4" width="12" height="16" rx="2" fill="#f8f8f8" stroke="#555" strokeWidth="1.5" transform="rotate(0 12 12)"/>
                              <rect x="10" y="6" width="12" height="16" rx="2" fill="#fafafa" stroke="#555" strokeWidth="1.5" transform="rotate(10 16 14)"/>
                            </svg>
                          </ItemCardIcon>
                          <RewardText>{selectedAssignment.rewardItemCardIds.length}</RewardText>
                        </RewardItem>
                      )}
                    </RewardInfo>
                  ) : null}
                </ModalAssignmentMetaText>
              </ModalAssignmentMeta>
            </ModalAssignmentInfo>
          )}

          <ModalDivider />

          {/* 과제 안내 섹션 */}
          {selectedAssignment?.notes && selectedAssignment.notes.trim() && (
            <ModalSection $centeredTitle>
              <ModalSectionTitle>과제 안내</ModalSectionTitle>
              <ModalAssignmentNotesText>
                {selectedAssignment.notes}
              </ModalAssignmentNotesText>
            </ModalSection>
          )}

          {selectedAssignment?.studentStatus?.status === 'submitted' && (
            <ExistingSubmissionInfo>
              <InfoLabel>제출일시</InfoLabel>
              <InfoValue>
                {formatSubmittedAt(selectedAssignment.studentStatus.submittedAt)}
              </InfoValue>
            </ExistingSubmissionInfo>
          )}

          {/* 과제 제출하기 섹션 */}
          <ModalSection $centeredTitle>
            <ModalSectionTitle>과제 제출하기</ModalSectionTitle>
            <ModalSectionHint>
              과제 이미지, 과제 링크, 과제 메세지 중 하나 이상을 입력하여 과제를 제출할 수 있습니다.
            </ModalSectionHint>

            {showSubmissionImageSection && (
              <FormGroup>
                <Label>과제 사진</Label>
                <SubmissionImageEditBlock>
                  {pendingImagePreviewUrl ? (
                    <SubmissionImagePreviewWrap>
                      <SubmissionImagePreview src={pendingImagePreviewUrl} alt="제출 사진 미리보기" />
                    </SubmissionImagePreviewWrap>
                  ) : !removedExistingSubmissionImage && existingSubmissionImageUrl ? (
                    <SubmissionImagePreviewWrap>
                      <SubmissionImagePreview src={existingSubmissionImageUrl} alt="제출된 과제 사진" />
                    </SubmissionImagePreviewWrap>
                  ) : !removedExistingSubmissionImage &&
                    selectedExistingSubmissionImagePath &&
                    existingSubmissionImageLoadFailed ? (
                    <SubmissionEmptyImageSlot>제출된 사진을 불러오지 못했습니다.</SubmissionEmptyImageSlot>
                  ) : (
                    <SubmissionEmptyImageSlot>제출할 과제 사진이 없습니다.</SubmissionEmptyImageSlot>
                  )}
                  {canSubmitAssignmentImage &&
                    (pendingImagePreviewUrl ||
                    (!removedExistingSubmissionImage && existingSubmissionImageUrl) ? (
                      <SubmissionImageActionRow>
                        <SubmissionImagePickBtn type="button" onClick={() => setIsSubmissionCropOpen(true)}>
                          사진 변경
                        </SubmissionImagePickBtn>
                        <SubmissionImagePickBtn type="button" onClick={handleRemoveSubmissionImage}>
                          사진 제거
                        </SubmissionImagePickBtn>
                      </SubmissionImageActionRow>
                    ) : (
                      <SubmissionImagePickBtn
                        type="button"
                        $fullWidth
                        onClick={() => setIsSubmissionCropOpen(true)}
                      >
                        사진 추가
                      </SubmissionImagePickBtn>
                    ))}
                </SubmissionImageEditBlock>
              </FormGroup>
            )}
            
            <FormGroup>
              <Label>과제 링크</Label>
              <SubmissionInput
                type="url"
                value={submissionLink}
                onChange={(e) => setSubmissionLink(e.target.value)}
                placeholder="https://..."
              />
            </FormGroup>

            <FormGroup>
              <Label>과제 메시지</Label>
              <SubmissionTextArea
                value={submissionMessage}
                onChange={(e) => setSubmissionMessage(e.target.value)}
                placeholder="코디에게 전달할 메시지를 입력하세요"
              />
            </FormGroup>
          </ModalSection>

          {/* 과제 완료 인증하기 섹션 (일반과제이고 rewardEnabled가 true인 경우만) */}
          {selectedAssignment?.type === 'general' && selectedAssignment?.rewardEnabled && selectedAssignment?.rewardCode && (
            <ModalSection $centeredTitle>
              <ModalSectionTitle>과제 완료 인증하기</ModalSectionTitle>
              
              <FormGroup>
                <Label>과제 완료 코드</Label>
                <Input
                  type="text"
                  value={completionCode}
                  onChange={(e) => {
                    setCompletionCode(e.target.value);
                    setIsCodeVerified(false); // 코드가 변경되면 검증 상태 초기화
                  }}
                  placeholder="과제 완료 인증 코드를 입력하세요."
                  disabled={isCodeVerified}
                  $centerText={true}
                />
                <VerifyButton 
                  onClick={handleVerifyCode}
                  $verified={isCodeVerified}
                  disabled={!completionCode.trim() || isCodeVerified}
                >
                  {isCodeVerified ? (
                    <>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      확인됨
                    </>
                  ) : (
                    '확인하기'
                  )}
                </VerifyButton>
              </FormGroup>
            </ModalSection>
          )}

          <ModalActions>
            <Button $variant="secondary" data-variant="modal-secondary" onClick={handleCloseModal}>
              닫기
            </Button>
            <Button 
              $variant="primary" 
              onClick={handleSubmitAssignment}
              disabled={
                submitting ||
                (!isCodeVerified &&
                  !submissionLink.trim() &&
                  !submissionMessage.trim() &&
                  !pendingSubmissionImage)
              }
            >
              {submitting ? '제출 중...' : '제출하기'}
            </Button>
          </ModalActions>
        </ModalContent>
      </ModalOverlay>

      {/* 전체공지노트 열람 모달 (학생용) */}
      <ModalOverlay
        $isOpen={isGeneralNoticeModalOpen}
        onClick={() => setIsGeneralNoticeModalOpen(false)}
      >
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle>전체공지노트</ModalTitle>
            <ModalCloseButton onClick={() => setIsGeneralNoticeModalOpen(false)}>×</ModalCloseButton>
          </ModalHeader>

          {isLoadingGeneralNoticeNotes ? (
            <LoadingState>불러오는 중...</LoadingState>
          ) : generalNoticeNotes.length === 0 ? (
            <EmptyState>등록된 전체공지노트가 없습니다</EmptyState>
          ) : (
            <>
              {(() => {
                const totalPages = Math.max(1, Math.ceil(generalNoticeNotes.length / GENERAL_NOTICE_PER_PAGE));
                const safePage = Math.min(Math.max(generalNoticePage, 1), totalPages);
                const startIndex = (safePage - 1) * GENERAL_NOTICE_PER_PAGE;
                const endIndex = startIndex + GENERAL_NOTICE_PER_PAGE;
                const currentNotes = generalNoticeNotes.slice(startIndex, endIndex);

                const goPrev = () => {
                  setExpandedGeneralNoticeId(null);
                  setGeneralNoticePage((p) => Math.max(1, p - 1));
                };
                const goNext = () => {
                  setExpandedGeneralNoticeId(null);
                  setGeneralNoticePage((p) => Math.min(totalPages, p + 1));
                };

                return (
                  <>
                    {totalPages > 1 && (
                      <NoticePaginationRow>
                        <NoticePaginationButton onClick={goPrev} disabled={safePage === 1} title="이전">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="15 18 9 12 15 6"></polyline>
                          </svg>
                        </NoticePaginationButton>
                        <NoticePaginationInfo>{safePage} / {totalPages}</NoticePaginationInfo>
                        <NoticePaginationButton onClick={goNext} disabled={safePage === totalPages} title="다음">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="9 18 15 12 9 6"></polyline>
                          </svg>
                        </NoticePaginationButton>
                      </NoticePaginationRow>
                    )}

                    <NoticeList>
                      {currentNotes.map((note) => {
                const isExpanded = expandedGeneralNoticeId === note.id;
                const content = (note.content || '').toString();
                const lines = content.split('\n');
                const titleLine = (lines[0] || '').trim();
                const restContentRaw = lines.slice(1).join('\n');
                const restContent = restContentRaw.trim() ? restContentRaw : ' ';
                return (
                  <NoticeListItem
                    key={note.id}
                    $expanded={isExpanded}
                    onClick={() => setExpandedGeneralNoticeId((prev) => (prev === note.id ? null : note.id))}
                  >
                    <NoticeListItemDate>
                      {((note as any).noticeScope || 'all') === 'group' ? '[그룹] ' : '[전체] '}
                      {note.date || ''}
                    </NoticeListItemDate>
                    <NoticeListItemPreview>{titleLine || '(내용 없음)'}</NoticeListItemPreview>
                    {isExpanded && (
                      <NoticeListItemFull>{restContent}</NoticeListItemFull>
                    )}
                  </NoticeListItem>
                );
                      })}
                    </NoticeList>
                  </>
                );
              })()}
            </>
          )}

          <ModalActions>
            <Button $variant="secondary" onClick={() => setIsGeneralNoticeModalOpen(false)}>
              닫기
            </Button>
          </ModalActions>
        </ModalContent>
      </ModalOverlay>

      {/* 개별공지노트 열람 모달 (학생용) */}
      <ModalOverlay
        $isOpen={isIndividualNoticeModalOpen}
        onClick={() => setIsIndividualNoticeModalOpen(false)}
      >
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle>개별공지노트</ModalTitle>
            <ModalCloseButton onClick={() => setIsIndividualNoticeModalOpen(false)}>×</ModalCloseButton>
          </ModalHeader>

          {isLoadingIndividualNoticeNotes ? (
            <LoadingState>불러오는 중...</LoadingState>
          ) : individualNoticeNotes.length === 0 ? (
            <EmptyState>등록된 개별공지노트가 없습니다</EmptyState>
          ) : (
            <>
              {(() => {
                const totalPages = Math.max(1, Math.ceil(individualNoticeNotes.length / INDIVIDUAL_NOTICE_PER_PAGE));
                const safePage = Math.min(Math.max(individualNoticePage, 1), totalPages);
                const startIndex = (safePage - 1) * INDIVIDUAL_NOTICE_PER_PAGE;
                const endIndex = startIndex + INDIVIDUAL_NOTICE_PER_PAGE;
                const currentNotes = individualNoticeNotes.slice(startIndex, endIndex);

                const goPrev = () => {
                  setExpandedIndividualNoticeId(null);
                  setIndividualNoticePage((p) => Math.max(1, p - 1));
                };
                const goNext = () => {
                  setExpandedIndividualNoticeId(null);
                  setIndividualNoticePage((p) => Math.min(totalPages, p + 1));
                };

                return (
                  <>
                    {totalPages > 1 && (
                      <NoticePaginationRow>
                        <NoticePaginationButton onClick={goPrev} disabled={safePage === 1} title="이전">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="15 18 9 12 15 6"></polyline>
                          </svg>
                        </NoticePaginationButton>
                        <NoticePaginationInfo>{safePage} / {totalPages}</NoticePaginationInfo>
                        <NoticePaginationButton onClick={goNext} disabled={safePage === totalPages} title="다음">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="9 18 15 12 9 6"></polyline>
                          </svg>
                        </NoticePaginationButton>
                      </NoticePaginationRow>
                    )}
                    <NoticeList>
                      {currentNotes.map((note) => {
                        const isExpanded = expandedIndividualNoticeId === note.id;
                        const fullText = note.content || '';
                        const lines = fullText.split('\n');
                        const titleLine = lines[0] || '';
                        const restContent = lines.slice(1).join('\n');

                        return (
                          <NoticeListItem
                            key={note.id}
                            $expanded={isExpanded}
                            onClick={() => setExpandedIndividualNoticeId((prev) => (prev === note.id ? null : note.id))}
                          >
                            <NoticeListItemDate>{note.date || ''}</NoticeListItemDate>
                            <NoticeListItemPreview>{titleLine || '(내용 없음)'}</NoticeListItemPreview>
                            {isExpanded && (
                              <NoticeListItemFull>{restContent}</NoticeListItemFull>
                            )}
                          </NoticeListItem>
                        );
                      })}
                    </NoticeList>
                  </>
                );
              })()}
            </>
          )}

          <ModalActions>
            <Button $variant="secondary" onClick={() => setIsIndividualNoticeModalOpen(false)}>
              닫기
            </Button>
          </ModalActions>
        </ModalContent>
      </ModalOverlay>

      {/* 코인 히스토리 모달 */}
      <ModalOverlay $isOpen={isCoinHistoryModalOpen} onClick={() => setIsCoinHistoryModalOpen(false)}>
        <CoinHistoryModalContent onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle>코인 사용 내역</ModalTitle>
            <ModalCloseButton onClick={() => setIsCoinHistoryModalOpen(false)}>×</ModalCloseButton>
          </ModalHeader>

          {(() => {
            const sortedHistory = getCoinHistory();
            const ITEMS_PER_PAGE = 10;
            const totalPages = Math.max(1, Math.ceil(sortedHistory.length / ITEMS_PER_PAGE));
            const safePage = Math.min(Math.max(coinHistoryPage, 1), totalPages);
            const startIndex = (safePage - 1) * ITEMS_PER_PAGE;
            const endIndex = startIndex + ITEMS_PER_PAGE;
            const currentHistory = sortedHistory.slice(startIndex, endIndex);

            const goPrev = () => {
              setCoinHistoryPage((p) => Math.max(1, p - 1));
            };
            const goNext = () => {
              setCoinHistoryPage((p) => Math.min(totalPages, p + 1));
            };

            return (
              <>
                {totalPages > 1 && (
                  <NoticePaginationRow>
                    <NoticePaginationButton onClick={goPrev} disabled={safePage === 1} title="이전">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="15 18 9 12 15 6"></polyline>
                      </svg>
                    </NoticePaginationButton>
                    <NoticePaginationInfo>{safePage} / {totalPages}</NoticePaginationInfo>
                    <NoticePaginationButton onClick={goNext} disabled={safePage === totalPages} title="다음">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="9 18 15 12 9 6"></polyline>
                      </svg>
                    </NoticePaginationButton>
                  </NoticePaginationRow>
                )}
          <CoinTable>
            <CoinTableHeader>
              <CoinHeaderCell columnIndex={0}>타입</CoinHeaderCell>
              <CoinHeaderCell columnIndex={1}>날짜</CoinHeaderCell>
              <CoinHeaderCell columnIndex={2}>메모</CoinHeaderCell>
              <CoinHeaderCell columnIndex={3}>획득 코인</CoinHeaderCell>
            </CoinTableHeader>
            <CoinTableBody>
                    {currentHistory.length > 0 ? (
                      currentHistory.map((history: any, index: number) => {
                    return (
                      <CoinTableRow key={index}>
                        {/* 타입 셀 */}
                        <CoinCell columnIndex={0}>
                          <CoinTypeBadge $coinType={history.coinType || 'General'}>
                            {history.coinType === 'Levelup' ? 'L' : 'G'}
                          </CoinTypeBadge>
                        </CoinCell>

                        {/* 날짜 셀 */}
                        <CoinCell columnIndex={1}>
                          {formatDateToMMDD(history.earnedAt)}
                        </CoinCell>

                        {/* 메모 셀 */}
                        <CoinCell columnIndex={2}>
                          {history.earnedCoinMemo || '-'}
                        </CoinCell>

                        {/* 획득 코인 셀 */}
                        <CoinCell columnIndex={3} $coinValue={history.earnedCoin}>
                          {history.earnedCoin}
                        </CoinCell>
                      </CoinTableRow>
                    );
                  })
                ) : (
                  <CoinTableRow $isEmptyState={true}>
                    <CoinCell colSpan={4} columnIndex={0}>코인 획득 내역이 없습니다.</CoinCell>
                  </CoinTableRow>
                    )}
            </CoinTableBody>
          </CoinTable>
              </>
            );
          })()}

          <ModalActions>
            <Button $variant="secondary" onClick={() => setIsCoinHistoryModalOpen(false)}>
              닫기
            </Button>
          </ModalActions>
        </CoinHistoryModalContent>
      </ModalOverlay>

      {/* 과제 포인트 내역 모달 */}
      <ModalOverlay $isOpen={isAssignmentHistoryModalOpen} onClick={() => setIsAssignmentHistoryModalOpen(false)}>
        <CoinHistoryModalContent onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle>과제 포인트 획득 내역</ModalTitle>
            <ModalCloseButton onClick={() => setIsAssignmentHistoryModalOpen(false)}>×</ModalCloseButton>
          </ModalHeader>

          {isLoadingAssignment ? (
            <LoadingState>로딩 중...</LoadingState>
          ) : (
            <>
              {(() => {
                // 필터링된 과제 목록
                const filteredAssignments = assignmentData?.assignments ? [...assignmentData.assignments]
                  .filter((assignment: any) => {
                    if (assignmentFilterType === 'all') return true;
                    if (assignmentFilterType === 'general') return assignment.type === 'general';
                    if (assignmentFilterType === 'project') return assignment.type === 'project';
                    if (assignmentFilterType === 'routine') return assignment.type === 'routine';
                    return true;
                  })
                  .sort((a, b) => {
                    // dueDate 기준 최신순 정렬
                    const dateA = a.dueDate ? new Date(a.dueDate).getTime() : 0;
                    const dateB = b.dueDate ? new Date(b.dueDate).getTime() : 0;
                    return dateB - dateA; // 최신순
                  }) : [];
                
                const ITEMS_PER_PAGE = 10;
                const totalPages = Math.max(1, Math.ceil(filteredAssignments.length / ITEMS_PER_PAGE));
                const safePage = Math.min(Math.max(assignmentHistoryPage, 1), totalPages);
                const startIndex = (safePage - 1) * ITEMS_PER_PAGE;
                const endIndex = startIndex + ITEMS_PER_PAGE;
                const currentAssignments = filteredAssignments.slice(startIndex, endIndex);

                const goPrev = () => {
                  setAssignmentHistoryPage((p) => Math.max(1, p - 1));
                };
                const goNext = () => {
                  setAssignmentHistoryPage((p) => Math.min(totalPages, p + 1));
                };

                // 필터 타입에 따른 슬라이더 위치
                const getSliderPosition = () => {
                  switch (assignmentFilterType) {
                    case 'all': return 0;
                    case 'general': return 1;
                    case 'project': return 2;
                    case 'routine': return 3;
                    default: return 0;
                  }
                };

                return (
                  <>
                    {/* 필터 토글 버튼 */}
                    <AssignmentFilterToggleContainer>
                      <AssignmentFilterToggleSlider $position={getSliderPosition()} />
                      <AssignmentFilterToggleOption
                        $active={assignmentFilterType === 'all'}
                        onClick={() => {
                          setAssignmentFilterType('all');
                          setAssignmentHistoryPage(1);
                        }}
                      >
                        전체
                      </AssignmentFilterToggleOption>
                      <AssignmentFilterToggleOption
                        $active={assignmentFilterType === 'general'}
                        onClick={() => {
                          setAssignmentFilterType('general');
                          setAssignmentHistoryPage(1);
                        }}
                      >
                        일반
                      </AssignmentFilterToggleOption>
                      <AssignmentFilterToggleOption
                        $active={assignmentFilterType === 'project'}
                        onClick={() => {
                          setAssignmentFilterType('project');
                          setAssignmentHistoryPage(1);
                        }}
                      >
                        프로젝트
                      </AssignmentFilterToggleOption>
                      <AssignmentFilterToggleOption
                        $active={assignmentFilterType === 'routine'}
                        onClick={() => {
                          setAssignmentFilterType('routine');
                          setAssignmentHistoryPage(1);
                        }}
                      >
                        루틴
                      </AssignmentFilterToggleOption>
                    </AssignmentFilterToggleContainer>

                    {/* 페이지네이션 */}
                    {totalPages > 1 && (
                      <NoticePaginationRow>
                        <NoticePaginationButton onClick={goPrev} disabled={safePage === 1} title="이전">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="15 18 9 12 15 6"></polyline>
                          </svg>
                        </NoticePaginationButton>
                        <NoticePaginationInfo>{safePage} / {totalPages}</NoticePaginationInfo>
                        <NoticePaginationButton onClick={goNext} disabled={safePage === totalPages} title="다음">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="9 18 15 12 9 6"></polyline>
                          </svg>
                        </NoticePaginationButton>
                      </NoticePaginationRow>
                    )}

                    {/* 테이블 */}
            <RewardTable>
              <RewardTableHeader $columns={5}>
                <RewardHeaderCell columnIndex={0}>타입</RewardHeaderCell>
                <RewardHeaderCell columnIndex={1}>마감일</RewardHeaderCell>
                <RewardHeaderCell columnIndex={2}>과제명</RewardHeaderCell>
                <RewardHeaderCell columnIndex={3}>포인트</RewardHeaderCell>
                <RewardHeaderCell columnIndex={4}>메모</RewardHeaderCell>
              </RewardTableHeader>
              <RewardTableBody>
                        {currentAssignments.length > 0 ? (
                          currentAssignments.map((assignment: any, index: number) => (
                      <RewardTableRow key={index} $columns={5}>
                        <RewardTableCell columnIndex={0}>
                          <TypeBadge type={assignment.type}>
                            {assignment.type === 'general' ? 'G' : assignment.type === 'project' ? 'P' : 'R'}
                          </TypeBadge>
                        </RewardTableCell>
                        <RewardTableCell columnIndex={1}>
                          {assignment.dueDate ? formatDateToMMDD(assignment.dueDate) : '-'}
                        </RewardTableCell>
                        <RewardTableCell columnIndex={2}>
                          {assignment.title}
                        </RewardTableCell>
                        <RewardTableCell columnIndex={3}>
                          {assignment.points}점
                        </RewardTableCell>
                        <RewardTableCell columnIndex={4}>
                          {assignment.memo || '-'}
                        </RewardTableCell>
                      </RewardTableRow>
                    ))
                ) : (
                  <RewardTableRow $isEmptyState={true}>
                    <RewardTableCell colSpan={5} columnIndex={0}>
                      과제 포인트 획득 내역이 없습니다.
                    </RewardTableCell>
                  </RewardTableRow>
                )}
              </RewardTableBody>
            </RewardTable>
                  </>
                );
              })()}
            </>
          )}

          <ModalActions>
            <Button $variant="secondary" onClick={() => setIsAssignmentHistoryModalOpen(false)}>
              닫기
            </Button>
          </ModalActions>
        </CoinHistoryModalContent>
      </ModalOverlay>

      {/* 긍정행동 포인트 내역 모달 */}
      <ModalOverlay $isOpen={isPositiveHistoryModalOpen} onClick={() => setIsPositiveHistoryModalOpen(false)}>
        <CoinHistoryModalContent onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle>긍정행동 포인트 획득 내역</ModalTitle>
            <ModalCloseButton onClick={() => setIsPositiveHistoryModalOpen(false)}>×</ModalCloseButton>
          </ModalHeader>

          {isLoadingPositive ? (
            <LoadingState>로딩 중...</LoadingState>
          ) : (
            <>
              {(() => {
                const ITEMS_PER_PAGE = 10;
                const totalPages = Math.max(1, Math.ceil(rewardPoints.length / ITEMS_PER_PAGE));
                const safePage = Math.min(Math.max(positiveHistoryPage, 1), totalPages);
                const startIndex = (safePage - 1) * ITEMS_PER_PAGE;
                const endIndex = startIndex + ITEMS_PER_PAGE;
                const currentPoints = rewardPoints.slice(startIndex, endIndex);

                const goPrev = () => {
                  setPositiveHistoryPage((p) => Math.max(1, p - 1));
                };
                const goNext = () => {
                  setPositiveHistoryPage((p) => Math.min(totalPages, p + 1));
                };

                return (
                  <>
                    {totalPages > 1 && (
                      <NoticePaginationRow>
                        <NoticePaginationButton onClick={goPrev} disabled={safePage === 1} title="이전">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="15 18 9 12 15 6"></polyline>
                          </svg>
                        </NoticePaginationButton>
                        <NoticePaginationInfo>{safePage} / {totalPages}</NoticePaginationInfo>
                        <NoticePaginationButton onClick={goNext} disabled={safePage === totalPages} title="다음">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="9 18 15 12 9 6"></polyline>
                          </svg>
                        </NoticePaginationButton>
                      </NoticePaginationRow>
                    )}
            <RewardTable>
              <RewardTableHeader>
                <RewardHeaderCell columnIndex={0}>타입</RewardHeaderCell>
                <RewardHeaderCell columnIndex={1}>날짜</RewardHeaderCell>
                <RewardHeaderCell columnIndex={2}>메모</RewardHeaderCell>
                <RewardHeaderCell columnIndex={3}>포인트</RewardHeaderCell>
              </RewardTableHeader>
              <RewardTableBody>
                        {currentPoints.length > 0 ? (
                          currentPoints.map((point, index) => {
                    return (
                      <RewardTableRow key={index}>
                        <RewardTableCell columnIndex={0}>
                          <GemIcon 
                            color="red" 
                            size={20} 
                          />
                        </RewardTableCell>
                        <RewardTableCell columnIndex={1}>
                          {formatDateToMMDD(point.recordedDate)}
                        </RewardTableCell>
                        <RewardTableCell columnIndex={2}>
                          <MemoWithTimeTag>
                            {point.timeTag && (
                              <TimeTagBadge>
                                {point.timeTag.startsWith('일반 ') ? point.timeTag.replace('일반 ', '') : point.timeTag}
                              </TimeTagBadge>
                            )}
                            <MemoText>{point.pointMemo || '-'}</MemoText>
                          </MemoWithTimeTag>
                        </RewardTableCell>
                        <RewardTableCell columnIndex={3}>
                          {point.pointValue}점
                        </RewardTableCell>
                      </RewardTableRow>
                    );
                  })
                ) : (
                  <RewardTableRow $isEmptyState={true}>
                    <RewardTableCell colSpan={4} columnIndex={0}>
                      긍정행동 포인트 획득 내역이 없습니다.
                    </RewardTableCell>
                  </RewardTableRow>
                )}
              </RewardTableBody>
            </RewardTable>
                  </>
                );
              })()}
            </>
          )}

          <ModalActions>
            <Button $variant="secondary" onClick={() => setIsPositiveHistoryModalOpen(false)}>
              닫기
            </Button>
          </ModalActions>
        </CoinHistoryModalContent>
      </ModalOverlay>

      {/* 보상 토스트 */}
      {/* 토스트 메시지 */}
      {showToast && (
        <ToastOverlay>
          <ToastMessage>
            {toastMessage}
          </ToastMessage>
        </ToastOverlay>
      )}
      
      <RewardToastOverlay
        $isOpen={showRewardToast}
        onClick={() => {
          // 프로젝트 보상 알람인 경우 알람 제거 처리
          if (currentNotification) {
            handleRemoveNotification();
          } else {
            setShowRewardToast(false);
          }
        }}
      >
        <RewardToastContent onClick={(e) => e.stopPropagation()}>
          <RewardToastTitle>{currentNotification ? '프로젝트 완료!' : '과제 완료!'}</RewardToastTitle>
          
          {rewardToastData?.message && (
            <RewardMessage>{rewardToastData.message}</RewardMessage>
          )}
          
          {(rewardToastData?.coins || (rewardToastData?.itemCardIds && rewardToastData.itemCardIds.length > 0)) && (
            <RewardSection>
              <RewardSectionTitle>{currentNotification ? '프로젝트 완료 보상' : '과제 완료 보상'}</RewardSectionTitle>
              
              {rewardToastData?.coins && rewardToastData.coins > 0 && (
                <RewardCoinsDisplay>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" fill="#f39c12"/>
                    <circle cx="12" cy="12" r="8" fill="#f1c40f"/>
                    <text x="12" y="16" fontSize="12" fontWeight="bold" fill="#e67e22" textAnchor="middle">₩</text>
                  </svg>
                  <span>{rewardToastData.coins}</span>
                </RewardCoinsDisplay>
              )}
              
              {rewardToastData?.itemCardIds && rewardToastData.itemCardIds.length > 0 && (() => {
                const rewardCards = itemCards.filter(card => rewardToastData.itemCardIds?.includes(card.itemCardId));
                return (
                <RewardItemCardsWrapper>
                  <RewardItemCardsContainer $columnCount={rewardCards.length}>
                  {rewardCards.map(card => (
                      <RewardItemCard key={card.itemCardId}>
                        <RewardItemCardIconArea>
                          {card.cardImage === 'default' ? (
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <rect x="2" y="6" width="12" height="16" rx="2" fill="var(--color-surface-highlight)" stroke="#555" strokeWidth="1.5" transform="rotate(-10 8 14)"/>
                              <rect x="6" y="4" width="12" height="16" rx="2" fill="#f8f8f8" stroke="#555" strokeWidth="1.5" transform="rotate(0 12 12)"/>
                              <rect x="10" y="6" width="12" height="16" rx="2" fill="#fafafa" stroke="#555" strokeWidth="1.5" transform="rotate(10 16 14)"/>
                            </svg>
                          ) : card.cardImage && /^\d+$/.test(card.cardImage) ? (
                            <img src={`/CardIcon/IconBasic/IconBasic-${card.cardImage}.png`} alt={card.name} />
                          ) : card.cardImage ? (
                            <img src={card.cardImage} alt={card.name} />
                          ) : card.icon ? (
                            <img src={card.icon} alt={card.name} />
                          ) : (
                            <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <rect width="48" height="48" rx="8" fill="var(--color-surface-highlight)"/>
                              <path d="M24 14V34M14 24H34" stroke="#999" strokeWidth="3" strokeLinecap="round"/>
                            </svg>
                          )}
                        </RewardItemCardIconArea>
                        <RewardItemCardName>{card.name}</RewardItemCardName>
                        <RewardItemCardMemo>{card.memo}</RewardItemCardMemo>
                      </RewardItemCard>
                    ))}
                </RewardItemCardsContainer>
                </RewardItemCardsWrapper>
                );
              })()}
            </RewardSection>
          )}
          
          <RewardCloseButton onClick={() => {
            // 프로젝트 보상 알람인 경우 알람 제거 처리
            if (currentNotification) {
              handleRemoveNotification();
            } else {
              setShowRewardToast(false);
            }
          }}>
            확인
          </RewardCloseButton>
        </RewardToastContent>
      </RewardToastOverlay>

      {!isGuestMode && resultData?.classInfo && resultData?.student && (
        <StoreModal
          isOpen={showStoreModal}
          onClose={() => setShowStoreModal(false)}
          userId={resultData.classInfo.userId}
          classId={resultData.classInfo.classId}
          studentId={resultData.student.basicInfo.id}
          memberGroup={studentMemberGroup}
          currentCoins={currentStoreCoins}
          userType={storeUserType}
          onPurchaseSuccess={() => {
            if (memberPageScope) {
              userDataService.invalidateMembersCache(
                memberPageScope.userId,
                memberPageScope.classId,
                memberPageScope.memberGroup
              );
              invalidateMemberPageAfterStoreChange(memberPageScope);
            }
            void loadItemCards(true);
            void reloadStudentInfo(true);
          }}
        />
      )}

      <AssignmentSubmissionCropModal
        isOpen={isSubmissionCropOpen}
        onClose={() => setIsSubmissionCropOpen(false)}
        onSave={handlePendingImageSave}
      />

      {/* 스토어 비활성화 안내 모달 */}
      {showStoreDisabledInfoModal && (
        <InfoModalOverlay onClick={() => setShowStoreDisabledInfoModal(false)}>
          <InfoModalContent onClick={(e) => e.stopPropagation()}>
            <InfoModalTitle>스토어 비활성화</InfoModalTitle>
            <InfoModalMessage>
              스토어를 통한 개별 아이템 구매가 비활성화 되어있습니다.<br/>
              코디를 통해 아이템을 구매해주세요.
            </InfoModalMessage>
            <InfoModalButton onClick={() => setShowStoreDisabledInfoModal(false)}>
              확인
            </InfoModalButton>
          </InfoModalContent>
        </InfoModalOverlay>
      )}

      {/* 프로필 이미지 선택 모달 (studentInfo 우선 사용 → 프로필 카드 구매 후 즉시 반영) */}
      {!isGuestMode && resultData?.student && (
        <ProfileImageSelectModal
          isOpen={isProfileModalOpen}
          onClose={() => setIsProfileModalOpen(false)}
          onConfirm={handleProfileImageConfirm}
          currentImageId={effectiveProfileImageId}
          unlockedProfiles={(studentInfo || resultData.student)?.basicInfo?.unlockedProfiles || []}
        />
      )}

      {/* 사용자 설정 모달 */}
      {!isGuestMode && resultData && (
        <StudentProfileEditModal
          isOpen={showProfileEditModal}
          resultData={resultData}
          onClose={() => setShowProfileEditModal(false)}
          onAccessCodeUpdated={(newAccessCode) => {
            // 입장코드 업데이트 후 resultData 업데이트
            if (resultData?.student) {
              resultData.student.basicInfo.accessCode = newAccessCode;
            }
          }}
          onProfileImageUpdated={handleProfileImageConfirm}
        />
      )}
    </Container>
  );
};

// 스토어 비활성화 안내 모달 스타일
const InfoModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1300;
`;

const InfoModalContent = styled.div`
  background: var(--color-surface);
  border-radius: 16px;
  padding: 32px;
  max-width: 400px;
  width: 90%;
  box-shadow: var(--modal-shadow-xlarge);
  border: var(--modal-border);
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InfoModalTitle = styled.h3`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: var(--color-text-primary);
  text-align: center;
`;

const InfoModalMessage = styled.div`
  font-size: 15px;
  color: var(--color-text-secondary);
  text-align: center;
  line-height: 1.6;
`;

const InfoModalButton = styled.button`
  padding: 12px 24px;
  background: var(--color-accent-strong);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: var(--color-accent);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  
  &:active {
    transform: translateY(0);
  }
`;
